import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import EmojiPicker from './EmojiPicker';
import { collection, getDocs, addDoc, query, orderBy, doc, updateDoc, serverTimestamp, onSnapshot, where, deleteDoc, setDoc, getDoc } from 'firebase/firestore';
import type { QuerySnapshot, DocumentData, FirestoreError } from 'firebase/firestore';
import { PostInteractions } from './PostInteractions';
import { PostRespectArea } from './respect';
import { db, storage, auth } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateProfile } from 'firebase/auth';
import { getValidProfilePhoto } from '../utils/profileUtils';
import { withErrorHandling } from '../utils/errorHandler';
import { getAvatarUrl } from '../utils/avatarUtils';
import { applyEnergyMode } from '../utils/energyMode';
import PageHeader from './ui/PageHeader';
import ScrollIndicator from './ui/ScrollIndicator';
import ChakraBar from './ChakraBar';
import { calculateVibeScore, getFeedRankingWeight } from '../services/vibeScoreService';
import geminiService from '../services/geminiTestService';
import { MutePersonButton } from './admin/AdminMuteButton';
import { AdminMutePanel } from './admin/AdminMutePanel';
import { useAdminMute } from './admin/AdminMuteProvider';
import MultiHeader from './MultiHeader';
import OndernemersOnline from './OndernemersOnline';
// SocialMediaHub import removed - using App.tsx version instead
import PostLimitIndicator from './PostLimitIndicator';
import SocialSharingOptions from './SocialSharingOptions';
import SocialMediaExpirationBanner from './social/SocialMediaExpirationBanner';
import { useSocialMediaExpiration } from '../hooks/useSocialMediaExpiration';
import PostSchedulerModal from './social/PostSchedulerModal';
import { Video, Lock, FileText, Users, Settings, Volume2, VolumeX, CheckCircle, Lightbulb, Search, Camera } from './icons';
import { InsightIcon, EmojiIcon, ImageIcon, AttachIcon, CalendarIcon, LinkedInIcon, FacebookIcon, TwitterIcon, InstagramIcon, ICON_STYLES } from './icons';
import IntroductionModal from './IntroductionModal';
import InfoTooltip from './ui/InfoTooltip';
import OpenForConnectionsList from './OpenForConnectionsList';
import { MentionInput } from './MentionInput';
import { MotionCard } from './ui/motion-card';
import NetworkClubFinder from './NetworkClubFinder';
import { sortPostsByFormatPreference } from '../utils/contentTypeDetection';
import { generateAllVariants, cleanupUrls } from './mediaEditor';
import type { AspectRatioOption, ImageCropPosition, ImageFitMode, MediaEditorVariants, PlatformId } from './mediaEditor';
import { FocusControlCard } from './focus/FocusControlCard';
import { useFocusControl } from '../hooks/useFocusControl';
import { BlockedOverlay } from './focus/BlockedOverlay';
import { formatRemainingTime } from '../utils/scrollFreeze';
import { canSeePlatformOnlineStats } from '../utils/platformAdmin';
import { isPhoneViewport } from '../utils/mobileViewport';
import { openConnectieFlow } from '../utils/navigationHelper';
import { FEATURE_POST_CTA } from '../config/flags';
import { SCREEN_LOCK_DURATION_SECONDS } from '../config/flags';
import type { FeedPostCTA, FeedPostCtaType } from '../types/feedTools';
import { DEFAULT_CONNECTIEFLOW_CTA } from '../types/feedTools';
import { useFeedScreenLock } from '../hooks/useFeedScreenLock';
import { feedToolAnalyticsService } from '../services/feedToolAnalyticsService';
import { feedToolsPilotService } from '../services/feedToolsPilotService';

// Quiet Network Lounge Theme — verwijder deze import + class op root div om te reverten
import '../styles/homeQuietLounge.css';
import '../styles/focusControl.css';

interface CommunityDashboardProps {
  user: any;
  userProfile: any;
  isAdmin?: boolean;
  onNavigateToUserStory?: (userId: string) => void;
  subscription?: any;
  canAccessFeature?: (feature: string) => boolean;
  onStartTrial?: () => void;
  onUpgrade?: () => void;
  displaySettings?: any;
  setDisplaySettings?: (settings: any) => void;
  // setActivePage prop removed - using direct navigation instead
  /**
   * Mobile sub-tab index from App.tsx.
   * 0 = Newsfeed (hide MultiHeader, show feed)
   * 1 = Video's   (show MultiHeader on 'videos' tab, hide feed)
   * 2 = Events    (show MultiHeader on 'events' tab, hide feed)
   * 3 = Collaborations (show MultiHeader on 'collaborations', hide feed)
   * 4 = Club Nieuws (show MultiHeader on 'clubnieuws', hide feed)
   * undefined = desktop, show everything
   */
  mobileTabIndex?: number;
}

const INDUSTRY_SUGGESTIONS = [
  "SaaS",
  "E-commerce",
  "Consulting",
  "Marketing",
  "Finance",
  "Healthcare",
  "Education",
  "Real Estate",
  "Manufacturing",
  "Retail",
  "Technology",
  "Media"
];

const SidebarInfoItem: React.FC<{ title: string; items: string[] }> = ({ title, items }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="text-sm font-medium text-[#0A2342]">{title}</span>
        <svg
          className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <ul className="px-4 pb-2.5 space-y-1.5">
          {items.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-gray-600 leading-relaxed">
              <span className="mt-1 w-1 h-1 rounded-full bg-[#7A9488] flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const MOBILE_TAB_TO_HEADER: Record<number, 'videos' | 'events' | 'ondernemers' | 'collaborations' | 'clubnieuws'> = {
  1: 'videos',
  2: 'events',
  3: 'collaborations',
  4: 'clubnieuws',
};

const CommunityDashboard: React.FC<CommunityDashboardProps> = ({ 
  user, 
  userProfile, 
  isAdmin = false, 
  onNavigateToUserStory,
  subscription,
  canAccessFeature,
  onStartTrial,
  onUpgrade,
  displaySettings: externalDisplaySettings,
  setDisplaySettings: externalSetDisplaySettings,
  mobileTabIndex,
  // setActivePage removed
}) => {
  // Check if we're on the home page (dashboard)
  const [isOnHomePage, setIsOnHomePage] = useState(true);
  
  // Focus Control state
  const [showFocusCard, setShowFocusCard] = useState(false);
  const { state: focusState, isBlocked: focusIsBlocked } = useFocusControl();
  const focusRemainingSeconds = focusState.remainingSeconds || (
    focusState.activeMode === 'timer' && focusState.timer.endsAt
      ? Math.max(0, Math.floor((new Date(focusState.timer.endsAt).getTime() - Date.now()) / 1000))
      : focusState.activeMode === 'block' && focusState.block.endsAt
        ? Math.max(0, Math.floor((new Date(focusState.block.endsAt).getTime() - Date.now()) / 1000))
        : 0
  );
  
  useEffect(() => {
    const checkHomePage = () => {
      const hash = window.location.hash.slice(1);
      const isHome = hash === 'dashboard' || hash === '' || (!hash && window.location.pathname === '/');
      setIsOnHomePage(isHome);
    };
    
    checkHomePage();
    window.addEventListener('hashchange', checkHomePage);
    return () => window.removeEventListener('hashchange', checkHomePage);
  }, []);

  // Event listener voor Respect hover card "Verbinden" knop
  useEffect(() => {
    const handleOpenConnectionModal = (event: CustomEvent<{ userId: string; userName: string }>) => {
      const { userId, userName } = event.detail;
      if (userId && userName) {
        sendConnectionRequest(userId, userName);
      }
    };

    window.addEventListener('openConnectionModal', handleOpenConnectionModal as EventListener);
    return () => window.removeEventListener('openConnectionModal', handleOpenConnectionModal as EventListener);
  }, [user, userProfile]);

  useEffect(() => {
    const openNewsfeed = () => {
      setFeedsVisibility((prev) => ({ ...prev, news: true }));
      setExpandedFeed('news');
    };
    const openFirstPostComposer = () => {
      openNewsfeed();
      setIsPostExpanded(true);
      // MentionInput keeps its own ref; focus textarea after expansion render.
      setTimeout(() => {
        const composerTextarea = composerCardRef.current?.querySelector('textarea') as HTMLTextAreaElement | null;
        composerTextarea?.focus();
      }, 180);
    };

    const handleOpenNewsfeed = () => {
      openNewsfeed();
    };
    const handleOpenFirstPostComposer = () => {
      openFirstPostComposer();
    };

    const pending = sessionStorage.getItem('pendingOpenNewsfeed');
    if (pending === '1') {
      sessionStorage.removeItem('pendingOpenNewsfeed');
      openNewsfeed();
    }
    const pendingComposer = sessionStorage.getItem('pendingOpenFirstPostComposer');
    if (pendingComposer === '1') {
      sessionStorage.removeItem('pendingOpenFirstPostComposer');
      openFirstPostComposer();
    }

    window.addEventListener('openNewsfeed', handleOpenNewsfeed as EventListener);
    window.addEventListener('openFirstPostComposer', handleOpenFirstPostComposer as EventListener);
    return () => {
      window.removeEventListener('openNewsfeed', handleOpenNewsfeed as EventListener);
      window.removeEventListener('openFirstPostComposer', handleOpenFirstPostComposer as EventListener);
    };
  }, []);
  // Admin mute functionality
  const { isUserMuted } = useAdminMute();
  
  // Check for expiring social media connections
  const { expiringConnections } = useSocialMediaExpiration(user?.uid || null);
  
  // Dismissed banners state (per session)
  const [dismissedBanners, setDismissedBanners] = useState<string[]>([]);
  
  // Post scheduler modal
  const [showSchedulerModal, setShowSchedulerModal] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [hasLoadedData, setHasLoadedData] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<any[]>([]);
  const [communityPosts, setCommunityPosts] = useState<any[]>([]);
  const [newPostContent, setNewPostContent] = useState('');
  const [expandedTextBlocks, setExpandedTextBlocks] = useState<Record<string, boolean>>({});
  const [aiDisclosure, setAiDisclosure] = useState<'no' | 'partial' | 'full' | null>(null);
  const [aiAssistanceTypes, setAiAssistanceTypes] = useState<string[]>([]);
  const [showAiPastePrompt, setShowAiPastePrompt] = useState(false);
  const [aiPastePromptDismissed, setAiPastePromptDismissed] = useState(false);
  const previousPostContentRef = useRef('');
  const [isPostExpanded, setIsPostExpanded] = useState(false);
  const [mobileFeedChromeHidden, setMobileFeedChromeHidden] = useState(false);
  const lastMobileFeedScrollTopRef = useRef(0);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const composerScrollRef = useRef<HTMLDivElement>(null);
  const composerCardRef = useRef<HTMLDivElement>(null);
  const [showComposerScrollTop, setShowComposerScrollTop] = useState(false);

  const [expandedFeed, setExpandedFeed] = useState<null | 'news' | 'community' | 'my' | 'pinned'>(null);
  const [showIntroductionModal, setShowIntroductionModal] = useState(false);
  const [selectedUserForIntroduction, setSelectedUserForIntroduction] = useState<{userId: string, userName: string, userPhoto?: string} | null>(null);
  const [ctaEditorPost, setCtaEditorPost] = useState<any | null>(null);
  const [ctaDraft, setCtaDraft] = useState<FeedPostCTA>(DEFAULT_CONNECTIEFLOW_CTA);
  const [screenLockCountdown, setScreenLockCountdown] = useState<number>(SCREEN_LOCK_DURATION_SECONDS);
  const [activeScreenLockPost, setActiveScreenLockPost] = useState<any | null>(null);
  const [screenLockReady, setScreenLockReady] = useState(false);
  const desktopLockBucketRef = useRef(0);
  const mobileLockBucketRef = useRef(0);
  const [attachments, setAttachments] = useState<Array<{ type: 'image' | 'video' | 'file'; url: string; name?: string }>>([]);
  const [filePreview, setFilePreview] = useState<{ url: string; name: string } | null>(null);
  const [isSubmittingPost, setIsSubmittingPost] = useState(false);
  const [postToast, setPostToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [pulseAiSection, setPulseAiSection] = useState(false);
  const aiDisclosureRef = useRef<HTMLDivElement | null>(null);
  const [showPhotoPositioner, setShowPhotoPositioner] = useState(false);
  const [tempPhotoURL, setTempPhotoURL] = useState('');
  const [tempPhotoFile, setTempPhotoFile] = useState<File | null>(null);
  const [tempPhotoPosition, setTempPhotoPosition] = useState({ x: 50, y: 50 });
  const [isDraggingPhoto, setIsDraggingPhoto] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [pendingUploadPreview, setPendingUploadPreview] = useState<{ type: 'image' | 'video' | 'file'; url: string; name: string } | null>(null);
  const [showMobileMediaSheet, setShowMobileMediaSheet] = useState(false);
  const [mobileMediaPreviewOrientation, setMobileMediaPreviewOrientation] = useState<'vertical' | 'horizontal'>('vertical');
  const [mobileMediaFitMode, setMobileMediaFitMode] = useState<'contain' | 'cover' | 'blur'>('blur');
  const [platformMediaFitModes, setPlatformMediaFitModes] = useState<Record<string, 'contain' | 'cover' | 'blur'>>({});
  const [mobileMediaPosition, setMobileMediaPosition] = useState<ImageCropPosition>({ x: 50, y: 50 });
  const [platformMediaPositions, setPlatformMediaPositions] = useState<Partial<Record<PlatformId, ImageCropPosition>>>({});
  const mediaDragRef = useRef<{ key: string; startX: number; startY: number; position: ImageCropPosition } | null>(null);
  const [platformAspectModes, setPlatformAspectModes] = useState<Partial<Record<PlatformId, AspectRatioOption>>>({
    linkedin: '1.91:1',
  });
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState<string>('');
  const [postCollaborations, setPostCollaborations] = useState<{ userIds: string[]; names: string[] }>({ userIds: [], names: [] });
  const [pendingConnects, setPendingConnects] = useState<any[]>([]);
  const [sentRequests, setSentRequests] = useState<any[]>([]);
  const [receivedRequests, setReceivedRequests] = useState<any[]>([]);
  const [recentChats, setRecentChats] = useState<any[]>([]);
  const [displayOpen, setDisplayOpen] = useState<boolean>(false);
  const [cleanOpen, setCleanOpen] = useState<boolean>(false);
  const [connectionsOpen, setConnectionsOpen] = useState<boolean>(false);
  const [communityOpen, setCommunityOpen] = useState<boolean>(false);
  const [showSidebarInfo, setShowSidebarInfo] = useState<boolean>(false);
  const [showSocialMediaHub, setShowSocialMediaHub] = useState<boolean>(false);
  const [networkClubImageIndex, setNetworkClubImageIndex] = useState(0);
  const [showNetworkClubFinder, setShowNetworkClubFinder] = useState(false);
  const [selectedSocialPlatforms, setSelectedSocialPlatforms] = useState<string[]>([]);
  const [selectedHashtags, setSelectedHashtags] = useState<string[]>([]);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [newsfeedSearchName, setNewsfeedSearchName] = useState('');
  const [newsfeedSearchIndustry, setNewsfeedSearchIndustry] = useState('');
  const [newsfeedSearchPostcode, setNewsfeedSearchPostcode] = useState('');
  const [showIndustryDropdown, setShowIndustryDropdown] = useState(false);
  const industryDropdownRef = useRef<HTMLDivElement>(null);
  const sidebarInfoRef = useRef<HTMLDivElement>(null);
  // Organization posting state - DISABLED (requires LinkedIn Marketing Partner status)
  // const [linkedInAsOrganization, setLinkedInAsOrganization] = useState<boolean>(false);
  // const [linkedInOrganizations, setLinkedInOrganizations] = useState<any[]>([]);
  // const [selectedLinkedInOrg, setSelectedLinkedInOrg] = useState<string | null>(null);
  const sanitizeHashtag = (value: string) => {
    const normalized = value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, ' en ')
      .replace(/[^a-zA-Z0-9]+/g, ' ')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join('');

    return normalized ? `#${normalized}` : '';
  };

  const [suggestedHashtags, setSuggestedHashtags] = useState<string[]>([]);
  const [hashtagsLoading, setHashtagsLoading] = useState(false);

  // Returns keyword-based fallback tags immediately (no API needed)
  const getKeywordHashtags = (text: string, collabNames: string[]): string[] => {
    const suggestions = new Set<string>();
    const src = text.toLowerCase();
    const add = (v: string) => { const t = sanitizeHashtag(v); if (t.length > 1) suggestions.add(t); };
    const has = (p: RegExp) => p.test(src);
    collabNames.forEach(add);

    // Ondernemen & netwerken
    if (has(/ondernem|bedrijf|business|startup|zzp|freelance|klant/)) add('Ondernemen');
    if (has(/samenwerk|collab|partner|partnerschap/)) add('Samenwerken');
    if (has(/netwerk|connect|verbinden|community/)) add('Netwerken');

    // Social media & focus (duidelijk onderscheiden van AI)
    if (has(/social media|socialmedia|instagram|facebook|linkedin|twitter|tiktok|algoritme.hustle|algorithme-hustle|energie.lek|energielek/)) add('SocialMedia');
    if (has(/focus|productiviteit|concentratie|regie|controle|schone internet|ruis eruit|aandacht/)) add('Focus');
    if (has(/altijd.aan|always.on|afleiding|burnout|stress|druk|vermoeid/)) add('Productiviteit');

    // AI (alleen echte AI-toepassingen, NIET sociale algoritmes)
    if (has(/\bartificial intelligence\b|\bkunstmatige intelligentie\b|dall-e|dall_e|midjourney|chatgpt|\bgpt-[34]\b|machine learning|large language model|\bllm\b|generatieve ai|getraind op bestaande werken/)) add('AI');

    // Technologie & innovatie
    if (has(/technologie|tech\b|software|digitaal|automatisering|app\b|platform/)) add('Technologie');
    if (has(/innovatie|innoveren|vernieuwing|toekomst|disruptief/)) add('Innovatie');

    // Auteursrecht & ethiek
    if (has(/auteursrecht|auteurschap|copyright|claimen|rechten|instemmen|licentie/)) add('Auteursrecht');
    if (has(/ethisch|ethiek|moreel|verantwoord|eerlijk|grens.*creatief|vraagstukken/)) add('Ethiek');

    // Kunst & creativiteit
    if (has(/kunst\b|kunstenaar|artiest|design|illustratie|schilder|muziek|fotografie|moma|warhol|liechtenstein|comic|tekenaar/)) add('Kunst');
    if (has(/creatief|creativiteit|auteur|originele|creatie/)) add('Creativiteit');

    // Gezondheid & welzijn
    if (has(/gezond|gezondheid|herstel|lichaam|welzijn|ziekte/)) add('Gezondheid');
    if (has(/darm|buik|crohn|spijsvertering|voeding/)) add('Darmgezondheid');
    if (has(/therapie|therapeut|coach\b|coaching|begeleiding/)) add('Therapie');

    // Persoonlijke groei & motivatie
    if (has(/persoonlijke ontwikkeling|persoonlijke groei|zingeving|bewust|blokkade/)) add('PersoonlijkeGroei');
    if (has(/\bmotivatie\b|passie|drijfveer|inspiratie|liefde voor dit vak/)) add('Motivatie');

    // Events & content
    if (has(/\bevent\b|workshop|training|seminar|webinar/)) add('Event');
    if (has(/\bpodcast\b|aflevering|uitzending|opname\b/)) add('Podcast');

    // Marketing (alleen direct relevant)
    if (has(/\bmarketing\b|verkoop|sales|merk\b|campagne|advertentie\b/)) add('Marketing');

    // Finance (alleen direct relevant, niet bij zijdelingse vermeldingen)
    if (has(/financieel|investering|funding|subsidie|begroting|cashflow/)) add('Finance');

    // Leiderschap & duurzaamheid
    if (has(/leiderschap|management|leidinggeven/)) add('Leiderschap');
    if (has(/duurzaam|milieu|circulair|klimaat|maatschappelijk/)) add('Duurzaamheid');

    return Array.from(suggestions).slice(0, 8);
  };

  useEffect(() => {
    const trimmed = newPostContent.trim();

    if (
      trimmed.length < 40 ||
      /lorem ipsum|de finibus bonorum|cicero|rackham|generated lorem ipsum|randomised words|standard chunk/i.test(trimmed)
    ) {
      setSuggestedHashtags([]);
      setHashtagsLoading(false);
      return;
    }

    // Show keyword fallback immediately
    const immediate = getKeywordHashtags(trimmed, postCollaborations.names);
    setSuggestedHashtags(immediate);
    setHashtagsLoading(true);

    // After debounce, upgrade with AI suggestions
    const timer = setTimeout(async () => {
      try {
        const aiTags = await geminiService.suggestHashtagsFromContent(trimmed, postCollaborations.names);
        if (aiTags.length > 0) {
          const sanitized = aiTags.map((t) => sanitizeHashtag(t)).filter((t) => t.length > 1);
          const collabTags = postCollaborations.names.map((n) => sanitizeHashtag(n)).filter((t) => t.length > 1);
          const merged = Array.from(new Set([...collabTags, ...sanitized])).slice(0, 10);
          setSuggestedHashtags(merged);
        }
        // If AI returns nothing, keep the keyword fallback already shown
      } catch {
        // Keep keyword fallback on error
      } finally {
        setHashtagsLoading(false);
      }
    }, 900);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPostContent, postCollaborations.names]);

  // Toggle helpers to align feeds on open
  const handleToggleDisplayOpen = () => {
    console.log('🔵 handleToggleDisplayOpen called, current state:', displayOpen);
    setDisplayOpen((prev) => {
      const next = !prev;
      console.log('🔵 Display state changing from', prev, 'to', next);
      if (next) {
        // Standaard volgorde: Newsfeed, Eigen Feed, Pin Feed (Community Feed uit)
        setFeedsVisibility({ news: true, community: false, my: true, pinned: true });
        // Close other sidebar cards
        setCommunityOpen(false);
        setCleanOpen(false);
        setConnectionsOpen(false);
      }
      return next;
    });
  };
  const handleToggleCommunityOpen = () => {
    console.log('🟡 handleToggleCommunityOpen called, current state:', communityOpen);
    setCommunityOpen((prev) => {
      const next = !prev;
      console.log('🟡 Community/Feeds state changing from', prev, 'to', next);
      if (next) {
        setFeedsVisibility((p) => {
          const nextVis = { ...p, community: true } as FeedsVisibility;
          const onCount = Object.values(nextVis).filter(Boolean).length;
          if (onCount > MAX_ACTIVE_FEEDS && nextVis.news) {
            nextVis.news = false;
          }
          return nextVis;
        });
        // Close other sidebar cards
        setDisplayOpen(false);
        setCleanOpen(false);
        setConnectionsOpen(false);
      }
      return next;
    });
  };

  const filteredIndustrySuggestions = useMemo(() => {
    const term = newsfeedSearchIndustry.trim().toLowerCase();
    if (term.length < 2) return [];
    return INDUSTRY_SUGGESTIONS.filter((item) =>
      item.toLowerCase().includes(term)
    );
  }, [newsfeedSearchIndustry]);

  const networkClubImages = useMemo(
    () => [
      require('./content/Afbeeldingen/netwerk_SK_1.png'),
      require('./content/Afbeeldingen/netwerk_SK_2.png'),
      require('./content/Afbeeldingen/netwerk_SK_3.png')
    ],
    []
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (industryDropdownRef.current && !industryDropdownRef.current.contains(event.target as Node)) {
        setShowIndustryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (sidebarInfoRef.current && !sidebarInfoRef.current.contains(e.target as Node)) {
        setShowSidebarInfo(false);
      }
    };
    if (showSidebarInfo) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSidebarInfo]);

  useEffect(() => {
    if (networkClubImages.length <= 1) return;
    const intervalId = window.setInterval(() => {
      setNetworkClubImageIndex((prev) => (prev + 1) % networkClubImages.length);
    }, 4 * 60 * 1000);
    return () => window.clearInterval(intervalId);
  }, [networkClubImages.length]);
  const handleToggleCleanOpen = () => {
    console.log('🟢 handleToggleCleanOpen called, current state:', cleanOpen);
    setCleanOpen((prev) => {
      const next = !prev;
      console.log('🟢 Clean Mind state changing from', prev, 'to', next);
      if (next) {
        setDisplayOpen(false);
        setCommunityOpen(false);
        setConnectionsOpen(false);
      }
      return next;
    });
  };
  const handleToggleConnectionsOpen = () => {
    console.log('🔴 handleToggleConnectionsOpen called, current state:', connectionsOpen);
    setConnectionsOpen((prev) => {
      const next = !prev;
      console.log('🔴 Connections state changing from', prev, 'to', next);
      if (next) {
        setDisplayOpen(false);
        setCommunityOpen(false);
        setCleanOpen(false);
      }
      return next;
    });
  };
  const [connType, setConnType] = useState<'sent' | 'received'>('sent');
  const [connStatus, setConnStatus] = useState<'all' | 'pending' | 'accepted' | 'declined'>('all');
  const [muteRefresh, setMuteRefresh] = useState<number>(0);
  const [showMutedManage, setShowMutedManage] = useState<boolean>(false);
  const [mutedList, setMutedList] = useState<Array<{ userId: string; name?: string; photo?: string; until?: number }>>([]);
  const getInitialsAvatar = (name: string) => `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=cccccc&color=fff&size=128`;
  const getPostAvatar = (post: any) => {
    const preferredPhoto =
      post?.authorPhotoURL ||
      post?.authorPhoto ||
      post?.userPhoto ||
      post?.profilePhoto ||
      post?.photoURL;
    if (preferredPhoto && String(preferredPhoto).startsWith('http')) return String(preferredPhoto);
    return getInitialsAvatar(String(post?.author || post?.authorName || post?.userName || 'U'));
  };
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [purgeDays, setPurgeDays] = useState<number>(90);

  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const videoInputRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const profilePhotoInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadingProfilePhoto, setUploadingProfilePhoto] = useState(false);
  const emojiButtonRef = useRef<HTMLButtonElement | null>(null);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  // const emojiButtonRef = useRef<HTMLButtonElement | null>(null);
  
  // Desktop camera capture state/refs
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraMode, setCameraMode] = useState<'photo' | 'video'>('photo');
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const recordedChunksRef = useRef<Blob[]>([]);
  
  const openPhotoPicker = () => {
    const el = photoInputRef.current;
    if (el) {
      el.setAttribute('capture', 'environment');
      el.click();
    }
  };
  const openVideoPicker = () => {
    const el = videoInputRef.current;
    if (el) {
      el.setAttribute('capture', 'environment');
      el.click();
    }
  };
  const openFilePicker = () => fileInputRef.current?.click();

  const handleMobileMediaChoice = (choice: 'photo' | 'video' | 'gallery') => {
    setShowMobileMediaSheet(false);
    if (choice === 'photo') {
      openPhotoPicker();
      return;
    }
    if (choice === 'video') {
      openVideoPicker();
      return;
    }
    openMediaPicker();
  };

  const openMobileMediaSheet = () => {
    if (isPhoneViewport()) {
      setShowMobileMediaSheet(true);
      return;
    }
    openMediaPicker();
  };

  const handleProfilePhotoUpload = (file: File) => {
    if (!file.type.startsWith('image/')) return;
    if (file.size > 20 * 1024 * 1024) {
      alert('❌ Bestand is te groot. Maximaal 20MB toegestaan.');
      return;
    }
    const previewURL = URL.createObjectURL(file);
    setTempPhotoURL(previewURL);
    setTempPhotoFile(file);
    setTempPhotoPosition({ x: 50, y: 50 });
    setShowPhotoPositioner(true);
  };

  const handlePhotoPositionSave = async () => {
    if (!tempPhotoFile) return;
    const uid = auth.currentUser?.uid || user?.uid;
    if (!uid) return;
    setUploadingProfilePhoto(true);
    setShowPhotoPositioner(false);
    try {
      const canvas = document.createElement('canvas');
      const img = new Image();
      const objectUrl = URL.createObjectURL(tempPhotoFile);
      await new Promise<void>((resolve, reject) => {
        img.onload = () => {
          const maxW = 800;
          const scale = img.width > maxW ? maxW / img.width : 1;
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          const ctx = canvas.getContext('2d');
          if (ctx) ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          URL.revokeObjectURL(objectUrl);
          resolve();
        };
        img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(); };
        img.src = objectUrl;
      });
      const blob: Blob = await new Promise((res) => canvas.toBlob(b => res(b!), 'image/jpeg', 0.85));
      const compressedFile = new File([blob], 'profile.jpg', { type: 'image/jpeg' });
      const photoRef = ref(storage, `profilePhotos/${uid}/${Date.now()}-profile.jpg`);
      await uploadBytes(photoRef, compressedFile);
      const downloadURL = await getDownloadURL(photoRef);
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, { photoURL: downloadURL });
      }
      await updateDoc(doc(db, 'userProfiles', uid), {
        photoURL: downloadURL,
        photoPosition: tempPhotoPosition,
        'onboardingData.photoURL': downloadURL,
        'onboardingData.photoPosition': tempPhotoPosition,
        updatedAt: new Date()
      });
      URL.revokeObjectURL(tempPhotoURL);
      setTempPhotoFile(null);
      setTempPhotoURL('');
      window.dispatchEvent(new CustomEvent('profilePhotoUpdated', { detail: { photoURL: downloadURL } }));
      showPostToast('success', 'Profielfoto bijgewerkt!');
    } catch (err) {
      console.error('Profile photo upload error:', err);
      showPostToast('error', 'Upload mislukt. Probeer opnieuw.');
    } finally {
      setUploadingProfilePhoto(false);
    }
  };

  const handlePhotoDragMouseDown = (e: React.MouseEvent) => {
    setIsDraggingPhoto(true);
    updatePhotoPosition(e);
  };

  const handlePhotoDragMouseMove = (e: React.MouseEvent) => {
    if (isDraggingPhoto) updatePhotoPosition(e);
  };

  const handlePhotoDragMouseUp = () => setIsDraggingPhoto(false);

  const updatePhotoPosition = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setTempPhotoPosition({
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y))
    });
  };

  // const insertEmoji = (emoji: string) => {
  //   const el = textAreaRef.current;
  //   if (!el) {
  //     setNewPostContent(prev => (prev || '') + emoji);
  //     return;
  //   }
  //   const start = el.selectionStart || 0;
  //   const end = el.selectionEnd || 0;
  //   const before = newPostContent.slice(0, start);
  //   const after = newPostContent.slice(end);
  //   const next = `${before}${emoji}${after}`;
  //   setNewPostContent(next);
  //   requestAnimationFrame(() => {
  //     el.focus();
  //     const caret = start + emoji.length;
  //     try { el.setSelectionRange(caret, caret); } catch {}
  //   });
  // };

  const isMobileDevice = () => {
    if (typeof navigator === 'undefined') return false;
    return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  };

  const openCameraModal = async (mode: 'photo' | 'video') => {
    try {
      setCameraMode(mode);
      setCameraOpen(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: mode === 'video'
      });
      mediaStreamRef.current = stream;
      if (videoElRef.current) {
        (videoElRef.current as any).srcObject = stream;
        await videoElRef.current.play();
      }
    } catch (err) {
      console.error('Camera access failed:', err);
      // Fallback to native pickers
      if (mode === 'photo') {
        openPhotoPicker();
      } else {
        openVideoPicker();
      }
      setCameraOpen(false);
    }
  };

  const closeCameraModal = () => {
    setCameraOpen(false);
    setIsRecording(false);
    if (mediaRecorderRef.current) {
      try { mediaRecorderRef.current.stop(); } catch {}
      mediaRecorderRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;
    }
    recordedChunksRef.current = [];
  };

  const capturePhotoFromStream = async () => {
    try {
      const video = videoElRef.current;
      if (!video) return;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 1280;
      canvas.height = video.videoHeight || 720;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const blob: Blob = await new Promise(resolve => canvas.toBlob(b => resolve(b as Blob), 'image/jpeg', 0.92)!);
      const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setSelectedImageFile(file);
      await uploadSelectedFile(file, 'image');
      console.log('✅ Foto geüpload');
      closeCameraModal();
      } catch (e) {
      console.error('Photo capture failed:', e);
      alert('Foto maken mislukt. Probeer opnieuw.');
    }
  };

  const startRecording = () => {
    if (!mediaStreamRef.current) return;
    recordedChunksRef.current = [];
    try {
      // Use compressed settings for smaller file size
      const mimeType = 'video/webm;codecs=vp9';
      const options: any = { 
        mimeType: MediaRecorder.isTypeSupported(mimeType) ? mimeType : 'video/webm',
        videoBitsPerSecond: 2_500_000 // 2.5 Mbps for good quality at smaller size
      };
      
      const rec = new MediaRecorder(mediaStreamRef.current, options);
      mediaRecorderRef.current = rec;
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        const file = new File([blob], `video-${Date.now()}.webm`, { type: 'video/webm' });
        const sizeMB = file.size / 1024 / 1024;
        console.log(`📹 Recorded video size: ${sizeMB.toFixed(2)} MB`);
        await uploadSelectedFile(file, 'video');
        console.log('✅ Video geüpload');
        closeCameraModal();
      };
      rec.start();
      setIsRecording(true);
    } catch (e) {
      console.error('Start recording failed:', e);
      alert('Opname starten mislukt.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      try { mediaRecorderRef.current.stop(); } catch {}
      setIsRecording(false);
    }
  };

  const openPhoto = () => {
    if (isMobileDevice()) {
      openPhotoPicker();
        } else {
      openCameraModal('photo');
    }
  };

  const openVideo = () => {
    if (isMobileDevice()) {
      openVideoPicker();
      } else {
      openCameraModal('video');
    }
  };

  // Combined media picker for images and videos
  const openMediaPicker = () => {
    const el = document.createElement('input');
    el.type = 'file';
    el.accept = 'image/*,video/*';
    el.onchange = (e: any) => {
      const file = e.target.files?.[0];
      if (file) {
        const isImage = file.type.startsWith('image/');
        const isVideo = file.type.startsWith('video/');
        if (isImage) {
          setSelectedImageFile(file);
          uploadSelectedFile(file, 'image');
        } else if (isVideo) {
          uploadSelectedFile(file, 'video');
        }
      }
    };
    el.click();
  };

  const getPlatformPreviewMeta = (platform: string) => {
    const normalized = platform === 'twitter' ? 'x' : platform;
    const meta: Record<string, { id: PlatformId; shortLabel: string; label: string; aspectRatio: string; hint: string; accent: string; textColor: string; icon: React.ReactNode }> = {
      linkedin: { id: 'linkedin', shortLabel: 'LinkedIn', label: 'LinkedIn', aspectRatio: '1.91 / 1', hint: 'Brede zakelijke feed-preview', accent: '#6F9BB3', textColor: '#FFFFFF', icon: <LinkedInIcon size={14} /> },
      facebook: { id: 'facebook', shortLabel: 'FB', label: 'Facebook', aspectRatio: '1.91 / 1', hint: 'Brede tijdlijn-preview', accent: '#789AC8', textColor: '#FFFFFF', icon: <FacebookIcon size={14} /> },
      x: { id: 'x', shortLabel: 'X', label: 'X', aspectRatio: '16 / 9', hint: 'Landscape post-preview', accent: '#6B7280', textColor: '#FFFFFF', icon: <TwitterIcon size={14} /> },
      instagram: { id: 'instagram', shortLabel: 'Instagram', label: 'Instagram', aspectRatio: '4 / 5', hint: 'Verticale feed-preview', accent: '#C27B98', textColor: '#FFFFFF', icon: <InstagramIcon size={14} /> },
    };
    const fallback = { id: 'linkedin' as PlatformId, shortLabel: platform, label: platform, aspectRatio: '1 / 1', hint: 'Platform-preview', accent: '#F0F7F4', textColor: '#4F6660', icon: <ImageIcon size={14} weight="regular" /> };
    const selected = meta[normalized] || fallback;
    return {
      ...selected,
      id: selected.id || normalized as PlatformId,
      shortLabel: selected.shortLabel || selected.label,
    };
  };

  const getPlatformAspectRatioForPreview = (platformId: PlatformId, defaultAspectRatio: string) => {
    const override = platformAspectModes[platformId];
    if (override === '1:1') return '1 / 1';
    if (override === '1.91:1') return '1.91 / 1';
    if (override === '16:9') return '16 / 9';
    if (override === '4:5') return '4 / 5';
    return defaultAspectRatio;
  };

  const getPlatformAspectOptions = (platformId: PlatformId): Array<{ value: AspectRatioOption; label: string }> => {
    if (platformId === 'instagram') {
      return [
        { value: '4:5', label: 'Verticaal' },
        { value: '1:1', label: 'Vierkant' },
      ];
    }
    if (platformId === 'x') {
      return [
        { value: '16:9', label: 'Breed' },
        { value: '1:1', label: 'Vierkant' },
      ];
    }
    return [
      { value: '1.91:1', label: 'Breed' },
      { value: '1:1', label: 'Vierkant' },
    ];
  };

  const clampMediaPosition = (value: number) => Math.max(0, Math.min(100, value));

  const handleMediaPositionDragStart = (
    key: string,
    position: ImageCropPosition,
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    mediaDragRef.current = {
      key,
      startX: event.clientX,
      startY: event.clientY,
      position,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleMediaPositionDragMove = (
    key: string,
    setPosition: (position: ImageCropPosition) => void,
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    const drag = mediaDragRef.current;
    if (!drag || drag.key !== key) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const next = {
      x: clampMediaPosition(drag.position.x - ((event.clientX - drag.startX) / Math.max(1, rect.width)) * 80),
      y: clampMediaPosition(drag.position.y - ((event.clientY - drag.startY) / Math.max(1, rect.height)) * 80),
    };
    setPosition(next);
  };

  const handleMediaPositionDragEnd = () => {
    mediaDragRef.current = null;
  };

  const renderExpandableText = (
    key: string,
    text: string,
    options: { collapsedLines?: number; className?: string; placeholder?: string; threshold?: number } = {}
  ) => {
    const {
      collapsedLines = 4,
      className = 'text-xs leading-relaxed text-gray-700',
      placeholder,
      threshold = 220,
    } = options;
    const value = text.trim();
    const displayValue = value || placeholder || '';
    if (!displayValue) return null;

    const isLong = value.length > threshold || value.split('\n').length > collapsedLines;
    const isExpanded = expandedTextBlocks[key];

    return (
      <div className="mb-2">
        <p
          className={`whitespace-pre-wrap break-words ${className}`}
          style={!isExpanded && isLong ? {
            display: '-webkit-box',
            WebkitLineClamp: collapsedLines,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          } : undefined}
        >
          {displayValue}
        </p>
        {isLong && (
          <button
            type="button"
            onClick={() => setExpandedTextBlocks((prev) => ({ ...prev, [key]: !prev[key] }))}
            className="mt-1 text-[11px] font-semibold text-[#4F6660]"
          >
            {isExpanded ? 'Minder tonen' : 'Lees meer'}
          </button>
        )}
      </div>
    );
  };

  const renderMobileMediaAsset = (
    media: { type: 'image' | 'video' | 'file'; url: string; name?: string },
    aspectRatio: string,
    roundedClass = 'rounded-xl',
    fitMode: 'contain' | 'cover' | 'blur' = mobileMediaFitMode,
    position: ImageCropPosition = { x: 50, y: 50 },
    onPositionChange?: (position: ImageCropPosition) => void,
    dragKey = 'media'
  ) => (
    <div
      className={`relative w-full overflow-hidden bg-gray-100 ${roundedClass}`}
      style={{ aspectRatio, touchAction: fitMode === 'cover' ? 'none' : 'auto', cursor: fitMode === 'cover' ? 'grab' : 'default' }}
      onPointerDown={(event) => {
        if (fitMode === 'cover' && onPositionChange) handleMediaPositionDragStart(dragKey, position, event);
      }}
      onPointerMove={(event) => {
        if (fitMode === 'cover' && onPositionChange) handleMediaPositionDragMove(dragKey, onPositionChange, event);
      }}
      onPointerUp={handleMediaPositionDragEnd}
      onPointerCancel={handleMediaPositionDragEnd}
    >
      {fitMode === 'blur' && media.type !== 'file' && (
        <div className="absolute inset-0 overflow-hidden">
          {media.type === 'image' ? (
            <img
              src={media.url}
              alt=""
              className="h-full w-full scale-110 object-cover"
              style={{ filter: 'blur(18px) brightness(0.82)' }}
            />
          ) : (
            <video
              src={media.url}
              className="h-full w-full scale-110 object-cover"
              style={{ filter: 'blur(18px) brightness(0.82)' }}
              playsInline
              muted
            />
          )}
          <div className="absolute inset-0 bg-white/10" />
        </div>
      )}
      {media.type === 'image' && (
        <img
          src={media.url}
          alt={media.name || 'Preview'}
          className="relative h-full w-full"
          draggable={false}
          style={{
            objectFit: fitMode === 'cover' ? 'cover' : 'contain',
            objectPosition: `${position.x}% ${position.y}%`,
            backgroundColor: fitMode === 'blur' ? 'transparent' : '#F3F4F6',
            userSelect: 'none',
          }}
        />
      )}
      {media.type === 'video' && (
        <video
          src={media.url}
          className="relative h-full w-full"
          style={{
            objectFit: fitMode === 'cover' ? 'cover' : 'contain',
            objectPosition: `${position.x}% ${position.y}%`,
            backgroundColor: fitMode === 'blur' ? 'transparent' : '#111827',
            userSelect: 'none',
          }}
          controls
          playsInline
          muted
        />
      )}
      {media.type === 'file' && (
        <div className="flex h-full w-full items-center justify-center text-sm font-medium text-gray-500">
          Bestand toegevoegd
        </div>
      )}
    </div>
  );

  const renderMobileFeedPreview = (media: { type: 'image' | 'video' | 'file'; url: string; name?: string } | null) => {
    const authorName = userProfile?.displayName || userProfile?.name || user?.displayName || 'Jij';
    const authorPhoto = getValidProfilePhoto(user, userProfile) || user?.photoURL;
    const feedAspectRatio = mobileMediaPreviewOrientation === 'vertical' ? '4 / 5' : '16 / 9';
    const platformMetas = (['linkedin', 'x', 'facebook', 'instagram'] as const).map(getPlatformPreviewMeta);
    const normalizedSelectedPlatforms = selectedSocialPlatforms.map((platform) => platform === 'twitter' ? 'x' : platform);
    const previewContent = getPostContentWithSelectedHashtags();
    const fitOptions = [
      { value: 'contain', label: 'Volledig' },
      { value: 'blur', label: 'Blur' },
      { value: 'cover', label: 'Vullen' },
    ] as const;

    return (
      <div className="mt-2 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm" style={{ position: 'relative' }}>
        {/* Scroll-to-top — position:absolute zodat hij buiten de kaart zweeft */}
        {showComposerScrollTop && (
          <button
            onClick={() => composerScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
            aria-label="Terug naar boven"
            style={{
              position: 'absolute',
              bottom: '-13px',
              right: '-13px',
              width: '26px',
              height: '26px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.92)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              border: '1px solid rgba(79,102,96,0.22)',
              color: '#3a5a54',
              animation: 'sagePulse 2.4s ease-in-out infinite',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 20,
              flexShrink: 0,
              outline: 'none',
            }}
          >
            <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24" style={{ display: 'block' }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
            </svg>
          </button>
        )}
        <div className="mb-3 space-y-2">
          <div>
            <div className="whitespace-nowrap text-sm font-semibold text-gray-800">SuperKonnected-preview</div>
            <div className="text-[11px] leading-snug text-gray-500">Swipe naar rechts voor LinkedIn, X, FB en Instagram.</div>
          </div>
          {media && (
            <>
              <div className="flex w-full rounded-full bg-gray-100 p-1">
                {(['vertical', 'horizontal'] as const).map((orientation) => (
                  <button
                    key={orientation}
                    type="button"
                    onClick={() => setMobileMediaPreviewOrientation(orientation)}
                    className={`flex-1 whitespace-nowrap rounded-full px-2.5 py-2 text-[11px] font-semibold leading-none ${
                      mobileMediaPreviewOrientation === orientation
                        ? 'bg-white text-[#4F6660] shadow-sm'
                        : 'text-gray-500'
                    }`}
                  >
                    {orientation === 'vertical' ? 'Verticaal' : 'Horizontaal'}
                  </button>
                ))}
              </div>
              <div className="flex w-full rounded-full bg-gray-100 p-1">
                {fitOptions.map((fit) => (
                  <button
                    key={fit.value}
                    type="button"
                    onClick={() => setMobileMediaFitMode(fit.value)}
                    className={`flex-1 whitespace-nowrap rounded-full px-2.5 py-2 text-[11px] font-semibold leading-none ${
                      mobileMediaFitMode === fit.value
                        ? 'bg-white text-[#4F6660] shadow-sm'
                        : 'text-gray-500'
                    }`}
                  >
                    {fit.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="rounded-2xl border border-gray-100 bg-[#FAFAF8] p-3">
          <div
            className="mb-2 flex h-7 w-full items-center justify-center rounded-full bg-[#4F6660] px-3 text-[10px] font-semibold"
            style={{ color: '#FFFFFF' }}
          >
            <span style={{ color: '#FFFFFF' }}>SuperKonnected</span>
          </div>
          <div className="mb-2 flex items-center gap-2">
            {authorPhoto ? (
              <img src={authorPhoto} alt="" className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#8DA8A0] text-xs font-bold text-white">
                {String(authorName).slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="truncate text-xs font-semibold text-gray-800">{authorName}</div>
              <div className="text-[10px] text-gray-500">Voorbeeld van je nieuwe post</div>
            </div>
          </div>
          {renderExpandableText('preview-superkonnected', previewContent, {
            collapsedLines: 4,
            className: `text-xs leading-relaxed ${previewContent.trim() ? 'text-gray-700' : 'text-gray-400'}`,
            placeholder: 'Je geschreven tekst komt hier boven de media te staan.',
          })}
          {media && renderMobileMediaAsset(media, feedAspectRatio, 'rounded-xl', mobileMediaFitMode, mobileMediaPosition, setMobileMediaPosition, 'superkonnected')}
          {media && mobileMediaFitMode === 'cover' && (
            <div className="mt-1.5 text-center text-[10px] text-gray-500">Sleep de afbeelding om de uitsnede te bepalen.</div>
          )}
        </div>

        <div className="mt-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div>
              <div className="text-xs font-semibold text-gray-800">Platform-preview</div>
              <div className="text-[10px] text-gray-500">Elke kaart staat hieronder en kan apart worden aangepast.</div>
            </div>
          </div>
          <div
            className="space-y-3"
            style={{ alignItems: 'stretch' }}
          >
            {platformMetas.map((platform) => {
              const platformFitMode = platformMediaFitModes[platform.id] || 'blur';
              const isSelected = normalizedSelectedPlatforms.includes(platform.id);
              const platformAspectRatio = getPlatformAspectRatioForPreview(platform.id, platform.aspectRatio);
              const platformPosition = platformMediaPositions[platform.id] || { x: 50, y: 50 };
              const platformAspectOptions = getPlatformAspectOptions(platform.id);
              return (
                <div
                  id={`mobile-platform-preview-${platform.id}`}
                  key={platform.id}
                  className="w-full rounded-2xl border border-gray-100 bg-white p-3"
                  style={{ height: 'auto', minHeight: 'unset', alignSelf: 'flex-start' }}
                >
                  <div className="mb-2">
                    <div
                      className="flex h-7 w-full items-center justify-center gap-1.5 rounded-full px-3 text-[10px] font-semibold"
                      style={{ backgroundColor: platform.accent, color: platform.textColor }}
                    >
                      <span className="text-white" style={{ color: '#FFFFFF' }}>{platform.icon}</span>
                      <span style={{ color: '#FFFFFF' }}>{platform.label}</span>
                    </div>
                    <div className="mt-1 text-center text-[10px] text-gray-500">
                      Formaat: {platformAspectRatio.replace(/\s/g, '')}
                    </div>
                  </div>
                  {media && (
                    <>
                      <div className="mb-2 flex w-full rounded-full bg-gray-100 p-1">
                        {platformAspectOptions.map((aspect) => (
                          <button
                            key={aspect.value}
                            type="button"
                            onClick={() => setPlatformAspectModes((prev) => ({ ...prev, [platform.id]: aspect.value }))}
                            className={`flex-1 whitespace-nowrap rounded-full px-2 py-1.5 text-[10px] font-semibold leading-none ${
                              (platformAspectModes[platform.id] || platformAspectOptions[0].value) === aspect.value
                                ? 'bg-white text-[#4F6660] shadow-sm'
                                : 'text-gray-500'
                            }`}
                          >
                            {aspect.label}
                          </button>
                        ))}
                      </div>
                      <div className="mb-2 flex w-full rounded-full bg-gray-100 p-1">
                        {fitOptions.map((fit) => (
                          <button
                            key={fit.value}
                            type="button"
                            onClick={() => setPlatformMediaFitModes((prev) => ({ ...prev, [platform.id]: fit.value }))}
                            className={`flex-1 whitespace-nowrap rounded-full px-2 py-1.5 text-[10px] font-semibold leading-none ${
                              platformFitMode === fit.value
                                ? 'bg-white text-[#4F6660] shadow-sm'
                                : 'text-gray-500'
                            }`}
                          >
                            {fit.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                  <div className="rounded-xl border border-gray-100 bg-[#FAFAF8] p-2">
                    {renderExpandableText(`preview-platform-${platform.id}`, previewContent, {
                      collapsedLines: 3,
                      className: `text-[11px] leading-relaxed ${previewContent.trim() ? 'text-gray-700' : 'text-gray-400'}`,
                      placeholder: 'Je tekst komt hier te staan.',
                    })}
                    {media && renderMobileMediaAsset(
                      media,
                      platformAspectRatio,
                      'rounded-lg',
                      platformFitMode,
                      platformPosition,
                      (position) => setPlatformMediaPositions((prev) => ({ ...prev, [platform.id]: position })),
                      `platform-${platform.id}`
                    )}
                    {media && platformFitMode === 'cover' && (
                      <div className="mt-1.5 text-center text-[10px] text-gray-500">Sleep om de uitsnede te verplaatsen.</div>
                    )}
                  </div>
                  <div className="mt-1.5 text-[10px] text-gray-500">{platform.hint}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Display settings - use props from App.tsx (global), fallback to local state for backwards compatibility
  type DayNightMode = 'day' | 'night';
  type DisplaySettings = {
    focusMode: boolean;
    energyMode: boolean;
    blueLightFilter: boolean;
    dayNightMode: DayNightMode;
  };
  const [localDisplaySettings, setLocalDisplaySettings] = useState<DisplaySettings>(() => {
    try {
      const saved = localStorage.getItem('displaySettings');
      if (saved) return JSON.parse(saved);
    } catch {}
    return { focusMode: false, energyMode: false, blueLightFilter: false, dayNightMode: 'day' };
  });

  // Use external props if available, otherwise use local state (backwards compatibility)
  const displaySettings = externalDisplaySettings || localDisplaySettings;
  const setDisplaySettings = externalSetDisplaySettings || setLocalDisplaySettings;

  // Helper function to get post filter status
  const getPostFilterStatus = (p: any): { filtered: boolean; reason: string | null } => {
    if (!cleanMindEnabled) return { filtered: false, reason: null };
    
    const content = String(p?.content || '').toLowerCase();
    const hasYouTubeOrVideoLink = /(youtube|vimeo|youtu\.be)/.test(content);
    const hasImageLink = /(\.png|\.jpg|\.jpeg|\.gif)(\?|$)/.test(content);
    const attachments = Array.isArray(p?.attachments) ? p.attachments : [];

    const hasVideoAttachment = attachments.some((a: any) => a?.type === 'video');
    const hasImageAttachment = attachments.some((a: any) => a?.type === 'image');
    const hasFileAttachment = attachments.some((a: any) => a?.type === 'file');

    // Heuristic: camera photos often have specific filenames
    const hasCameraPhotoAttachment = attachments.some((a: any) => a?.type === 'image' && typeof a?.name === 'string' && /(^(photo|camera|img|dsc)[-_]?)/i.test(a.name));
    const mentionsPhotoInText = /(\bfoto\b|\bphoto\b|📷)/i.test(content);

    const hasVideo = hasVideoAttachment || hasYouTubeOrVideoLink;
    const hasPhoto = hasCameraPhotoAttachment || mentionsPhotoInText;
    // Image (excluding photo where possible)
    const hasImage = (hasImageAttachment && !hasPhoto) || (!hasImageAttachment && hasImageLink);
    const isTextOnly = !hasVideo && !hasImage && !hasPhoto && !hasFileAttachment;

    // Return filter status - filter based on content type, but allow mixed content
    if (!cleanFilters.videos && hasVideo) return { filtered: true, reason: 'video' };
    if (!cleanFilters.photos && hasPhoto) return { filtered: true, reason: 'foto' };
    if (!cleanFilters.images && hasImage) return { filtered: true, reason: 'afbeelding' };
    if (!cleanFilters.text && isTextOnly) return { filtered: true, reason: 'tekst' };
    return { filtered: false, reason: null };
  };

  // Helper function to detect YouTube links
  const extractYouTubeVideoId = (text: string): string | null => {
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = text.match(youtubeRegex);
    return match ? match[1] : null;
  };

  // Helper function to render YouTube embedded player
  const renderYouTubePreview = (videoId: string, post?: any) => {
    const cta = post ? getPostCta(post) : null;
    const isDesktopOverlay = effectiveMobileTab === undefined;
    return (
      <div className="mt-3 mb-3">
        <div className="bg-gray-50 rounded-lg border p-3">
          <div className="flex items-center space-x-2 mb-3">
            <span className="text-red-600 text-sm font-medium flex items-center gap-1">
              <Video size={16} strokeWidth={1.5} />
              Video
            </span>
            <span className="text-xs text-gray-500">•</span>
            <span className="text-xs text-gray-500">Speel direct in de feed</span>
          </div>
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=0&controls=1&rel=0&modestbranding=1`}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute top-0 left-0 w-full h-full rounded"
            />
            {isDesktopOverlay && cta && (
              <button
                type="button"
                onClick={() => handlePostCtaClick(post, cta)}
                className="absolute top-2 right-2 px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm"
                style={{ backgroundColor: '#2E5F56', color: '#FFFFFF', zIndex: 20 }}
                title={cta.label}
              >
                {cta.label}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Helper function to remove YouTube links from text
  const removeYouTubeLinks = (text: string): string => {
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/g;
    return text.replace(youtubeRegex, '').trim();
  };

  // Helper function to render post content with filtering
  const renderPostContent = (post: any) => {
    const filterStatus = getPostFilterStatus(post);
    
    // If text filter is active and this is text-only content, show filtered message
    if (!cleanFilters.text && filterStatus.reason === 'tekst') {
      return (
        <div className="text-sm text-gray-500 italic mb-3 p-3 bg-gray-50 rounded border">
          <span className="flex items-center gap-1">
            <Lock size={14} strokeWidth={1.5} />
            Content gefilterd ({filterStatus.reason})
          </span>
        </div>
      );
    }
    
    const content = cleanTextEnabled ? cleanText(post.content) : post.content;
    const youtubeVideoId = extractYouTubeVideoId(content);
    
    // Remove YouTube links from text when preview is shown
    const displayContent = youtubeVideoId ? removeYouTubeLinks(content) : content;
    const hasAttachments = Array.isArray(post.attachments) && post.attachments.length > 0;
    const isTextOnly = !youtubeVideoId && !hasAttachments;
    
    return (
      <div className={isTextOnly ? 'pt-2' : ''}>
        {displayContent && renderExpandableText(`post-${post.id || post.timestamp || displayContent.slice(0, 24)}`, displayContent, {
          collapsedLines: effectiveMobileTab === 0 ? 4 : 7,
          className: `text-sm break-words break-all overflow-hidden max-w-full ${isTextOnly ? 'font-medium' : ''}`,
          threshold: effectiveMobileTab === 0 ? 220 : 420,
        })}
        {youtubeVideoId && renderYouTubePreview(youtubeVideoId, post)}
      </div>
    );
  };

  // Helper function to render post attachments with filtering
  const renderPostAttachments = (post: any) => {
    if (!Array.isArray(post.attachments) || post.attachments.length === 0) {
      return null;
    }

    const cta = getPostCta(post);
    const isDesktopOverlay = effectiveMobileTab === undefined;
    
    return (
      <div className="space-y-3 mb-3">
        {post.attachments.map((att: any) => {
          // Check if this specific attachment type should be filtered
          const isImage = att.type === 'image' || (att.name && /\.(jpg|jpeg|png|gif|webp)$/i.test(att.name));
          const isVideo = att.type === 'video' || (att.name && /\.(mp4|avi|mov|wmv|flv|webm)$/i.test(att.name));
          const isPhoto = isImage && (typeof att?.name === 'string' && /(^(photo|camera|img|dsc)[-_]?)/i.test(att.name));
          
          const shouldFilterImage = !cleanFilters.images && isImage;
          const shouldFilterVideo = !cleanFilters.videos && isVideo;
          const shouldFilterPhoto = !cleanFilters.photos && isPhoto;
          
          if (shouldFilterImage || shouldFilterVideo || shouldFilterPhoto) {
            return (
              <div key={att.url} className="p-3 bg-gray-50 rounded-lg border">
                <div className="text-sm text-gray-500 italic">
                  <span className="flex items-center gap-1">
                    <Lock size={14} strokeWidth={1.5} />
                    {att.type === 'video' ? 'Video' : 'Afbeelding'} gefilterd
                  </span>
                </div>
              </div>
            );
          }
          
          return (
            <div key={att.url} className="relative">
              {(att.type === 'image' || (att.name && /\.(jpg|jpeg|png|gif|webp)$/i.test(att.name))) && (
                <img src={att.url} alt={att.name || 'Afbeelding'} className="w-full max-h-[80vh] object-contain rounded-lg border bg-gray-50" />
              )}
              {(att.type === 'video' || (att.name && /\.(mp4|avi|mov|wmv|flv|webm)$/i.test(att.name))) && (
                <video src={att.url} controls playsInline className="w-full max-h-[60vh] rounded-lg border" />
              )}
              {isDesktopOverlay && cta && (att.type === 'image' || att.type === 'video' || (att.name && /\.(jpg|jpeg|png|gif|webp|mp4|avi|mov|wmv|flv|webm)$/i.test(att.name))) && (
                <button
                  type="button"
                  onClick={() => handlePostCtaClick(post, cta)}
                  className="absolute top-2 right-2 px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm"
                  style={{ backgroundColor: '#2E5F56', color: '#FFFFFF', zIndex: 20 }}
                  title={cta.label}
                >
                  {cta.label}
                </button>
              )}
              {(att.type === 'file' || (!att.type && att.name && !/\.(jpg|jpeg|png|gif|webp|mp4|avi|mov|wmv|flv|webm)$/i.test(att.name))) && (
                <button
                  onClick={() => setFilePreview({ url: att.url, name: att.name || 'Bestand' })}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-gray-50 hover:bg-gray-100 text-sm w-full text-left"
                >
                  <FileText size={16} strokeWidth={1.5} />
                  <span className="truncate">{att.name || 'Bestand'}</span>
                  <span className="ml-auto text-xs text-gray-400">Bekijken</span>
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Feed visibility (persisted)
  type FeedKey = 'news' | 'community' | 'my' | 'pinned';
  type FeedsVisibility = { news: boolean; community: boolean; my: boolean; pinned: boolean };
  const [feedsVisibility, setFeedsVisibility] = useState<FeedsVisibility>(() => {
    try {
      const saved = localStorage.getItem('feedsVisibility');
      if (saved) return JSON.parse(saved);
    } catch {}
    // Standaard: alleen Nieuwsfeed actief
    return { news: true, community: false, my: false, pinned: false };
  });
  useEffect(() => {
    try { localStorage.setItem('feedsVisibility', JSON.stringify(feedsVisibility)); } catch {}
  }, [feedsVisibility]);

  // Active feeds and responsive grid columns
  const activeFeeds = useMemo<FeedKey[]>(() => (
    [
      feedsVisibility.news ? 'news' : null,
      feedsVisibility.community ? 'community' : null,
      feedsVisibility.my ? 'my' : null,
      feedsVisibility.pinned ? 'pinned' : null
    ].filter(Boolean) as FeedKey[]
  ), [feedsVisibility]);

  const feedGridCols = useMemo<string>(() => {
    const count = activeFeeds.length;
    if (count >= 3) return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
    if (count === 2) return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-2';
    return 'grid-cols-1 md:grid-cols-1 lg:grid-cols-1';
  }, [activeFeeds.length]);

  const selectSingleFeed = (key: FeedKey) => {
    setFeedsVisibility({
      news: key === 'news',
      community: key === 'community',
      my: key === 'my',
      pinned: key === 'pinned',
    });
    setExpandedFeed(null);
    setMobileFeedChromeHidden(false);
    lastMobileFeedScrollTopRef.current = 0;
    window.dispatchEvent(new CustomEvent('mobileFeedChromeVisibility', { detail: { hidden: false } }));
  };

  const getActiveFeedKey = (): FeedKey => {
    if (feedsVisibility.news) return 'news';
    if (feedsVisibility.my) return 'my';
    if (feedsVisibility.community) return 'community';
    if (feedsVisibility.pinned) return 'pinned';
    return 'news';
  };

  const cycleMobileFeed = () => {
    const order: FeedKey[] = ['news', 'my', 'community', 'pinned'];
    const current = getActiveFeedKey();
    const next = order[(order.indexOf(current) + 1) % order.length];
    selectSingleFeed(next);
  };

  const activeMobileFeedLabel = {
    news: 'Newsfeed',
    my: 'Mijn posts',
    community: 'Community',
    pinned: 'Pins',
  }[getActiveFeedKey()];
  const mobileFeedOrder: FeedKey[] = ['news', 'my', 'community', 'pinned'];
  const activeMobileFeedIndex = Math.max(0, mobileFeedOrder.indexOf(getActiveFeedKey()));

  const handleMobileFeedScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (effectiveMobileTab !== 0) return;
    const nextTop = event.currentTarget.scrollTop;
    const previousTop = lastMobileFeedScrollTopRef.current;
    const delta = nextTop - previousTop;

    if (nextTop <= 12) {
      setMobileFeedChromeHidden(false);
      window.dispatchEvent(new CustomEvent('mobileFeedChromeVisibility', { detail: { hidden: false } }));
    } else if (delta > 2) {
      setMobileFeedChromeHidden(true);
      window.dispatchEvent(new CustomEvent('mobileFeedChromeVisibility', { detail: { hidden: true } }));
    } else if (delta < -2) {
      setMobileFeedChromeHidden(false);
      window.dispatchEvent(new CustomEvent('mobileFeedChromeVisibility', { detail: { hidden: false } }));
    }

    const mobileBucket = Math.floor(nextTop / 420);
    if (mobileBucket > mobileLockBucketRef.current && sortedDisplayPosts.length > 0) {
      mobileLockBucketRef.current = mobileBucket;
      registerItemView(getScreenLockRelevanceScore(sortedDisplayPosts[mobileBucket % sortedDisplayPosts.length]));
    }

    lastMobileFeedScrollTopRef.current = nextTop;
  };

  const MAX_ACTIVE_FEEDS = 3;
  const toggleFeed = (key: FeedKey) => {
    setFeedsVisibility(prev => {
      const next: FeedsVisibility = { ...prev, [key]: !prev[key] } as any;
      const nextCount = Object.values(next).filter(Boolean).length;
      if (nextCount > MAX_ACTIVE_FEEDS) {
        // Replacement rule: when enabling 'community' beyond limit, disable 'news'.
        if (key === 'community' && next.news) {
          next.news = false;
        } else if (key === 'news' && next.community) {
          next.community = false;
        } else {
          // Fallback: disable pinned first, then my, then news
          if (next.pinned && key !== 'pinned') next.pinned = false; 
          else if (next.my && key !== 'my') next.my = false; 
          else if (next.news && key !== 'news') next.news = false;
        }
      }
      return next;
    });
  };

  // When opening Mijn community: prefer showing Community feed replacing Newsfeed if limit reached
  // UITGESCHAKELD: Community Feed wordt niet meer automatisch geactiveerd
  // useEffect(() => {
  //   if (!communityOpen) return;
  //   setFeedsVisibility(prev => {
  //     if (prev.community) return prev;
  //     const next: FeedsVisibility = { ...prev, community: true };
  //     const count = Object.values(next).filter(Boolean).length;
  //     if (count > MAX_ACTIVE_FEEDS && next.news) {
  //       next.news = false;
  //     }
  //     return next;
  //   });
  // }, [communityOpen]);

  // Load pending connection requests for this user (receiver) with dedupe across schemas
  useEffect(() => {
    let unsub: any;
    (async () => {
      if (!user?.uid) return;
      const { onSnapshot, collection, query, where } = await import('firebase/firestore');
      const qA = query(collection(db, 'connectionRequests'), where('receiverId', '==', user.uid)) as any;
      const qB = query(collection(db, 'connectionRequests'), where('toUserId', '==', user.uid)) as any;
      let docsA: any[] = []; let docsB: any[] = [];
      const recalc = () => {
        // normalize and merge
        const normalize = (d: any) => {
          const data = d.data?.() || d.data || {};
          const createdAt = (data.createdAt?.toMillis?.() || data.createdAt || 0);
          const fromUserId = data.fromUserId || data.senderId;
          const toUserId = data.toUserId || data.receiverId;
          return { id: d.id, ...data, fromUserId, toUserId, createdAt };
        };
        const combined = [...docsA, ...docsB].map(normalize)
          .filter((x) => x.status === 'pending' || x.status === 'proposed' || x.status === 'waiting');
        
        // dedupe by unordered pair, keep latest createdAt
        const map = new Map<string, any>();
        const duplicates: string[] = [];
        
        combined.forEach((r) => {
          const a = String(r.fromUserId || r.senderId || '');
          const b = String(r.toUserId || r.receiverId || '');
          if (!a || !b) return; // Skip invalid requests
          
          const key = a < b ? `${a}|${b}` : `${b}|${a}`;
          const prev = map.get(key);
          
          // Keep the one with latest createdAt, but prefer pending over proposed/waiting
          if (!prev) {
            map.set(key, r);
          } else {
            // Found duplicate - log it
            duplicates.push(`${prev.id} vs ${r.id} (${key})`);
            
            // Keep the better one
            if ((prev.status !== 'pending' && r.status === 'pending') ||
                (prev.status === r.status && (prev.createdAt < r.createdAt))) {
              map.set(key, r);
            }
          }
        });
        
        if (duplicates.length > 0) {
          console.log('⚠️ Found duplicate requests:', duplicates);
        }
        
        const items = Array.from(map.values())
          .sort((a, b) => {
            // First sort by status priority (pending first)
            if (a.status === 'pending' && b.status !== 'pending') return -1;
            if (b.status === 'pending' && a.status !== 'pending') return 1;
            // Then by createdAt
            return (b.createdAt || 0) - (a.createdAt || 0);
          });
        
        console.log(`📋 Pending connects after dedupe: ${items.length} (from ${combined.length} total)`);
        setReceivedRequests(items);
        setPendingConnects(items);
      };
      const unsubA = onSnapshot(qA, (snap: any) => { docsA = snap.docs; recalc(); });
      const unsubB = onSnapshot(qB, (snap: any) => { docsB = snap.docs; recalc(); });
      unsub = () => { try { unsubA && unsubA(); unsubB && unsubB(); } catch {} };
    })();
    return () => { try { unsub && unsub(); } catch {} };
  }, [user?.uid]);

  // Load sent requests for current user with dedupe across schemas
  useEffect(() => {
    let unsub: any;
    (async () => {
      if (!user?.uid) return;
      const { onSnapshot, collection, query, where } = await import('firebase/firestore');
      const qA = query(collection(db, 'connectionRequests'), where('senderId', '==', user.uid)) as any;
      const qB = query(collection(db, 'connectionRequests'), where('fromUserId', '==', user.uid)) as any;
      let docsA: any[] = []; let docsB: any[] = [];
      const recalc = () => {
        const normalize = (d: any) => {
          const data = d.data?.() || d.data || {};
          const createdAt = (data.createdAt?.toMillis?.() || data.createdAt || 0);
          const fromUserId = data.fromUserId || data.senderId;
          const toUserId = data.toUserId || data.receiverId;
          return { id: d.id, ...data, fromUserId, toUserId, createdAt };
        };
        const combined = [...docsA, ...docsB].map(normalize);
        const map = new Map<string, any>();
        combined.forEach((r) => {
          const a = String(r.fromUserId || '');
          const b = String(r.toUserId || '');
          const key = a < b ? `${a}|${b}` : `${b}|${a}`;
          const prev = map.get(key);
          if (!prev || (prev.createdAt < r.createdAt)) map.set(key, r);
        });
        const items = Array.from(map.values()).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setSentRequests(items);
      };
      const unsubA = onSnapshot(qA, (snap: any) => { docsA = snap.docs; recalc(); });
      const unsubB = onSnapshot(qB, (snap: any) => { docsB = snap.docs; recalc(); });
      unsub = () => { try { unsubA && unsubA(); unsubB && unsubB(); } catch {} };
    })();
    return () => { try { unsub && unsub(); } catch {} };
  }, [user?.uid]);

  // Load accepted connections for Community feed (both schema variants)
  const [communityUserIds, setCommunityUserIds] = useState<string[]>([]);
  useEffect(() => {
    let unsub: any;
    (async () => {
      if (!user?.uid) return;
      const { onSnapshot, collection, query, where } = await import('firebase/firestore');
      const q1 = query(collection(db, 'connectionRequests'), where('status', '==', 'accepted'), where('senderId', '==', user.uid)) as any;
      const q2 = query(collection(db, 'connectionRequests'), where('status', '==', 'accepted'), where('receiverId', '==', user.uid)) as any;
      const q3 = query(collection(db, 'connectionRequests'), where('status', '==', 'accepted'), where('fromUserId', '==', user.uid)) as any;
      const q4 = query(collection(db, 'connectionRequests'), where('status', '==', 'accepted'), where('toUserId', '==', user.uid)) as any;
      let d1: any[] = []; let d2: any[] = []; let d3: any[] = []; let d4: any[] = [];
      const recompute = () => {
        const ids = new Set<string>();
        const add = (docs: any[], pick: (d: any) => string | undefined) => {
          docs.forEach((doc: any) => {
            const other = pick(doc);
            if (other) ids.add(String(other));
          });
        };
        add(d1, (d) => (d.data?.()?.receiverId || d.data?.()?.toUserId));
        add(d2, (d) => (d.data?.()?.senderId || d.data?.()?.fromUserId));
        add(d3, (d) => (d.data?.()?.toUserId || d.data?.()?.receiverId));
        add(d4, (d) => (d.data?.()?.fromUserId || d.data?.()?.senderId));
        setCommunityUserIds(Array.from(ids));
      };
      const u1 = onSnapshot(q1, (snap: any) => { d1 = snap.docs; recompute(); });
      const u2 = onSnapshot(q2, (snap: any) => { d2 = snap.docs; recompute(); });
      const u3 = onSnapshot(q3, (snap: any) => { d3 = snap.docs; recompute(); });
      const u4 = onSnapshot(q4, (snap: any) => { d4 = snap.docs; recompute(); });
      unsub = () => { try { u1(); u2(); u3(); u4(); } catch {} };
    })();
    return () => { try { unsub && unsub(); } catch {} };
  }, [user?.uid]);

  // Clean Mind Filter state and derived filtering
  type CleanFilters = { videos: boolean; images: boolean; photos: boolean; text: boolean; ai: boolean };
  const [cleanMindEnabled, setCleanMindEnabled] = useState<boolean>(() => {
    try { return JSON.parse(localStorage.getItem('cleanMindEnabled') || 'false'); } catch { return false; }
  });
  const [cleanFilters, setCleanFilters] = useState<CleanFilters>(() => {
    try {
      const saved = localStorage.getItem('cleanFilters');
      // ai defaults to true (show AI content) for backward compatibility with old saved values
      if (saved) return { ai: true, ...JSON.parse(saved) };
    } catch {}
    return { videos: true, images: true, photos: true, text: true, ai: true };
  });
  const [removeEmojis, setRemoveEmojis] = useState<boolean>(() => {
    try { return JSON.parse(localStorage.getItem('cleanRemoveEmojis') || 'true'); } catch { return true; }
  });
  const [cleanTextEnabled, setCleanTextEnabled] = useState<boolean>(() => {
    try { return JSON.parse(localStorage.getItem('cleanTextEnabled') || 'false'); } catch { return false; }
  });
  const [connectionStatuses, setConnectionStatuses] = useState<Record<string, 'none' | 'pending' | 'connected'>>({});
  const [autoRedirectToChat, setAutoRedirectToChat] = useState<boolean>(() => {
    const saved = localStorage.getItem('autoRedirectToChat');
    return saved ? JSON.parse(saved) : true; // Default to true for backward compatibility
  });

  const handleAutoRedirectToggle = (enabled: boolean) => {
    setAutoRedirectToChat(enabled);
    localStorage.setItem('autoRedirectToChat', JSON.stringify(enabled));
  };
  const defaultSoftWords = useMemo<string[]>(() => ['kanker', 'fuck', 'shit'], []);
  const presetSwearWords = useMemo<string[]>(
    () => [
      // Nederlandse scheldwoorden
      'kanker','tyfus','tering','kut','lul','hoer','sukkel','debiel','idioot',
      'klootzak','eikel','lulletje','kutwijf','hoerenzoon','kankerlijer',
      'mongool','retard','gehandicapt','spastisch','autist','downie',
      'neger','kankerjood','kutmarokkaan','kankerallochtoon','homo','flikker',
      'trut','wijf','bitch','slut','whore','cunt','dickhead','motherfucker',
      'fuck','shit','bitch','asshole','bastard','damn','crap','piss','piss off',
      'fucking','shitty','damned','bloody','hell','bollocks','wanker',
      // Emotioneel kwetsende woorden
      'lelijk','dik','vet','mager','dun','dom','stom','slecht','waardeloos',
      'mislukking','loser','verliezer','niemand','alleen','eenzaam','haten',
      'walgelijk','smerig','vies','stinkend','irritant','vervelend','saai',
      'nutteloos','overbodig','verspilling','tijdverspilling','energieverspilling'
    ],
    []
  );
  const presetNegativePhrases = useMemo<string[]>(
    () => [
      // Zelfkritische zinnen
      'ik kan het niet','ik ben waardeloos','ik ben een mislukking',
      'niemand houdt van mij','ik ben dom','dit heeft geen zin',
      'het is onmogelijk','ik ben lelijk','ik ben dik','ik ben te dun',
      'ik ben niet goed genoeg','ik ben een loser','ik ben een verliezer',
      'ik ben alleen','niemand wil mij','ik ben nutteloos','ik ben overbodig',
      'ik ben een verspilling','ik ben een tijdverspilling','ik ben een energieverspilling',
      'ik ben een teleurstelling','ik ben een schande','ik ben een fout',
      'ik ben een mislukking','ik ben een ramp','ik ben een catastrofe',
      'het lukt nooit','altijd fout','ik geef op','ik ben een last',
      'ik ben een probleem','ik ben een obstakel','ik ben een belemmering',
      'ik ben een hindernis','ik ben een blok aan het been','ik ben niet welkom',
      'ik ben niet gewenst','ik ben niet geliefd','ik ben niet gewaardeerd',
      'ik ben niet gerespecteerd','ik ben niet geaccepteerd',
      // Negatieve uitspraken over anderen
      'jij bent dom','jij bent lelijk','jij bent dik','jij bent waardeloos',
      'jij bent een mislukking','jij bent een loser','jij bent nutteloos',
      'jij bent overbodig','jij bent een verspilling','jij bent een teleurstelling',
      'jij bent een schande','jij bent een fout','jij bent een ramp',
      'jij bent een catastrofe','jij bent een tijdverspilling','jij bent een energieverspilling',
      'jij bent een last','jij bent een probleem','jij bent een obstakel',
      'jij bent een belemmering','jij bent een hindernis','jij bent een blok aan het been',
      'jij bent niet welkom','jij bent niet gewenst','jij bent niet geliefd',
      'jij bent niet gewaardeerd','jij bent niet gerespecteerd','jij bent niet geaccepteerd',
      // Hopeloze uitspraken
      'het heeft geen zin','het is hopeloos','het is nutteloos','het is overbodig',
      'het is een verspilling','het is een tijdverspilling','het is een energieverspilling',
      'het is een teleurstelling','het is een schande','het is een fout',
      'het is een ramp','het is een catastrofe','het is onmogelijk',
      'het gaat nooit lukken','het is voor niets','het is zinloos',
      // Emotioneel kwetsende zinnen
      'niemand wil je','niemand houdt van je','je bent alleen','je bent eenzaam',
      'je bent niet welkom','je bent niet gewenst','je bent niet geliefd',
      'je bent niet gewaardeerd','je bent niet gerespecteerd','je bent niet geaccepteerd',
      'je bent een last','je bent een probleem','je bent een obstakel',
      'je bent een belemmering','je bent een hindernis','je bent een blok aan het been'
    ],
    []
  );
  const [customBlockedWords, setCustomBlockedWords] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('cleanCustomWords');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  useEffect(() => { try { localStorage.setItem('cleanCustomWords', JSON.stringify(customBlockedWords)); } catch {} }, [customBlockedWords]);

  const [insightsOpen, setInsightsOpen] = useState(false);
  const [newCustomWord, setNewCustomWord] = useState('');
  const [insightsData, setInsightsData] = useState<{ emojiRemoved: number; controlCharsRemoved: number; wordMatches: Record<string, number>; totalPosts: number } | null>(null);
  const formatPostDate = (d: any) => {
    const date = new Date(d);
    // Gebruik browser locale; fallback nl-NL
    const locale = navigator?.language || 'nl-NL';
    return new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
  };

  useEffect(() => {
    try { localStorage.setItem('cleanMindEnabled', JSON.stringify(cleanMindEnabled)); } catch {}
  }, [cleanMindEnabled]);

  // Re-sync clean mind + display settings when ProfileHub writes them (mobile)
  useEffect(() => {
    const handleSettingsChanged = () => {
      try {
        const enabled = JSON.parse(localStorage.getItem('cleanMindEnabled') || 'false');
        setCleanMindEnabled(enabled);
        const saved = localStorage.getItem('cleanFilters');
        if (saved) setCleanFilters({ ai: true, ...JSON.parse(saved) });
        const emojis = JSON.parse(localStorage.getItem('cleanRemoveEmojis') || 'true');
        setRemoveEmojis(emojis);
        const cleanText = JSON.parse(localStorage.getItem('cleanTextEnabled') || 'false');
        setCleanTextEnabled(cleanText);
      } catch {}
    };
    window.addEventListener('settingsChanged', handleSettingsChanged);
    return () => window.removeEventListener('settingsChanged', handleSettingsChanged);
  }, []);
  
  // 🏢 Load LinkedIn organizations when component mounts
  // NOTE: Organization posting disabled - requires LinkedIn Marketing Partner status
  // or verified company with approved business case for w_organization_social scope
  /* 
  useEffect(() => {
    const loadLinkedInOrgs = async () => {
      if (!user) return;
      
      try {
        // Load saved preference from Firestore
        const { getDoc, doc: firestoreDoc } = await import('firebase/firestore');
        const connectionDoc = await getDoc(firestoreDoc(db, 'socialConnections', user.uid));
        const linkedinData = connectionDoc.data()?.linkedin;
        
        // Set default if user has a saved preference
        if (linkedinData?.postAsOrganization && linkedinData?.defaultOrganizationId) {
          setLinkedInAsOrganization(true);
          setSelectedLinkedInOrg(linkedinData.defaultOrganizationId);
        }
        
        // Fetch organizations user is admin of from LinkedIn API
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const functions = getFunctions();
        const getOrgsFunction = httpsCallable(functions, 'getLinkedInOrganizations');
        
        const result = await getOrgsFunction();
        const data = result.data as { success: boolean; organizations?: any[] };
        if (data.success && data.organizations) {
          setLinkedInOrganizations(data.organizations);
          console.log(`📊 Loaded ${data.organizations.length} LinkedIn organization(s)`);
        }
      } catch (error) {
        console.log('ℹ️ Could not load LinkedIn organizations (normal if user is not admin of any)');
      }
    };
    
    loadLinkedInOrgs();
  }, [user]);
  */
  useEffect(() => {
    try { localStorage.setItem('cleanFilters', JSON.stringify(cleanFilters)); } catch {}
  }, [cleanFilters]);
  useEffect(() => {
    try { localStorage.setItem('cleanRemoveEmojis', JSON.stringify(removeEmojis)); } catch {}
  }, [removeEmojis]);
  useEffect(() => {
    try { localStorage.setItem('cleanTextEnabled', JSON.stringify(cleanTextEnabled)); } catch {}
  }, [cleanTextEnabled]);

  // Precompiled control characters regex to avoid lint no-control-regex
  const CONTROL_CHARS_RE = new RegExp('[\\x00-\\x1F\\x7F]', 'g');

  const cleanText = (content: string): string => {
    // Basic sanitization: collapse whitespace, strip control chars
    const withoutCtrl = (content || '').replace(CONTROL_CHARS_RE, '');
    
    // Only remove emojis if the removeEmojis toggle is specifically enabled
    let stripped = withoutCtrl;
    if (removeEmojis) {
      // Without Unicode property escapes: conservative broad removal
      stripped = stripped
        .replace(/[\uD83C-\uDBFF][\uDC00-\uDFFF]/g, '') // surrogate pair emojis
        .replace(/[\u2600-\u27BF]/g, '') // BMP symbols/dingbats
        .replace(/[\u200D\uFE0F\uFE0E]/g, '') // ZWJ + variation selectors
        .replace(/\uD83C[\uDFFB-\uDFFF]/g, ''); // skin tone modifiers
    }
    
    const base = stripped;
    
    // Only apply profanity filtering if cleanTextEnabled is true
    if (!cleanTextEnabled) {
      return base.replace(/\s{2,}/g, ' ').trim();
    }
    
    // Profanity softener using default + custom words
    const allWords = [...defaultSoftWords, ...customBlockedWords].filter(Boolean);
    let softened = base;
    const maskWord = (w: string) => '*'.repeat(Math.max(3, w.length)); // Complete masking, minimum 3 stars
    
    // Sort words by length (longest first) to handle merged words properly
    const sortedWords = allWords.sort((a, b) => b.length - a.length);
    
    sortedWords.forEach((w) => {
      const safe = w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const hasSpace = /\s/.test(w);
      
      if (hasSpace) {
        // For phrases with spaces, use exact matching
        const re = new RegExp(safe, 'gi');
        softened = softened.replace(re, (m) => maskWord(m));
      } else {
        // For single words, try both word boundary and non-word boundary matching
        // First try word boundary matching
        const wordBoundaryRe = new RegExp(`\\b${safe}\\b`, 'gi');
        softened = softened.replace(wordBoundaryRe, (m) => maskWord(m));
        
        // Then try non-word boundary matching for merged words
        const nonWordBoundaryRe = new RegExp(safe, 'gi');
        softened = softened.replace(nonWordBoundaryRe, (m) => maskWord(m));
      }
    });
    return softened.replace(/\s{2,}/g, ' ').trim();
  };

  const filteredCommunityPosts = useMemo(() => {
    const now = Date.now();
    const sorted = [...communityPosts].sort((a: any, b: any) => {
      // Calculate ranking weights with boosts
      const aVibeScore = a.vibeScore || 50;
      const bVibeScore = b.vibeScore || 50;
      
      const aEngagement = {
        saves: a.saves || 0,
        shares: a.shares || 0,
        comments: a.comments || 0,
        likes: a.likes || 0
      };
      const bEngagement = {
        saves: b.saves || 0,
        shares: b.shares || 0,
        comments: b.comments || 0,
        likes: b.likes || 0
      };
      
      // Calculate freshness decay (newer = higher)
      const aTimestamp = (a?.timestamp?.toDate?.() || a?.timestamp) ? new Date(a.timestamp?.toDate?.() || a.timestamp).getTime() : 0;
      const bTimestamp = (b?.timestamp?.toDate?.() || b?.timestamp) ? new Date(b.timestamp?.toDate?.() || b.timestamp).getTime() : 0;
      const hoursSinceA = (now - aTimestamp) / (1000 * 60 * 60);
      const hoursSinceB = (now - bTimestamp) / (1000 * 60 * 60);
      const aFreshness = Math.max(0, 1 - (hoursSinceA / 168)); // Decay over 7 days
      const bFreshness = Math.max(0, 1 - (hoursSinceB / 168));
      
      // Get visibility boosts
      const aBoosts = a.visibilityBoosts ? {
        newsfeed: a.visibilityBoosts.newsfeed?.until?.toDate ? { until: a.visibilityBoosts.newsfeed.until.toDate(), weight: a.visibilityBoosts.newsfeed.weight } : undefined,
        jumpstarter: a.visibilityBoosts.jumpstarter?.until?.toDate ? { until: a.visibilityBoosts.jumpstarter.until.toDate(), weight: a.visibilityBoosts.jumpstarter.weight } : undefined
      } : undefined;
      const bBoosts = b.visibilityBoosts ? {
        newsfeed: b.visibilityBoosts.newsfeed?.until?.toDate ? { until: b.visibilityBoosts.newsfeed.until.toDate(), weight: b.visibilityBoosts.newsfeed.weight } : undefined,
        jumpstarter: b.visibilityBoosts.jumpstarter?.until?.toDate ? { until: b.visibilityBoosts.jumpstarter.until.toDate(), weight: b.visibilityBoosts.jumpstarter.weight } : undefined
      } : undefined;
      
      // Calculate ranking weights with boosts
      const aWeight = getFeedRankingWeight(aVibeScore, aEngagement, aFreshness, aBoosts);
      const bWeight = getFeedRankingWeight(bVibeScore, bEngagement, bFreshness, bBoosts);
      
      // Sort by ranking weight (higher = better)
      if (bWeight !== aWeight) {
        return bWeight - aWeight;
      }
      
      // Fallback to timestamp if weights are equal
      return bTimestamp - aTimestamp;
    });

    // Filter out posts from muted users
    const filteredByMuted = sorted.filter(post => {
      const postUserId = post.userId || post.authorId;
      if (postUserId && isUserMuted(postUserId)) {
        return false; // Hide posts from muted users
      }
      return true;
    });

    if (!cleanMindEnabled) return filteredByMuted;


    // Optional demp-logica: demp posts van relaties die recent decline-den
    const mutedByRelationship: Record<string, number> = {};
    try {
      // Expect localStorage entry like { "uidB": 30DaysTimestamp }
      const raw = localStorage.getItem('mutedRelationships');
      if (raw) Object.assign(mutedByRelationship, JSON.parse(raw));
    } catch {}

    return sorted
      .filter((p: any) => {
        // Relationship-mute filter
        const authorId = p?.userId;
        if (authorId) {
          const until = mutedByRelationship[authorId];
          if (until && until > now) return false;
        }
        // A.I.-content filter: when cleanFilters.ai is false, hide AI-labeled posts
        if (!cleanFilters.ai) {
          const disc = p?.aiDisclosure as string | undefined;
          if (disc === 'partial' || disc === 'full') return false;
        }
        return true;
      });
  }, [communityPosts, cleanMindEnabled, cleanFilters, muteRefresh]);

  // Community feed: only posts from accepted connections
  const communityFeedPosts = useMemo(() => {
    if (!Array.isArray(communityUserIds) || communityUserIds.length === 0) return [] as any[];
    const allowed = new Set(communityUserIds.map(String));
    const excluded = new Set((() => {
      try { return (JSON.parse(localStorage.getItem('communityFollowExcluded') || '[]') as string[]) } catch { return [] as string[] }
    })().map(String));
    return filteredCommunityPosts.filter((p: any) => {
      const uid = String(p?.userId || '');
      return allowed.has(uid) && !excluded.has(uid);
    });
  }, [filteredCommunityPosts, communityUserIds]);

  // Toggle voor "Connections Only" filter
  const [connectionsOnly, setConnectionsOnly] = useState<boolean>(false);

  // Sla keuze op
  useEffect(() => {
    try {
      localStorage.setItem('newsfeedConnectionsOnly', String(connectionsOnly));
    } catch {}
  }, [connectionsOnly]);

  // Bepaal welke feed te tonen
  const displayPosts = useMemo(() => {
    if (connectionsOnly) {
      return communityFeedPosts; // Alleen connecties
    } else {
      return filteredCommunityPosts; // Alles
    }
  }, [connectionsOnly, communityFeedPosts, filteredCommunityPosts]);

  // ✅ Sort posts based on user's format preferences
  const sortedDisplayPosts = useMemo(() => {
    const userFormatPrefs = userProfile?.contentPrefs?.formatPrefs;
    if (!userFormatPrefs || userFormatPrefs.length === 0) {
      // No preferences set - return posts as is
      return displayPosts;
    }
    // Sort posts so that preferred formats appear first
    return sortPostsByFormatPreference(displayPosts, userFormatPrefs);
  }, [displayPosts, userProfile?.contentPrefs?.formatPrefs]);

  const getScreenLockRelevanceScore = (post: any): number => {
    if (!post) return 0;
    const content = String(post?.content || '').toLowerCase();
    const goals = [
      'klant', 'leads', 'groei', 'partnership', 'samenwerking', 'netwerk', 'strategie',
      'sales', 'ondernemen', 'business', 'investering', 'funding'
    ];
    const matches = goals.reduce((sum, keyword) => sum + (content.includes(keyword) ? 1 : 0), 0);
    return Math.min(100, 55 + matches * 8);
  };

  const {
    enabled: screenLockEnabled,
    shouldShowLock,
    markShown: markScreenLockShown,
    registerItemView,
    registerOutcome: registerScreenLockOutcome,
  } = useFeedScreenLock({
    userId: user?.uid,
    isMobile: isPhoneViewport(),
    enabled: feedToolsPilotService.isScreenLockPilotEnabled(user?.uid),
  });

  // ✅ Sort community feed posts as well
  const sortedCommunityFeedPosts = useMemo(() => {
    const userFormatPrefs = userProfile?.contentPrefs?.formatPrefs;
    if (!userFormatPrefs || userFormatPrefs.length === 0) {
      return communityFeedPosts;
    }
    return sortPostsByFormatPreference(communityFeedPosts, userFormatPrefs);
  }, [communityFeedPosts, userProfile?.contentPrefs?.formatPrefs]);

  // Follow/Unfollow authors in community
  const [communityFollowExcluded, setCommunityFollowExcluded] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('communityFollowExcluded') || '[]'); } catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem('communityFollowExcluded', JSON.stringify(communityFollowExcluded)); } catch {}
  }, [communityFollowExcluded]);

  const communityAuthors = useMemo(() => {
    const map = new Map<string, { userId: string; name: string; photo: string }>();
    communityFeedPosts.forEach((p: any) => {
      const uid = String(p?.userId || '');
      if (!uid || map.has(uid)) return;
      map.set(uid, { userId: uid, name: p?.author || uid, photo: getPostAvatar(p) });
    });
    return Array.from(map.values());
  }, [communityFeedPosts]);

  const [showAllAuthors, setShowAllAuthors] = useState(false);

  // Today counter for community feed
  const postsTodayCount = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const startMs = start.getTime();
    return communityFeedPosts.filter((p: any) => {
      const t = p?.timestamp?.toDate?.() || p?.timestamp || Date.now();
      const ms = new Date(t).getTime();
      return ms >= startMs;
    }).length;
  }, [communityFeedPosts]);

  // Access gate for community feed (locked behind subscription/feature)
  const hasCommunityAccess = useMemo(() => {
    try {
      return Boolean((userProfile as any)?.features?.communityFeed === true);
    } catch {
      return false;
    }
  }, [userProfile]);

  const pinnedPosts = useMemo(() => {
    return [...communityPosts]
      .filter((p: any) => p.pinned)
      .sort((a: any, b: any) => {
        const ta = (a?.timestamp?.toDate?.() || a?.timestamp) ? new Date(a.timestamp?.toDate?.() || a.timestamp).getTime() : 0;
        const tb = (b?.timestamp?.toDate?.() || b?.timestamp) ? new Date(b.timestamp?.toDate?.() || b.timestamp).getTime() : 0;
        return tb - ta;
      });
  }, [communityPosts]);

  const myPosts = useMemo(() => {
    if (!user?.uid) return [] as any[];
    return [...communityPosts]
      .filter((p: any) => p.userId === user.uid)
      .sort((a: any, b: any) => {
        const ta = (a?.timestamp?.toDate?.() || a?.timestamp) ? new Date(a.timestamp?.toDate?.() || a.timestamp).getTime() : 0;
        const tb = (b?.timestamp?.toDate?.() || b?.timestamp) ? new Date(b.timestamp?.toDate?.() || b.timestamp).getTime() : 0;
        return tb - ta;
      });
  }, [communityPosts, user?.uid]);

  // Calming visual effect similar to original behavior
  const containerStyle = useMemo(() => (
    cleanMindEnabled
      ? { filter: 'saturate(0.92) contrast(0.98)', transition: 'filter 150ms ease' as const }
      : undefined
  ), [cleanMindEnabled]);

  const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const generateInsights = (): { emojiRemoved: number; controlCharsRemoved: number; wordMatches: Record<string, number>; totalPosts: number } => {
    let emoji = 0;
    let ctrl = 0;
    const wordMatches: Record<string, number> = {};
    const words = [...defaultSoftWords, ...customBlockedWords].filter(Boolean);
    communityPosts.forEach((p: any) => {
      const c = String(p?.content || '');
      ctrl += (c.match(CONTROL_CHARS_RE) || []).length;
      emoji += (c.match(/[\uD83C-\uDBFF][\uDC00-\uDFFF]/g) || []).length + (c.match(/[\u2600-\u27BF]/g) || []).length;
      words.forEach((w) => {
        const safe = escapeRegExp(w);
        const hasSpace = /\s/.test(w);
        const re = new RegExp(hasSpace ? safe : `\\b${safe}\\b`, 'gi');
        const count = (c.match(re) || []).length;
        if (count) wordMatches[w] = (wordMatches[w] || 0) + count;
      });
    });
    return { emojiRemoved: emoji, controlCharsRemoved: ctrl, wordMatches, totalPosts: communityPosts.length };
  };

  // Total count of blocked-word matches in current posts (for quick display)
  const blockedWordsTotal = useMemo<number>(() => {
    const data = generateInsights();
    return Object.values(data.wordMatches || {}).reduce((sum, count) => sum + (count as number), 0);
  }, [communityPosts, customBlockedWords]);

  useEffect(() => {
    if (user && userProfile && !hasLoadedData) {
      loadCommunityData();
      loadConnectionStatuses();
      setHasLoadedData(true);
      setIsInitialLoading(false);
    }
  }, [user, userProfile, hasLoadedData]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsInitialLoading(false);
    }, 3000);
    return () => clearTimeout(timeout);
  }, []);

  // Listen for changes in muted relationships (broadcasted by child components)
  useEffect(() => {
    const onChange = () => setMuteRefresh((v) => v + 1);
    window.addEventListener('mutedRelationshipsChanged', onChange);
    return () => window.removeEventListener('mutedRelationshipsChanged', onChange);
  }, []);

  // Listen for connection status changes
  useEffect(() => {
    const onConnectionStatusChange = (event: CustomEvent) => {
      const { fromUserId, toUserId, status } = event.detail;
      if (user?.uid && (fromUserId === user.uid || toUserId === user.uid)) {
        const otherUserId = fromUserId === user.uid ? toUserId : fromUserId;
        setConnectionStatuses(prev => ({
          ...prev,
          [otherUserId]: status
        }));
        // Reload connection statuses to ensure consistency
        loadConnectionStatuses();
      }
    };

    window.addEventListener('connectionStatusChanged', onConnectionStatusChange as EventListener);
    return () => window.removeEventListener('connectionStatusChanged', onConnectionStatusChange as EventListener);
  }, [user?.uid]);

  const loadMutedList = () => {
    try {
      const raw = localStorage.getItem('mutedRelationships');
      const map = raw ? JSON.parse(raw) : {};
      const entries = Object.keys(map).map(uid => ({ userId: uid, until: map[uid] }));
      setMutedList(entries);
    } catch { setMutedList([]); }
  };

  // Realtime updates for community posts
  useEffect(() => {
    const q = query(collection(db, 'communityPosts'), orderBy('timestamp', 'desc')) as any;
    const unsub = onSnapshot(
      q,
      (snap: QuerySnapshot<DocumentData>) => {
        setCommunityPosts(snap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
      },
      (err: FirestoreError) => {
        console.error('Realtime posts failed:', err);
      }
    );
    return () => { try { unsub(); } catch {} };
  }, []);

  // Realtime updates for connection requests
  useEffect(() => {
    if (!user?.uid) return;
    
    const q = query(
      collection(db, 'connectionRequests'),
      where('toUserId', '==', user.uid),
      where('status', '==', 'pending')
    ) as any;
    
    const unsub = onSnapshot(
      q,
      (snap: QuerySnapshot<DocumentData>) => {
        const requests = snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
        setPendingConnects(requests);
      },
      (err: FirestoreError) => {
        console.error('Realtime connection requests failed:', err);
      }
    );
    return () => { try { unsub(); } catch {} };
  }, [user?.uid]);

  // Realtime updates for chat rooms
  useEffect(() => {
    if (!user?.uid) return;
    
    const q = query(
      collection(db, 'chatRooms'),
      where('participants', 'array-contains', user.uid)
    ) as any;
    
    const unsub = onSnapshot(
      q,
      (snap: QuerySnapshot<DocumentData>) => {
        const rooms = snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
        // Sort by last message time
        rooms.sort((a, b) => {
          const aTime = a.lastMessageAt?.toDate?.() || a.lastMessageAt || 0;
          const bTime = b.lastMessageAt?.toDate?.() || b.lastMessageAt || 0;
          return new Date(bTime).getTime() - new Date(aTime).getTime();
        });
        // Keep only top 5 most recent chats for sidebar
        setRecentChats(rooms.slice(0, 5));
      },
      (err: FirestoreError) => {
        console.error('Realtime chat rooms failed:', err);
      }
    );
    return () => { try { unsub(); } catch {} };
  }, [user?.uid]);

  const loadCommunityData = async () => {
    console.log('Loading community data...');
    const email = user?.email || userProfile?.email;
    if (canSeePlatformOnlineStats(email)) {
      const onlineUsersResult = await withErrorHandling(
        () => getDocs(query(collection(db, 'onlineUsers'))),
        'Loading online users'
      );
      if ((onlineUsersResult as any).success) {
        setOnlineUsers((onlineUsersResult as any).data.docs.map((doc: any) => doc.data()));
      }
    } else {
      setOnlineUsers([]);
    }
    const postsResult = await withErrorHandling(
      () => getDocs(query(collection(db, 'communityPosts'), orderBy('timestamp', 'desc'))),
      'Loading community posts'
    );
    if ((postsResult as any).success) {
      setCommunityPosts((postsResult as any).data.docs.map((d: any) => ({ id: d.id, ...d.data() })));
    }
  };


  const showPostToast = (type: 'success' | 'error', message: string) => {
    setPostToast({ type, message });
    setTimeout(() => setPostToast(null), 4500);
  };

  const getPostContentWithSelectedHashtags = () => {
    const baseContent = newPostContent.trim();
    const existingTags = new Set((baseContent.match(/#[\p{L}\p{N}_]+/gu) || []).map((tag) => tag.toLowerCase()));
    const tagsToAdd = selectedHashtags.filter((tag) => !existingTags.has(tag.toLowerCase()));

    if (tagsToAdd.length === 0) return baseContent;
    return `${baseContent}${baseContent ? '\n\n' : ''}${tagsToAdd.join(' ')}`;
  };

  const addAiAssistanceType = (type: string) => {
    setAiAssistanceTypes((prev) => prev.includes(type) ? prev : [...prev, type]);
  };

  const maybeShowAiPastePrompt = (text: string) => {
    if (text.trim().length < 120 || aiPastePromptDismissed) return;

    setShowAiPastePrompt(true);
    setIsPostExpanded(true);
  };

  const handlePostContentChange = (value: string) => {
    const previousValue = previousPostContentRef.current;
    const addedCharacters = value.length - previousValue.length;

    setNewPostContent(value);
    previousPostContentRef.current = value;

    if (addedCharacters >= 120) {
      maybeShowAiPastePrompt(value.slice(Math.max(0, previousValue.length)));
    }
  };

  const handleComposerPaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pastedText = event.clipboardData.getData('text') || '';
    maybeShowAiPastePrompt(pastedText);
  };

  const answerAiPastePrompt = (value: 'no' | 'partial' | 'full') => {
    setAiDisclosure(value);
    setShowAiPastePrompt(false);
    setAiPastePromptDismissed(true);
    if (value !== 'no') {
      addAiAssistanceType(value === 'full' ? 'pasted-ai-draft' : 'pasted-ai-assisted-text');
    }
  };

  const handleCreatePost = async () => {
    if (isSubmittingPost) return;
    console.log('🚀 handleCreatePost called!');
    console.log('📝 Content:', newPostContent);
    console.log('📱 selectedSocialPlatforms:', selectedSocialPlatforms);
    console.log('📎 Attachments:', attachments);
    
    if (!newPostContent.trim() && attachments.length === 0) {
      console.log('❌ Validation failed - no content or attachments');
      return;
    }

    if (aiDisclosure === null) {
      setPulseAiSection(true);
      aiDisclosureRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      setTimeout(() => setPulseAiSection(false), 2200);
      return;
    }

    const postContentWithHashtags = getPostContentWithSelectedHashtags();
    
    setIsSubmittingPost(true);
    try {
      // Calculate VibeScore for the post
      const vibeScoreResult = await calculateVibeScore(postContentWithHashtags);
      const vibeScore = vibeScoreResult.score || 50;
      
      // Check for collaborations (mentions)
      const hasCollaborations = postContentWithHashtags.includes('@') || 
                                (postCollaborations?.userIds?.length > 0);
      
      // Extract mentions for collaboration tracking
      const mentionedUserIds = postCollaborations?.userIds || [];
      
      // Create community post with VibeScore
      console.log('💾 Creating community post...');
      const postRef = await addDoc(collection(db, 'communityPosts'), {
        content: postContentWithHashtags,
        author: userProfile?.displayName || user?.email,
        authorPhoto: getValidProfilePhoto(user, userProfile),
        timestamp: new Date(),
        userId: user?.uid,
        authorTierSnapshot: subscription?.tier || userProfile?.subscription?.tier || 'free',
        attachments,
        pinned: false,
        cta: canCurrentUserEditCTA ? { ...DEFAULT_CONNECTIEFLOW_CTA } : null,
        vibeScore: vibeScore,
        vibeScoreData: vibeScoreResult,
        mentionedUserIds: mentionedUserIds, // Store mentions for collaboration tracking
        selectedHashtags,
        aiDisclosure: aiDisclosure ?? 'no',
        aiAssisted: (aiDisclosure === 'partial' || aiDisclosure === 'full') || aiAssistanceTypes.length > 0,
        aiAssistanceTypes,
        aiDisclosureSource: aiAssistanceTypes.length > 0 ? 'composer-assisted' : 'user-selected',
        aiDisclosureSetAt: new Date(),
      });
      console.log('✅ Community post created!', postRef.id);
      
      // Invalidate AI Visibility cache if post has mentions (collaborations affect score)
      if (mentionedUserIds.length > 0 && user?.uid) {
        try {
          const { invalidateAIVisibilityCache } = await import('../services/aiVisibilityImpactService');
          // Invalidate cache for author and all mentioned users
          await Promise.all([
            invalidateAIVisibilityCache(user.uid),
            ...mentionedUserIds.map(userId => invalidateAIVisibilityCache(userId))
          ]);
        } catch (cacheError) {
          console.warn('Failed to invalidate cache:', cacheError);
        }
      }
      
      // Award quality bonus points (async, don't block post creation)
      try {
        const { calculateQualityBonus } = await import('../lib/points');
        const qualityBonus = calculateQualityBonus(
          vibeScore,
          hasCollaborations,
          false // isTrending wordt later gecheckt
        );
        
        if (qualityBonus > 0) {
          // Update user points with quality bonus
          const userRef = doc(db, 'userProfiles', user.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            const currentPoints = userDoc.data().points || 0;
            await updateDoc(userRef, {
              points: currentPoints + qualityBonus
            });
            console.log(`✅ Quality bonus toegekend: +${qualityBonus} punten`);
          }
        }
      } catch (error) {
        console.error('Error awarding quality bonus:', error);
        // Don't fail post creation if bonus fails
      }

      // Share to selected social media platforms
      if (selectedSocialPlatforms.length > 0) {
        console.log('📤 Sharing to social media platforms:', selectedSocialPlatforms);
        try {
          // Extract image URLs from attachments
          const imageUrls = attachments
            .filter(att => att.type === 'image')
            .map(att => att.url);
          console.log('🖼️ Images to share:', imageUrls);

          // Generate per-platform image variants when an image was uploaded
          const platformImageUrls: Record<string, string> = {};
          if (selectedImageFile && imageUrls.length > 0) {
            try {
              console.log('🎨 Generating platform-specific image variants...');
              const platforms = selectedSocialPlatforms
                .map(p => (p === 'twitter' ? 'x' : p))
                .filter((p): p is PlatformId => ['linkedin', 'x', 'facebook', 'instagram'].includes(p));

              const exportFitModes = platforms.reduce((acc, platform) => {
                const previewFit = platformMediaFitModes[platform] || mobileMediaFitMode;
                acc[platform] = previewFit === 'blur' ? 'blur' : 'crop';
                return acc;
              }, {} as Partial<Record<PlatformId, ImageFitMode>>);
              const aspectOverrides = platforms.reduce((acc, platform) => {
                const override = platformAspectModes[platform];
                if (override) acc[platform] = override;
                return acc;
              }, {} as Record<PlatformId, AspectRatioOption>);
              const cropPositions = platforms.reduce((acc, platform) => {
                const previewFit = platformMediaFitModes[platform] || mobileMediaFitMode;
                if (previewFit === 'cover') {
                  acc[platform] = platformMediaPositions[platform] || { x: 50, y: 50 };
                }
                return acc;
              }, {} as Partial<Record<PlatformId, ImageCropPosition>>);
              const variants = await generateAllVariants(selectedImageFile, platforms, aspectOverrides, undefined, exportFitModes, cropPositions);
              const { ref: storageRef, uploadBytes, getDownloadURL } = await import('firebase/storage');
              const ts = Date.now();
              const uid = user?.uid || 'anon';

              for (const platform of selectedSocialPlatforms) {
                const platformKey = (platform === 'twitter' ? 'x' : platform) as keyof MediaEditorVariants;
                const blobUrl = variants[platformKey];
                if (blobUrl && typeof blobUrl === 'string') {
                  try {
                    const blob = await fetch(blobUrl).then(r => r.blob());
                    const fileRef = storageRef(storage, `newsfeed/${uid}/${platformKey}_${ts}.jpg`);
                    await uploadBytes(fileRef, blob);
                    platformImageUrls[platform] = await getDownloadURL(fileRef);
                    console.log(`✅ ${platform} variant uploaded`);
                  } catch {
                    platformImageUrls[platform] = imageUrls[0];
                  }
                }
              }
              cleanupUrls([variants.feed, variants.linkedin, variants.x, variants.facebook, variants.instagram]);
              console.log('📸 Platform image URLs ready:', platformImageUrls);
            } catch (variantError) {
              console.warn('⚠️ Variant generation failed, using raw image URLs:', variantError);
            }
          }

          const { centralSharingService } = await import('../services/centralSharingService');
          // AI-disclosure metadata stays internal to SuperKonnected and is never appended to external platform shares.
          const results = await centralSharingService.shareToAllConnected(postContentWithHashtags, {
            platforms: selectedSocialPlatforms as any,
            addBranding: true,
            source: 'community',
            imageUrls: imageUrls,
            platformImageUrls: Object.keys(platformImageUrls).length > 0 ? platformImageUrls : undefined
            // linkedInOrganizationId: null - Organization posting disabled
          });
          console.log('✅ Social sharing results:', results);
          console.log('📊 Results details:');
          results.forEach((result, index) => {
            console.log(`  [${index}] Platform: ${result.platform}`);
            console.log(`      Success: ${result.success}`);
            if (result.success && result.shareUrl) {
              console.log(`      Share URL: ${result.shareUrl}`);
            }
            if (!result.success && result.error) {
              console.log(`      Error: ${result.error}`);
            }
          });
          
          // Update post with shared platforms info
          const successfulPlatforms = results.filter(r => r.success).map(r => r.platform);
          const failedPlatforms = results.filter(r => !r.success);
          
          console.log(`✅ Successful: ${successfulPlatforms.length}`);
          console.log(`❌ Failed: ${failedPlatforms.length}`);
          
          if (successfulPlatforms.length > 0) {
            await updateDoc(doc(db, 'communityPosts', postRef.id), {
              sharedTo: successfulPlatforms,
              sharedAt: new Date()
            });
            console.log('✅ Post updated with shared platforms:', successfulPlatforms);
          }
          
          // Show success message
          const successfulShares = results.filter(r => r.success);
          if (successfulShares.length > 0) {
            showPostToast('success', `Post geplaatst en gedeeld naar: ${successfulShares.map(r => r.platform).join(', ')}`);
          }
          
          // Show error message if any failed
          if (failedPlatforms.length > 0) {
            console.error('❌ Failed shares:', failedPlatforms);
            const errorMsg = failedPlatforms.map(f => f.platform).join(', ');
            showPostToast('error', `Post geplaatst, maar delen mislukt voor: ${errorMsg}`);
          }
        } catch (socialError) {
          console.error('❌ Social sharing failed:', socialError);
          // Don't block the main post if social sharing fails
        }
      } else {
        console.log('ℹ️ No social platforms selected - skipping social sharing');
        showPostToast('success', 'Post succesvol geplaatst!');
      }

      setNewPostContent('');
      previousPostContentRef.current = '';
      setAiDisclosure(null);
      setAiAssistanceTypes([]);
      setShowAiPastePrompt(false);
      setAiPastePromptDismissed(false);
      setIsPostExpanded(false);
      setAttachments([]);
      setSelectedImageFile(null);
      setPostCollaborations({ userIds: [], names: [] });
      setSelectedHashtags([]);
      console.log('🔄 Reloading community data...');
      await loadCommunityData();
      console.log('✅ Community data reloaded');
    } catch (error) {
      console.error('Error creating post:', error);
      showPostToast('error', 'Er is een fout opgetreden bij het maken van de post. Probeer opnieuw.');
    } finally {
      setIsSubmittingPost(false);
    }
  };

  const togglePin = async (post: any) => {
    try {
      if (!post?.id) return;
      const newPinned = !post.pinned;
      await updateDoc(doc(db, 'communityPosts', post.id), {
        pinned: newPinned,
        pinnedBy: newPinned ? (user?.uid || null) : null,
        pinnedAt: newPinned ? serverTimestamp() : null
      });
      setCommunityPosts(prev => prev.map(p => p.id === post.id ? { ...p, pinned: newPinned, pinnedBy: newPinned ? (user?.uid || null) : null, pinnedAt: newPinned ? new Date() as any : null } : p));
    } catch (e) {
      console.error('Pin toggle failed:', e);
      alert('Pinnen mislukt. Probeer opnieuw.');
    }
  };

  const sharePostToMyFeed = async (post: any) => {
    try {
      if (!user?.uid) return;
      await addDoc(collection(db, 'communityPosts'), {
        content: post?.content || '',
        attachments: Array.isArray(post?.attachments) ? post.attachments : [],
        author: userProfile?.displayName || user?.email,
        authorPhoto: getValidProfilePhoto(user, userProfile),
        timestamp: new Date(),
        userId: user.uid,
        authorTierSnapshot: subscription?.tier || userProfile?.subscription?.tier || 'free',
        cta: canCurrentUserEditCTA ? (post?.cta || { ...DEFAULT_CONNECTIEFLOW_CTA }) : null,
        sharedFromId: post?.id || null,
        sharedFromAuthor: post?.author || null,
        pinned: false
      });
      loadCommunityData();
    } catch (e) {
      console.error('Share failed:', e);
      alert('Delen mislukt. Probeer opnieuw.');
    }
  };

  const deleteOwnPost = async (post: any) => {
    try {
      if (!post?.id || !user?.uid || post.userId !== user.uid) return;
      await (await import('firebase/firestore')).deleteDoc(doc(db, 'communityPosts', post.id));
      setCommunityPosts(prev => prev.filter(p => p.id !== post.id));
      } catch (e) {
      console.error('Delete failed:', e);
      alert('Verwijderen mislukt. Probeer opnieuw.');
    }
  };

  // Force delete function for admin/troubleshooting
  const forceDeletePost = async (post: any) => {
    try {
      if (!post?.id) return;
      await (await import('firebase/firestore')).deleteDoc(doc(db, 'communityPosts', post.id));
      setCommunityPosts(prev => prev.filter(p => p.id !== post.id));
      console.log('Post deleted:', post.id);
    } catch (e) {
      console.error('Force delete failed:', e);
      alert('Force verwijderen mislukt. Probeer opnieuw.');
    }
  };

  // Admin delete function
  const adminDeletePost = async (post: any) => {
    try {
      if (!post?.id || !isAdmin) return;
      const confirmDelete = window.confirm(`Admin: Post verwijderen van ${post.author}?\n\nContent: "${post.content?.substring(0, 100)}..."`);
      if (!confirmDelete) return;
      
      await (await import('firebase/firestore')).deleteDoc(doc(db, 'communityPosts', post.id));
      setCommunityPosts(prev => prev.filter(p => p.id !== post.id));
      console.log('Admin deleted post:', post.id, 'by', post.author);
    } catch (e) {
      console.error('Admin delete failed:', e);
      alert('Admin verwijderen mislukt. Probeer opnieuw.');
    }
  };

  // Get connection status for a user
  const getConnectionStatus = (userId: string): 'none' | 'pending' | 'connected' => {
    return connectionStatuses[userId] || 'none';
  };

  const isPremiumLikeTier = (tier?: string): boolean => {
    const normalized = String(tier || '').toLowerCase();
    return normalized === 'premium' || normalized === 'trial' || normalized === 'lifetime';
  };

  const canCurrentUserEditCTA = useMemo(() => {
    return isPremiumLikeTier(subscription?.tier) || isPremiumLikeTier(userProfile?.subscription?.tier);
  }, [subscription?.tier, userProfile?.subscription?.tier]);

  const getPostCta = (post: any): FeedPostCTA | null => {
    if (!FEATURE_POST_CTA) return null;
    const authoredCta = post?.cta as FeedPostCTA | undefined;
    const authorTierSnapshot = String(post?.authorTierSnapshot || post?.authorSubscriptionTier || '');
    const authorIsPremiumLike = isPremiumLikeTier(authorTierSnapshot) || Boolean(authoredCta?.source === 'author');
    if (!authorIsPremiumLike) return null;

    const resolvedCta: FeedPostCTA = authoredCta?.enabled === false
      ? { ...authoredCta, enabled: false }
      : {
          ...DEFAULT_CONNECTIEFLOW_CTA,
          ...(authoredCta || {}),
          enabled: authoredCta?.enabled ?? true,
        };

    if (!resolvedCta.enabled) return null;

    if (resolvedCta.visibilityScope === 'region' && Array.isArray(resolvedCta.visibilityRegions) && resolvedCta.visibilityRegions.length > 0) {
      const viewerRegions = Array.isArray(userProfile?.selectedRegions) ? userProfile.selectedRegions : [];
      const hasRegionMatch = resolvedCta.visibilityRegions.some((region) => viewerRegions.includes(region));
      if (!hasRegionMatch) return null;
    }

    const postAuthorId = String(post?.userId || post?.authorId || '');
    const connectionStatus = postAuthorId ? getConnectionStatus(postAuthorId) : 'none';
    if (resolvedCta.type === 'connect' && (postAuthorId === user?.uid || connectionStatus === 'connected' || connectionStatus === 'pending')) {
      return null;
    }

    return resolvedCta;
  };

  const handlePostCtaClick = (post: any, cta: FeedPostCTA) => {
    const targetUserId = String(post?.userId || post?.authorId || '');
    if (cta.type === 'connect' || cta.type === 'connectieflow') {
      openConnectieFlow(targetUserId || undefined);
      return;
    }
    if (cta.href) {
      if (cta.target === '_blank') {
        window.open(cta.href, '_blank', 'noopener,noreferrer');
      } else if (/^https?:\/\//i.test(cta.href)) {
        window.location.href = cta.href;
      } else {
        window.location.hash = cta.href.startsWith('#') ? cta.href : `#${cta.href}`;
      }
      return;
    }
    openConnectieFlow(targetUserId || undefined);
  };

  const openCtaEditor = (post: any) => {
    const existing = getPostCta(post);
    setCtaEditorPost(post);
    setCtaDraft({
      ...DEFAULT_CONNECTIEFLOW_CTA,
      ...(existing || {}),
      source: 'author',
      enabled: true,
    });
  };

  const savePostCta = async () => {
    if (!ctaEditorPost?.id) return;
    const cleanedCta: FeedPostCTA = {
      ...ctaDraft,
      label: (ctaDraft.label || '').trim() || DEFAULT_CONNECTIEFLOW_CTA.label,
      href: ctaDraft.href?.trim() || undefined,
      source: 'author',
      updatedAt: new Date(),
      updatedBy: user?.uid,
      enabled: true,
    };
    await updateDoc(doc(db, 'communityPosts', ctaEditorPost.id), {
      cta: cleanedCta,
      ctaUpdatedAt: serverTimestamp(),
      ctaUpdatedBy: user?.uid || null,
    });
    setCtaEditorPost(null);
  };

  const closeScreenLock = (outcome: 'continue' | 'scroll' | 'dismiss_after_timer') => {
    if (user?.uid) {
      void feedToolAnalyticsService.logScreenLockEvent({
        userId: user.uid,
        outcome,
        device: effectiveMobileTab === undefined ? 'desktop' : 'mobile',
        relevanceScore: getScreenLockRelevanceScore(activeScreenLockPost),
        xItemsAtTrigger: 0,
      });
    }
    registerScreenLockOutcome(outcome);
    setActiveScreenLockPost(null);
    setScreenLockReady(false);
    setScreenLockCountdown(SCREEN_LOCK_DURATION_SECONDS);
  };

  const handleScreenLockContinue = () => {
    if (!activeScreenLockPost) {
      closeScreenLock('continue');
      return;
    }
    const cta = getPostCta(activeScreenLockPost);
    if (cta) {
      handlePostCtaClick(activeScreenLockPost, cta);
    } else {
      openConnectieFlow(activeScreenLockPost?.userId || activeScreenLockPost?.authorId || undefined);
    }
    closeScreenLock('continue');
  };

  // Load connection statuses
  const loadConnectionStatuses = async () => {
    if (!user?.uid) return;

    try {
      // Load existing connections
      const connectionsQuery = query(
        collection(db, 'connections'),
        where('user1Id', '==', user.uid)
      );
      const connectionsQuery2 = query(
        collection(db, 'connections'),
        where('user2Id', '==', user.uid)
      );

      const [connectionsSnapshot, connectionsSnapshot2] = await Promise.all([
        getDocs(connectionsQuery),
        getDocs(connectionsQuery2)
      ]);

      const allConnections = [...connectionsSnapshot.docs, ...connectionsSnapshot2.docs];
      const connectedUsers: Record<string, 'connected'> = {};

      allConnections.forEach(doc => {
        const data = doc.data();
        const otherUserId = data.user1Id === user.uid ? data.user2Id : data.user1Id;
        connectedUsers[otherUserId] = 'connected';
      });

      // Load pending requests
      const pendingQuery = query(
        collection(db, 'connectionRequests'),
        where('fromUserId', '==', user.uid),
        where('status', '==', 'pending')
      );

      const pendingSnapshot = await getDocs(pendingQuery);
      const pendingUsers: Record<string, 'pending'> = {};

      pendingSnapshot.docs.forEach(doc => {
        const data = doc.data();
        pendingUsers[data.toUserId] = 'pending';
      });

      // Combine statuses (connected takes priority over pending)
      const allStatuses = { ...pendingUsers, ...connectedUsers };
      setConnectionStatuses(allStatuses);

    } catch (error) {
      console.error('Error loading connection statuses:', error);
    }
  };

  // Send connection request function
  const sendConnectionRequest = async (targetUserId: string, targetUserName: string, postId?: string) => {
    if (!user?.uid || !userProfile) return;


    try {
      // Check if users are already connected
      const existingConnectionQuery1 = query(
        collection(db, 'connections'),
        where('user1Id', '==', user.uid),
        where('user2Id', '==', targetUserId)
      );
      const existingConnectionQuery2 = query(
        collection(db, 'connections'),
        where('user1Id', '==', targetUserId),
        where('user2Id', '==', user.uid)
      );
      
      const [connectionSnapshot1, connectionSnapshot2] = await Promise.all([
        getDocs(existingConnectionQuery1),
        getDocs(existingConnectionQuery2)
      ]);
      
      if (connectionSnapshot1.size > 0 || connectionSnapshot2.size > 0) {
        alert('Je bent al verbonden met deze gebruiker.');
        return;
      }

      // Check if connection request already exists
      const existingRequestQuery = query(
        collection(db, 'connectionRequests'),
        where('fromUserId', '==', user.uid),
        where('toUserId', '==', targetUserId),
        where('status', '==', 'pending')
      );
      
      const existingRequestSnapshot = await getDocs(existingRequestQuery);
      if (existingRequestSnapshot.size > 0) {
        alert('Je hebt al een connectieverzoek gestuurd naar deze gebruiker.');
        return;
      }

      // Get target user's photo
      const targetUserQuery = query(
        collection(db, 'userProfiles'),
        where('uid', '==', targetUserId)
      );
      const targetUserSnapshot = await getDocs(targetUserQuery);
      const targetUserPhoto = targetUserSnapshot.docs[0]?.data()?.photoURL || '';

      // Create connection request
      const requestData = {
        fromUserId: user.uid,
        toUserId: targetUserId,
        fromUserName: userProfile.displayName || user.email || 'Onbekend',
        toUserName: targetUserName,
        fromUserPhoto: user.photoURL,
        toUserPhoto: targetUserPhoto,
        // Also add receiverPhoto for compatibility
        receiverPhoto: targetUserPhoto,
        receiverName: targetUserName,
        senderPhoto: user.photoURL,
        senderName: userProfile.displayName || user.email || 'Onbekend',
        message: `Hallo ${targetUserName}, ik zou graag met je willen verbinden!`,
        status: 'pending',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await addDoc(collection(db, 'connectionRequests'), requestData);
      
      // ✅ Track connection from post (offspring tracking)
      if (postId) {
        try {
          const { trackConnectionFromPost } = await import('../services/postEngagementService');
          await trackConnectionFromPost(user.uid, targetUserId, postId);
        } catch (trackError) {
          console.warn('Failed to track connection from post:', trackError);
          // Don't block connection request if tracking fails
        }
      }
      
      // Update local status
      setConnectionStatuses(prev => ({
        ...prev,
        [targetUserId]: 'pending'
      }));
      
      // Reload connection statuses to ensure consistency
      await loadConnectionStatuses();
      
      // Navigate to Chatflow with pending request
      window.location.hash = '#connecties';
      localStorage.setItem('showPendingRequests', 'true');
      
      alert(`Connectieverzoek gestuurd naar ${targetUserName}! Ga naar Connecties om de status te bekijken.`);
      
    } catch (error) {
      console.error('Error sending connection request:', error);
      alert('Er is een fout opgetreden bij het versturen van het connectieverzoek.');
    }
  };

  // Function to debug connection requests
  const debugConnectionRequests = async () => {
    try {
      console.log('🔍 Debugging connection requests...');
      
      // Get all connection requests
      const requestsSnapshot = await getDocs(collection(db, 'connectionRequests'));
      console.log(`Total connection requests in database: ${requestsSnapshot.size}`);
      
      // Check for requests to current user
      const requestsToUser = requestsSnapshot.docs.filter(doc => {
        const data = doc.data();
        return data.toUserId === user?.uid || data.receiverId === user?.uid;
      });
      console.log(`Requests to current user (${user?.uid}): ${requestsToUser.length}`);
      
      // Check for requests from current user
      const requestsFromUser = requestsSnapshot.docs.filter(doc => {
        const data = doc.data();
        return data.fromUserId === user?.uid || data.senderId === user?.uid;
      });
      console.log(`Requests from current user: ${requestsFromUser.length}`);
      
      // Log all requests for debugging
      requestsSnapshot.docs.forEach((doc, index) => {
        const data = doc.data();
        console.log(`Request ${index + 1}:`, {
          id: doc.id,
          fromUserId: data.fromUserId,
          senderId: data.senderId,
          toUserId: data.toUserId,
          receiverId: data.receiverId,
          status: data.status,
          createdAt: data.createdAt
        });
      });
      
      alert(`Debug complete! Check console for details. Found ${requestsToUser.length} requests to you.`);
      
    } catch (error) {
      console.error('Error debugging connection requests:', error);
      alert('Error debugging connection requests. Check console.');
    }
  };

  // Function to create a test connection request
  const createTestConnectionRequest = async () => {
    if (!user?.uid || !userProfile) {
      alert('Geen gebruiker ingelogd');
      return;
    }

    try {
      // Create a test request from "Test User" to current user
      const testRequestData = {
        fromUserId: 'test-user-123',
        toUserId: user.uid,
        fromUserName: 'Test User',
        toUserName: userProfile.displayName || 'Current User',
        fromUserPhoto: 'https://ui-avatars.com/api/?name=Test+User&background=4F6660&color=fff',
        toUserPhoto: user.photoURL,
        // Also add receiverPhoto for compatibility
        receiverPhoto: user.photoURL,
        receiverName: userProfile.displayName || 'Current User',
        senderPhoto: 'https://ui-avatars.com/api/?name=Test+User&background=4F6660&color=fff',
        senderName: 'Test User',
        message: 'Dit is een test connectieverzoek',
        status: 'pending',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await addDoc(collection(db, 'connectionRequests'), testRequestData);
      console.log('✅ Test connection request created');
      alert('Test connectieverzoek aangemaakt! Check of het verschijnt in de lijst.');
      
    } catch (error) {
      console.error('Error creating test connection request:', error);
      alert('Error creating test connection request. Check console.');
    }
  };

  // Function to fix existing connection requests with missing photo fields
  const fixConnectionRequestPhotos = async () => {
    try {
      console.log('🔧 Fixing connection request photos...');
      
      // Get all connection requests
      const requestsSnapshot = await getDocs(collection(db, 'connectionRequests'));
      let fixedCount = 0;
      
      for (const docSnapshot of requestsSnapshot.docs) {
        const data = docSnapshot.data();
        const needsUpdate = !data.receiverPhoto || !data.senderPhoto;
        
        if (needsUpdate) {
          const updateData: any = {};
          
          // Fix receiverPhoto if missing
          if (!data.receiverPhoto && data.toUserPhoto) {
            updateData.receiverPhoto = data.toUserPhoto;
            updateData.receiverName = data.toUserName;
          }
          
          // Fix senderPhoto if missing
          if (!data.senderPhoto && data.fromUserPhoto) {
            updateData.senderPhoto = data.fromUserPhoto;
            updateData.senderName = data.fromUserName;
          }
          
          if (Object.keys(updateData).length > 0) {
            await updateDoc(doc(db, 'connectionRequests', docSnapshot.id), updateData);
            fixedCount++;
            console.log(`✅ Fixed connection request ${docSnapshot.id}`);
          }
        }
      }
      
      console.log(`🎉 Fixed ${fixedCount} connection requests`);
      alert(`Connection request photos gefixed! ${fixedCount} requests bijgewerkt.`);
      
      // Reload data
      loadConnectionStatuses();
      
    } catch (error) {
      console.error('Error fixing connection request photos:', error);
      alert('Er is een fout opgetreden bij het fixen van de connection request photos.');
    }
  };

  // Global function for console access
  (window as any).deleteTestPosts = async () => {
    const testPosts = communityPosts.filter((post: any) => 
      post.content?.includes('test🎥') || 
      post.content?.includes('video post') || 
      post.content?.includes('post') && post.content.length < 10
    );
    
    console.log('Found test posts:', testPosts);
    
    for (const post of testPosts) {
      try {
        await (await import('firebase/firestore')).deleteDoc(doc(db, 'communityPosts', post.id));
        console.log('Deleted post:', post.id, post.content);
      } catch (e) {
        console.error('Failed to delete post:', post.id, e);
      }
    }
    
    // Reload data
    loadCommunityData();
    alert(`Verwijderd ${testPosts.length} test posts`);
  };

  // Simple function to delete old community posts (before September 10, 2025)
  (window as any).deleteOldCommunityPosts = async () => {
    const cutoffDate = new Date('2025-09-10');
    const snapshot = await getDocs(collection(db, 'communityPosts'));
    let deleted = 0;
    
    for (const docSnapshot of snapshot.docs) {
      const postDate = docSnapshot.data().timestamp?.toDate();
      if (postDate && postDate < cutoffDate) {
        await deleteDoc(doc(db, 'communityPosts', docSnapshot.id));
        deleted++;
      }
    }
    
    console.log(`🗑️ Verwijderd ${deleted} oude community posts`);
    alert(`🗑️ Verwijderd ${deleted} oude community posts`);
    loadCommunityData();
  };

  const updateOwnPostContent = async (postId: string, content: string) => {
    try {
      if (!postId || !user?.uid) return;
      await updateDoc(doc(db, 'communityPosts', postId), { content });
      setCommunityPosts(prev => prev.map(p => p.id === postId ? { ...p, content } : p));
      setEditingPostId(null);
      setEditContent('');
    } catch (e) {
      console.error('Update content failed:', e);
      alert('Bijwerken mislukt. Probeer opnieuw.');
    }
  };

  const deleteMyPostsOlderThan = async (days: number) => {
    try {
      if (!user?.uid || !Array.isArray(communityPosts)) return;
      const cutoff = Date.now() - Math.max(1, days) * 24 * 60 * 60 * 1000;
      const oldPosts = communityPosts.filter((p: any) => p.userId === user.uid).filter((p: any) => {
        const t = p?.timestamp?.toDate?.() || p?.timestamp;
        const ts = t ? new Date(t).getTime() : 0;
        return ts > 0 && ts < cutoff;
      });
      if (oldPosts.length === 0) {
        alert(`Geen posts ouder dan ${days} dagen gevonden.`);
        return;
      }
      const ok = window.confirm(`Weet je zeker dat je ${oldPosts.length} post(s) ouder dan ${days} dagen wilt verwijderen?`);
      if (!ok) return;
      const { deleteDoc } = await import('firebase/firestore');
      await Promise.all(oldPosts.map((p: any) => deleteDoc(doc(db, 'communityPosts', p.id))));
      setCommunityPosts(prev => prev.filter(p => !oldPosts.some(op => op.id === (p as any).id)));
    } catch (e) {
      console.error('Bulk delete failed:', e);
      alert('Bulk verwijderen mislukt. Probeer opnieuw.');
    }
  };

  // Media upload
  const uploadSelectedFile = async (file: File, kind: 'image' | 'video' | 'file') => {
    const { ref, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
    const localPreviewUrl = URL.createObjectURL(file);
    try {
      setUploading(true);
      setUploadProgress(0);
      setPendingUploadPreview({ type: kind, url: localPreviewUrl, name: file.name });
      const uid = user?.uid || 'anon';
      // Use storage path allowed by storage.rules: newsfeed/{uid}/{file}
      const path = `newsfeed/${uid}/${Date.now()}-${kind}-${file.name}`;
      const storageRef = ref(storage, path);
      const task = uploadBytesResumable(storageRef, file);
      await new Promise<void>((resolve, reject) => {
        task.on('state_changed', (snap) => {
          const p = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
          setUploadProgress(p);
        }, reject, () => resolve());
      });
      const url = await getDownloadURL(task.snapshot.ref);
      setAttachments(prev => [...prev, { type: kind, url, name: file.name }]);
      // Force UI to show attachments area
      setIsPostExpanded(true);
      console.log('✅ Upload gelukt', { kind, url });
    } catch (e) {
      console.error('Upload failed:', e);
      alert('Upload mislukt. Probeer opnieuw.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setPendingUploadPreview(null);
      URL.revokeObjectURL(localPreviewUrl);
    }
  };

  // Track viewport to ensure mobile tab logic only applies on small screens.
  // Must be declared before any early returns to satisfy Rules of Hooks.
  const [isMobileViewport, setIsMobileViewport] = useState(() => isPhoneViewport());
  useEffect(() => {
    const onResize = () => setIsMobileViewport(isPhoneViewport());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // On mobile, mobileTabIndex controls which section to show:
  // 0 = Newsfeed: hide MultiHeader, show post composer + feeds
  // 1-4 = MultiHeader tabs: show MultiHeader with correct tab, hide post composer + feeds
  // undefined = desktop: show everything
  const effectiveMobileTab = isMobileViewport ? mobileTabIndex : undefined;
  const showFeedContent = effectiveMobileTab === undefined || effectiveMobileTab === 0;

  useEffect(() => {
    const onScroll = () => {
      const nearBottom = window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 120;
      setShowScrollTop(nearBottom && window.scrollY > 200);
      const scrollBucket = Math.floor(window.scrollY / 650);
      if (scrollBucket > desktopLockBucketRef.current && showFeedContent && sortedDisplayPosts.length > 0) {
        desktopLockBucketRef.current = scrollBucket;
        registerItemView(getScreenLockRelevanceScore(sortedDisplayPosts[scrollBucket % sortedDisplayPosts.length]));
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [getScreenLockRelevanceScore, registerItemView, showFeedContent, sortedDisplayPosts]);

  useEffect(() => {
    if (!screenLockEnabled || !shouldShowLock || sortedDisplayPosts.length === 0 || !showFeedContent) return;
    const pick = sortedDisplayPosts[Math.floor(Math.random() * Math.min(6, sortedDisplayPosts.length))];
    setActiveScreenLockPost(pick);
    setScreenLockCountdown(SCREEN_LOCK_DURATION_SECONDS);
    setScreenLockReady(false);
    markScreenLockShown();
  }, [markScreenLockShown, screenLockEnabled, shouldShowLock, showFeedContent, sortedDisplayPosts]);

  useEffect(() => {
    if (!activeScreenLockPost) return;
    if (screenLockCountdown <= 0) {
      setScreenLockReady(true);
      return;
    }
    const timer = window.setTimeout(() => {
      setScreenLockCountdown((prev) => prev - 1);
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [activeScreenLockPost, screenLockCountdown]);

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p>Dashboard laden...</p>
            </div>
            </div>
    );
  }

  return (
    <>
    {/* Keyframe for scroll-to-top pulse */}
    <style>{`
      @keyframes sagePulse {
        0%, 100% {
          background: rgba(255,255,255,0.90);
          box-shadow: 0 1px 4px rgba(79,102,96,0.12);
        }
        50% {
          background: rgba(79,102,96,0.18);
          box-shadow: 0 0 0 5px rgba(79,102,96,0.07), 0 1px 4px rgba(79,102,96,0.12);
        }
      }
    `}</style>
    {/* Skip to content for keyboard users */}
    <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 bg-white text-gray-900 border rounded px-3 py-2 shadow">Ga naar hoofdinhoud</a>
    <div id="main-content" role="main" aria-label="The movement dashboard" className={`quiet-lounge max-w-full min-w-0 overflow-x-hidden md:min-h-screen md:p-6 ${effectiveMobileTab !== undefined ? 'h-full flex flex-col' : 'p-6'}`} style={{ ...containerStyle, backgroundColor: effectiveMobileTab !== undefined && effectiveMobileTab !== 0 ? '#FFFFFF' : '#F4F4F1' }}>
      <div className={`grid grid-cols-12 mx-auto min-w-0 ${effectiveMobileTab !== undefined ? 'flex-1 min-h-0 max-w-full' : 'gap-8 max-w-5xl'}`}>
        {/* Left Sidebar - Profile + Settings — desktop only, hidden on mobile (bottom nav handles navigation) */}
        <div className="hidden lg:block lg:col-span-3">
          <div className="lg:sticky lg:top-20">
            {/* Sidebar Content */}
            <div className="flex flex-col gap-6">
              {/* Admin Mute Panel */}
              <AdminMutePanel page="movement" />
              {/* Profile Card */}
              <div className="bg-white rounded-lg p-4 order-0" style={{ border: '1px solid #F2F0EB', boxShadow: '0 1px 3px rgba(0,0,0,0.015)' }}>
                {/* Hidden file input for profile photo */}
                <input
                  ref={profilePhotoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleProfilePhotoUpload(file);
                    e.target.value = '';
                  }}
                />
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => profilePhotoInputRef.current?.click()}
                      disabled={uploadingProfilePhoto}
                      title="Profielfoto wijzigen"
                      className="relative group focus:outline-none flex-shrink-0 w-12 h-12 rounded-full"
                    >
                      <img 
                        src={getValidProfilePhoto(user, userProfile)}
                        alt="Profile" 
                        className="w-12 h-12 rounded-full object-cover"
                        style={{
                          objectPosition: userProfile?.photoPosition 
                            ? `${userProfile.photoPosition.x}% ${userProfile.photoPosition.y}%` 
                            : 'center'
                        }}
                        onError={(e) => {
                          const target = e.currentTarget as HTMLImageElement;
                          target.src = 'https://ui-avatars.com/api/?name=U&background=cccccc&color=fff&size=128';
                        }}
                      />
                      <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        {uploadingProfilePhoto ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <span className="text-white text-xs">📷</span>
                        )}
                      </div>
                    </button>
                    <div>
                      <h3 className="font-semibold">{userProfile?.displayName || 'Gebruiker'}</h3>
                      <p className="text-sm text-gray-500">Ondernemer</p>
                    </div>
                  </div>
                  {/* Sidebar Info Button */}
                  <div className="relative" ref={sidebarInfoRef}>
                    <button
                      data-info-spot="sidebar"
                      onClick={() => setShowSidebarInfo(!showSidebarInfo)}
                      className="homepage-info-button w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center transition-colors"
                      style={{
                        backgroundColor: showSidebarInfo ? '#8DA8A0' : 'rgba(147,174,163,0.12)',
                        color: showSidebarInfo ? '#FFFFFF' : '#7A9488'
                      }}
                      title="Informatie over sidebar onderdelen"
                    >
                      i
                    </button>
                    {showSidebarInfo && (
                      <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
                        <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
                          <span className="text-sm font-semibold text-[#0A2342]">Sidebar uitleg</span>
                          <button
                            onClick={() => setShowSidebarInfo(false)}
                            className="text-gray-400 hover:text-gray-600 text-lg leading-none"
                          >
                            ×
                          </button>
                        </div>
                        <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
                          <SidebarInfoItem
                            title="Feeds"
                            items={[
                              "Bekijk statistieken van je posts en engagement",
                              "Schakel verschillende feeds in/uit naar wens",
                              "Volg of ontvolg specifieke auteurs in de feed"
                            ]}
                          />
                          <SidebarInfoItem
                            title="Display Instellingen"
                            items={[
                              "Focus Mode: vermindert visuele afleiding",
                              "Energy Mode: activeer een energiekere kleurstijl",
                              "Blauw Licht Filter: bescherm je ogen bij avondgebruik",
                              "Dag/Nacht: schakel tussen lichte en donkere modus"
                            ]}
                          />
                          <SidebarInfoItem
                            title="Clean Mind Filter"
                            items={[
                              "Filter content types (video's, afbeeldingen, tekst)",
                              "Verwijder emoji's voor een cleaner look",
                              "Profanity Filter: blokkeer ongepast taalgebruik"
                            ]}
                          />
                          <SidebarInfoItem
                            title="Netwerkclub Finder"
                            items={[
                              "Zoek netwerkclubs op basis van regio, provincie of land",
                              "Bekijk club profielen en activiteiten",
                              "Connect direct met clubs in jouw buurt"
                            ]}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  <p>
                    {userProfile?.posts || 0} posts
                    {canSeePlatformOnlineStats(user?.email || userProfile?.email)
                      ? ` • ${onlineUsers.length} online`
                      : ''}
                    {' '}• {communityUserIds.length} connecties
                  </p>
                </div>
              </div>

              {/* Feeds Card */}
              <div className={`bg-white rounded-lg ${hasCommunityAccess ? 'order-2' : 'order-1'}`} style={{ border: '1px solid #F2F0EB', boxShadow: '0 1px 3px rgba(0,0,0,0.015)' }}> 
                <div className="p-4 cursor-pointer flex items-center justify-between" style={{ borderBottom: '1px solid #F4F2ED' }} onClick={() => setCommunityOpen(prev => !prev)}>
                  <div className="flex items-center space-x-2">
                    <Users size={18} strokeWidth={1.5} className="text-gray-500" />
                    <div>
                      <h3 className="font-medium">Feeds</h3>
                      <p className="text-sm text-gray-500">Snelle toegang en statistieken</p>
                    </div>
                  </div>
                  <span className="text-gray-500">{communityOpen ? '▾' : '▸'}</span>
                </div>
                {communityOpen && (
                  <div className="p-4 space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-gray-50 border rounded-lg p-3">
                        <div className="text-gray-600">Posts totaal</div>
                        <div className="text-lg font-semibold">{communityPosts.length}</div>
                      </div>
                      <div className="bg-gray-50 border rounded-lg p-3">
                        <div className="text-gray-600">Gepind</div>
                        <div className="text-lg font-semibold">{pinnedPosts.length}</div>
                      </div>
                      <div className="bg-gray-50 border rounded-lg p-3 col-span-2">
                        <div className="text-gray-600">Vandaag in movement feed</div>
                        <div className="text-lg font-semibold">{postsTodayCount}</div>
                      </div>
                    </div>
                    <button onClick={() => { setConnectionsOpen(true); document.getElementById('connections-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 w-full text-left">Beheer connecties</button>
                    {/* Volg/ontvolg auteurs */}
                    {communityAuthors.length > 0 && (
                      <div className="pt-2 border-t">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-gray-600">Volgen in movement feed</div>
                          <button onClick={() => setShowAllAuthors(s => !s)} className="text-xs text-blue-600 hover:underline">{showAllAuthors ? 'Minder' : 'Meer'}</button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {(showAllAuthors ? communityAuthors : communityAuthors.slice(0, 6)).map(a => {
                            const excluded = communityFollowExcluded.includes(a.userId);
                            return (
                              <button
                                key={a.userId}
                                onClick={() => setCommunityFollowExcluded(prev => excluded ? prev.filter(id => id !== a.userId) : Array.from(new Set([...prev, a.userId])))}
                                className={`px-2 py-1 rounded-full border text-xs ${excluded ? 'bg-gray-100 text-gray-600' : 'bg-blue-50 text-blue-700'}`}
                                title={excluded ? 'Klik om te volgen' : 'Klik om te ontvolgen'}
                              >
                                {excluded ? 'Volg' : 'Ontvolg'} • {a.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {/* Toon feeds toggles */}
                    <div className="pt-2 border-t">
                      <div className="text-gray-600 mb-2">Toon feeds</div>
                      <div className="flex flex-col gap-2">
                        <label className="flex items-center justify-between cursor-pointer">
                          <span className={feedsVisibility.news ? 'text-gray-800' : 'text-gray-400'}>Nieuwsfeed</span>
                          <input type="checkbox" className="sr-only peer" checked={feedsVisibility.news} onChange={() => toggleFeed('news')} />
                          <span className="w-11 h-6 bg-gray-300 rounded-full peer-checked:bg-[#7A9488] relative after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full flex-shrink-0"></span>
                        </label>
                        <label className="flex items-center justify-between cursor-pointer" title={hasCommunityAccess ? '' : 'Alleen voor Community‑abonnement'}>
                          <div className="flex items-center gap-2">
                            <span className={feedsVisibility.community ? 'text-gray-800' : 'text-gray-400'}>Movement feed</span>
                            {!hasCommunityAccess && (
                              <span className="text-[11px] leading-none px-2 py-0.5 rounded-full text-white bg-gradient-to-r from-[#7A9488] via-[#9AAF88] to-[#C5D3B8]">Upgrade</span>
                            )}
                          </div>
                          <input type="checkbox" className="sr-only peer" checked={feedsVisibility.community} onChange={() => toggleFeed('community')} aria-label="Toggle Community feed" />
                          <span className="w-11 h-6 bg-gray-300 rounded-full peer-checked:bg-[#7A9488] relative after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full flex-shrink-0"></span>
                        </label>
                        <label className="flex items-center justify-between cursor-pointer">
                          <span className={feedsVisibility.my ? 'text-gray-800' : 'text-gray-400'}>Mijn posts</span>
                          <input type="checkbox" className="sr-only peer" checked={feedsVisibility.my} onChange={() => toggleFeed('my')} />
                          <span className="w-11 h-6 bg-gray-300 rounded-full peer-checked:bg-[#7A9488] relative after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full flex-shrink-0"></span>
                        </label>
                        <label className="flex items-center justify-between cursor-pointer">
                          <span className={feedsVisibility.pinned ? 'text-gray-800' : 'text-gray-400'}>Gepinde posts</span>
                          <input type="checkbox" className="sr-only peer" checked={feedsVisibility.pinned} onChange={() => toggleFeed('pinned')} />
                          <span className="w-11 h-6 bg-gray-300 rounded-full peer-checked:bg-[#7A9488] relative after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full flex-shrink-0"></span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Display Instellingen */}
              <div id="display-section" className="bg-white rounded-lg order-3" style={{ border: '1px solid #F2F0EB', boxShadow: '0 1px 3px rgba(0,0,0,0.015)' }}>
                <div className="p-4 cursor-pointer flex items-center justify-between" style={{ borderBottom: '1px solid #F4F2ED' }} onClick={() => setDisplayOpen(prev => !prev)}>
                  <div className="flex items-center space-x-2">
                    <Settings size={18} strokeWidth={1.5} className="text-gray-500" />
                    <div>
                      <h3 className="font-medium">Display Instellingen</h3>
                      <p className="text-sm text-gray-500">Pas je visuele ervaring aan</p>
                    </div>
                  </div>
                  <span className="text-gray-500">{displayOpen ? '▾' : '▸'}</span>
                </div>
                {displayOpen && (
                  <div className="p-4 space-y-4">
                    {/* Focus Mode */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Focus Mode</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={displaySettings.focusMode}
                          onChange={(e) => setDisplaySettings(s => ({ ...s, focusMode: e.target.checked }))}
                        />
                        <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-pink-500 peer-focus:outline-none after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full peer-checked:after:border-white"></div>
                      </label>
                    </div>
                    {/* Energy Mode */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Energy Mode</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={displaySettings.energyMode}
                          onChange={(e) => setDisplaySettings(s => ({ ...s, energyMode: e.target.checked }))}
                        />
                        <div 
                          className="w-11 h-6 rounded-full peer peer-focus:outline-none after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full peer-checked:after:border-white transition-all duration-300 relative overflow-hidden"
                          style={{
                            background: displaySettings.energyMode 
                              ? 'linear-gradient(135deg, #9AAF88, #7A9488)' 
                              : '#e5e7eb',
                            boxShadow: displaySettings.energyMode 
                              ? '0 4px 12px rgba(112, 130, 89, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)' 
                              : 'none',
                            border: displaySettings.energyMode 
                              ? '1px solid rgba(112, 130, 89, 0.3)' 
                              : 'none'
                          }}
                        >
                        </div>
                      </label>
                    </div>
                    {/* Blue Light */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Blauw Licht</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox"
                          className="sr-only peer"
                          checked={displaySettings.blueLightFilter}
                          onChange={(e) => setDisplaySettings(s => ({ ...s, blueLightFilter: e.target.checked }))}
                        />
                        <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-500 peer-focus:outline-none after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full peer-checked:after:border-white"></div>
                      </label>
                    </div>
                    {/* Day/Night */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Dag/Nacht</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={displaySettings.dayNightMode === 'night'}
                          onChange={(e) => setDisplaySettings(s => ({ ...s, dayNightMode: e.target.checked ? 'night' : 'day' }))}
                        />
                        <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-purple-500 peer-focus:outline-none after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full peer-checked:after:border-white"></div>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Clean Mind Filter Card */}
              <div className="bg-white rounded-lg order-4" style={{ border: '1px solid #F2F0EB', boxShadow: '0 1px 3px rgba(0,0,0,0.015)' }}>
                <div className="p-4 cursor-pointer flex items-center justify-between" style={{ borderBottom: '1px solid #F4F2ED' }} onClick={() => setCleanOpen(prev => !prev)}>
                  <div className="flex items-center space-x-2">
                    <InsightIcon size={18} weight="regular" className="text-gray-500" />
                    <div>
                      <h3 className="font-medium text-gray-700">Clean Mind Filter</h3>
                      <p className="text-sm text-gray-500">Filter content voor een positieve ervaring</p>
                    </div>
                  </div>
                  <span className="text-gray-500">{cleanOpen ? '▾' : '▸'}</span>
                </div>
                {cleanOpen && (
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium">Clean Mind</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox"
                          className="sr-only peer"
                          checked={cleanMindEnabled}
                          onChange={(e) => setCleanMindEnabled(e.target.checked)}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                      </label>
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                      <button onClick={() => setCleanFilters(f => ({ ...f, videos: !f.videos }))} className={`w-full text-left px-3 py-2 text-sm border rounded-lg transition-colors relative ${cleanFilters.videos ? 'bg-white hover:bg-gray-50 text-gray-900' : 'bg-gray-100 text-gray-500'}`}>
                        Video's
                        {!cleanFilters.videos && <div className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full"></div>}
                      </button>
                      <button onClick={() => setCleanFilters(f => ({ ...f, images: !f.images }))} className={`w-full text-left px-3 py-2 text-sm border rounded-lg transition-colors relative ${cleanFilters.images ? 'bg-white hover:bg-gray-50 text-gray-900' : 'bg-gray-100 text-gray-500'}`}>
                        Afbeeldingen
                        {!cleanFilters.images && <div className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full"></div>}
                      </button>
                      <button onClick={() => setCleanFilters(f => ({ ...f, photos: !f.photos }))} className={`w-full text-left px-3 py-2 text-sm border rounded-lg transition-colors relative ${cleanFilters.photos ? 'bg-white hover:bg-gray-50 text-gray-900' : 'bg-gray-100 text-gray-500'}`}>
                        Foto's
                        {!cleanFilters.photos && <div className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full"></div>}
                      </button>
                      <button onClick={() => setCleanFilters(f => ({ ...f, text: !f.text }))} className={`w-full text-left px-3 py-2 text-sm border rounded-lg transition-colors relative ${cleanFilters.text ? 'bg-white hover:bg-gray-50 text-gray-900' : 'bg-gray-100 text-gray-500'}`}>
                        Tekst
                        {!cleanFilters.text && <div className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full"></div>}
                      </button>
                      <button onClick={() => setCleanFilters(f => ({ ...f, ai: !f.ai }))} className={`w-full text-left px-3 py-2 text-sm border rounded-lg transition-colors relative ${cleanFilters.ai ? 'bg-white hover:bg-gray-50 text-gray-900' : 'bg-gray-100 text-gray-500'}`}>
                        A.I.-content
                        {!cleanFilters.ai && <div className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full"></div>}
                      </button>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-sm">Emoji's verwijderen</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" checked={removeEmojis} onChange={(e) => setRemoveEmojis(e.target.checked)} />
                        <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-green-500 peer-focus:outline-none after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full peer-checked:after:border-white"></div>
                      </label>
                    </div>
                    <button
                      onClick={() => setCleanTextEnabled(!cleanTextEnabled)}
                      className={`w-full mt-3 px-3 py-2 text-sm border rounded-lg transition-colors relative ${
                        cleanTextEnabled 
                          ? 'bg-[#7A9488] text-white hover:bg-[#4F6660] border-[#7A9488]' 
                          : 'bg-gray-100 text-gray-500 border-gray-200'
                      }`}
                    >
                      Profanity Filter
                      {cleanTextEnabled && (
                        <div className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full"></div>
                      )}
                    </button>
                    <div className="mt-2 text-xs text-gray-600">
                      Geblokkeerde woorden (totaal): <span className="font-medium">{blockedWordsTotal}</span>
                    </div>
                    <button onClick={() => { setInsightsData(generateInsights()); setInsightsOpen(true); }} className="w-full mt-2 px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors">Clean Text Insights</button>
                  </div>
                )}
              </div>

              {/* Network Club Card */}
              <button
                type="button"
                onClick={() => setShowNetworkClubFinder(true)}
                className="bg-white rounded-lg p-4 text-left transition-all order-5 group"
                style={{ border: '1px solid #F2F0EB', boxShadow: '0 1px 3px rgba(0,0,0,0.015)' }}
                aria-label="Vind & connect met jouw netwerk club"
              >
                <div className="w-full h-24 rounded-md mb-3 overflow-hidden relative">
                  <div
                    className="w-full h-full bg-center bg-cover transition-transform group-hover:scale-105"
                    style={{
                      backgroundImage: `url(${networkClubImages[networkClubImageIndex]})`,
                      filter: 'saturate(0.6) brightness(0.95)'
                    }}
                  />
                  <div className="absolute inset-0 pointer-events-none rounded-md" style={{ backgroundColor: 'rgba(141,168,160,0.25)' }} />
                </div>
                <h3 className="font-medium group-hover:text-[#4F6660] transition-colors">Vind &amp; connect met jouw netwerk club</h3>
                <p className="text-sm text-gray-500">
                  Bekijk clubs op basis van regio, provincie of land.
                </p>
                <div className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[#7A9488] bg-stone-50 border border-stone-200 rounded-full px-2 py-1">
                  <Search size={12} /> Zoek clubs
                </div>
              </button>
            </div>
          </div>
        </div>
        {/* Main Content Area */}
        <div className={`col-span-12 lg:col-span-9 ${effectiveMobileTab !== undefined ? 'flex flex-col h-full min-h-0' : 'space-y-8'}`}>
          {/* Multifunctionele Header - Video's, Events, Ondernemers Online, Club Nieuws
              On mobile: hidden when effectiveMobileTab===0 (newsfeed), otherwise shown with correct tab.
              On desktop (effectiveMobileTab===undefined): always shown. */}
          <div
            className={effectiveMobileTab !== undefined && effectiveMobileTab !== 0 ? 'flex-1 min-h-0' : ''}
            style={effectiveMobileTab === 0 ? { display: 'none' } : {}}
          >
            <MultiHeader 
              isAdmin={isAdmin}
              currentUser={user}
              userProfile={userProfile}
              initialTab={effectiveMobileTab !== undefined ? MOBILE_TAB_TO_HEADER[effectiveMobileTab] : undefined}
            />
          </div>

          {/* Social Media Expiration Banners — hidden on mobile when on non-newsfeed tab */}
          {showFeedContent && expiringConnections
            .filter((conn) => !dismissedBanners.includes(conn.platform))
            .map((conn) => (
              <SocialMediaExpirationBanner
                key={conn.platform}
                platform={conn.platform}
                daysUntilExpiry={conn.daysUntilExpiry}
                onRenew={() => {
                  // Navigate to social media settings
                  window.location.hash = '#social-media';
                }}
                onDismiss={() => {
                  // Dismiss for this session only
                  setDismissedBanners((prev) => [...prev, conn.platform]);
                }}
              />
            ))}

          {/* Ontvangen connectieverzoeken verplaatst naar Connecties (sidebar) */}

          {/* Mobile mini-composer pill — tappable row, only on mobile newsfeed tab when collapsed */}
          {effectiveMobileTab === 0 && feedsVisibility.news && !isPostExpanded && (
            <div
              className={`grid grid-cols-[32px_minmax(0,1fr)_32px_32px_32px] items-center gap-1.5 w-full bg-white rounded-full border px-3 flex-shrink-0 overflow-hidden transition-all duration-300 ease-in-out ${
                mobileFeedChromeHidden
                  ? 'max-h-0 min-h-0 py-0 mb-0 opacity-0 -translate-y-2 border-transparent shadow-none pointer-events-none'
                  : 'max-h-[64px] min-h-[48px] py-2.5 mb-[6px] opacity-100 translate-y-0 border-[#E5E2DD] shadow-sm'
              }`}
            >
              <img
                src={getValidProfilePhoto(user, userProfile)}
                alt=""
                className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                style={{ objectPosition: userProfile?.photoPosition ? `${userProfile.photoPosition.x}% ${userProfile.photoPosition.y}%` : 'center' }}
                onError={(e) => { (e.target as HTMLImageElement).src = 'https://ui-avatars.com/api/?name=U&background=cccccc&color=fff&size=64'; }}
              />
              <button
                type="button"
                onClick={() => setIsPostExpanded(true)}
                className="min-w-0 text-left text-sm text-gray-400 truncate"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Deel iets waardevols...
              </button>
              {/* Direct camera shortcut in collapsed composer pill */}
              <button
                type="button"
                aria-label="Foto maken"
                onClick={() => {
                  setIsPostExpanded(true);
                  window.setTimeout(() => handleMobileMediaChoice('photo'), 80);
                }}
                className="w-8 h-8 rounded-full flex items-center justify-center text-[#4F6660] hover:bg-[#E8F4EF] active:bg-[#4F6660] active:text-white flex-shrink-0 transition-colors"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Camera size={18} />
              </button>
              {/* Direct video shortcut */}
              <button
                type="button"
                aria-label="Video opnemen"
                onClick={() => {
                  setIsPostExpanded(true);
                  window.setTimeout(() => handleMobileMediaChoice('video'), 80);
                }}
                className="w-8 h-8 rounded-full flex items-center justify-center text-[#4F6660] hover:bg-[#E8F4EF] active:bg-[#4F6660] active:text-white flex-shrink-0 transition-colors"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Video size={18} />
              </button>
              {/* Gallery / all media */}
              <button
                type="button"
                aria-label="Galerij kiezen"
                onClick={() => {
                  setIsPostExpanded(true);
                  window.setTimeout(openMobileMediaSheet, 80);
                }}
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-50 flex-shrink-0"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <ImageIcon size={18} weight="regular" />
              </button>
            </div>
          )}

          {/* Enhanced Post Creation Card — hidden on mobile newsfeed tab when collapsed (mini pill shows instead)
              On mobile when expanded: full-screen overlay as a flex-column sheet */}
          <div
            ref={composerCardRef}
            className={
              effectiveMobileTab === 0 && isPostExpanded
                ? 'bg-white flex flex-col overflow-hidden'
                : `bg-white rounded-2xl ${isPostExpanded ? 'p-4' : 'p-3 h-[120px] overflow-hidden'}`
            }
            style={{
              ...(effectiveMobileTab !== 0 || !isPostExpanded ? { border: '1px solid #F0EEE9', boxShadow: '0 1px 6px rgba(0,0,0,0.018)' } : {}),
              ...(!showFeedContent ? { display: 'none' } : {}),
              ...(effectiveMobileTab === 0 && !isPostExpanded ? { display: 'none' } : {}),
              ...(effectiveMobileTab === 0 && isPostExpanded ? {
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                zIndex: 9999,
              } : {}),
            }}
          >
            {/* ── Mobile full-screen composer header ─────────────────────────
                 Left: Annuleren  |  Center: Nieuw bericht  |  Right: Plaatsen */}
            {effectiveMobileTab === 0 && isPostExpanded && (
              <div
                className="flex items-center justify-between px-4 border-b border-gray-200 flex-shrink-0"
                style={{ height: 52, backgroundColor: '#ffffff' }}
              >
                {/* Annuleren — always closes, resets draft if nothing typed */}
                <button
                  onClick={() => {
                    setIsPostExpanded(false);
                    if (!newPostContent.trim() && attachments.length === 0) {
                      setAiDisclosure(null);
                    }
                  }}
                  className="text-sm text-gray-500 font-medium"
                  style={{ WebkitTapHighlightColor: 'transparent', minWidth: 80 }}
                >
                  Annuleren
                </button>

                {/* Title */}
                <span className="text-[15px] font-semibold text-gray-900">Nieuw bericht</span>

                {/* Plaatsen */}
                <button
                  onClick={() => {
                    if (aiDisclosure === null) {
                      setPulseAiSection(true);
                      aiDisclosureRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                      setTimeout(() => setPulseAiSection(false), 2200);
                      return;
                    }
                    handleCreatePost();
                  }}
                  disabled={isSubmittingPost || (!newPostContent.trim() && attachments.length === 0)}
                  className="text-sm font-semibold rounded-full px-4 py-1.5 text-white transition-opacity disabled:opacity-40"
                  style={{ backgroundColor: '#8DA8A0', minWidth: 80, WebkitTapHighlightColor: 'transparent' }}
                >
                  {isSubmittingPost ? (
                    <svg className="animate-spin h-4 w-4 text-white mx-auto" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                  ) : 'Plaatsen'}
                </button>
              </div>
            )}

            {/* Header row with icons - always above the input */}
            <div className={`flex items-center justify-end gap-2 mb-2 ${effectiveMobileTab === 0 ? 'hidden' : ''}`}>
              {/* Expand/Collapse button — hidden on mobile (back button handles collapse) */}
              {isPostExpanded ? (
                <button
                  onClick={() => setIsPostExpanded(false)}
                  className="w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center transition-colors"
                  aria-label="Inklappen"
                  title="Inklappen"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </button>
              ) : (
                <button
                  onClick={() => setIsPostExpanded(true)}
                  className="w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center transition-colors"
                  aria-label="Uitklappen"
                  title="Uitklappen"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              )}
              
            </div>
            
            {/* Scrollable content area — flex-1 on mobile full-screen so it fills between header and toolbar */}
            <div
              ref={composerScrollRef}
              className={effectiveMobileTab === 0 && isPostExpanded ? 'flex-1 overflow-y-auto px-3 pt-3 pb-2' : ''}
              onScroll={effectiveMobileTab === 0 && isPostExpanded ? (e) => {
                const el = e.currentTarget;
                const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 100;
                setShowComposerScrollTop(nearBottom && el.scrollTop > 100);
              } : undefined}
            >

            {/* Input row */}
            <div className={`flex items-start ${effectiveMobileTab === 0 && isPostExpanded ? '' : 'space-x-3'}`}>
              {/* Avatar — only shown outside mobile full-screen composer */}
              {!(effectiveMobileTab === 0 && isPostExpanded) && (
                <img 
                  src={getValidProfilePhoto(user, userProfile)}
                  alt="Profile" 
                  className={`rounded-full object-cover ${isPostExpanded ? 'w-10 h-10' : 'w-9 h-9'}`}
                  style={{
                    objectPosition: userProfile?.photoPosition 
                      ? `${userProfile.photoPosition.x}% ${userProfile.photoPosition.y}%` 
                      : 'center'
                  }}
                  onError={(e) => {
                    const name = user?.displayName || userProfile?.displayName || 'U';
                    const initials = name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
                    (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=4F6660&color=fff&size=96`;
                  }}
                />
              )}
              <div className="flex-1">
                {/* On mobile full-screen: give the textarea a visible container */}
                {effectiveMobileTab === 0 && isPostExpanded && (
                  <MentionInput
                    value={newPostContent}
                    onChange={handlePostContentChange}
                    onPaste={handleComposerPaste}
                    placeholder="Wat wil je delen? Gebruik @ om iemand te taggen."
                    rows={7}
                    className="text-[15px] leading-relaxed"
                    style={{ minHeight: 160, padding: '2px 0' }}
                    showFormatting={true}
                    onFocus={() => setIsPostExpanded(true)}
                    onMentionsChange={(mentions) => {
                      setPostCollaborations(mentions);
                    }}
                  />
                )}
                {!(effectiveMobileTab === 0 && isPostExpanded) && <MentionInput
                  value={newPostContent}
                  onChange={handlePostContentChange}
                  onPaste={handleComposerPaste}
                  placeholder="Deel iets waardevols met ondernemers..."
                  rows={isPostExpanded ? 4 : 2}
                  className={isPostExpanded ? 'text-sm leading-relaxed' : 'py-2 text-sm'}
                  onFocus={() => setIsPostExpanded(true)}
                  onMentionsChange={(mentions) => {
                    setPostCollaborations(mentions);
                  }}
                />}
                
                {/* All expanded content - hidden when collapsed */}
                {isPostExpanded && (
                  <>
                {/* Collaboration Indicator */}
                {postCollaborations.userIds.length > 0 && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-gray-600">🤝 Collaborations:</span>
                    {postCollaborations.names.map((name, idx) => (
                      <span 
                        key={idx}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-stone-50 to-blue-50 text-[#4F6660] text-xs font-medium rounded-full border border-stone-200"
                      >
                        @{name}
                      </span>
                    ))}
                  </div>
                )}

                {(suggestedHashtags.length > 0 || hashtagsLoading) && newPostContent.trim().length >= 40 && (
                  <div className="mt-2">
                    <div className="mb-1.5 flex items-center gap-2">
                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Hashtags</span>
                      {hashtagsLoading && (
                        <span className="text-[10px] text-gray-400 animate-pulse">Analyseren…</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {hashtagsLoading && suggestedHashtags.length === 0 && [1,2,3].map(i => (
                        <div key={i} className="h-6 w-16 rounded-full bg-gray-100 animate-pulse" />
                      ))}
                      {suggestedHashtags.map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => {
                            setNewPostContent((prev) => {
                              const trimmed = prev.trimEnd();
                              const alreadyPresent = new RegExp(`${tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(trimmed);
                              if (alreadyPresent) return prev;
                              const lines = trimmed.split('\n');
                              const lastLine = lines[lines.length - 1] ?? '';
                              if (/^\s*(#\S+\s*)+$/.test(lastLine)) {
                                return `${trimmed} ${tag}`;
                              }
                              return trimmed ? `${trimmed}\n${tag}` : tag;
                            });
                            setSuggestedHashtags((prev) => prev.filter((t) => t !== tag));
                            setSelectedHashtags((prev) => prev.filter((t) => t !== tag));
                          }}
                          className="inline-flex items-center gap-0.5 rounded-full border border-[#C8D8D4] bg-[#F0F7F4] px-2.5 py-1 text-[11px] font-medium text-[#4F6660] transition-all active:bg-[#7A9488] active:text-white"
                        >
                          <span className="text-[10px] opacity-60">+</span>{tag}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Live YouTube Preview */}
                {(() => {
                  const youtubeVideoId = extractYouTubeVideoId(newPostContent);
                  return youtubeVideoId ? (
                    <div className="mt-3">
                      <div className="bg-gray-50 rounded-lg border p-3">
                        <div className="flex items-center space-x-2 mb-3">
                          <span className="text-red-600 text-sm font-medium">🎥 Video</span>
                          <span className="text-xs text-gray-500">•</span>
                          <span className="text-xs text-gray-500">Speel direct in de feed</span>
                        </div>
                        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                          <iframe
                            src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=0&controls=1&rel=0&modestbranding=1`}
                            title="YouTube video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="absolute top-0 left-0 w-full h-full rounded"
                          />
                        </div>
                      </div>
                    </div>
                  ) : null;
                })()}
                
                  {/* Social Sharing Options */}
                  <div className={effectiveMobileTab === 0 ? 'mt-2 pt-2 border-t border-gray-100' : 'mt-3 pt-3 border-t border-gray-100'}>
                    {/* Post Limit Indicator */}
                    <div className={effectiveMobileTab === 0 ? 'mb-2 text-xs' : 'mb-3'}>
                      <PostLimitIndicator showDetails={true} />
                    </div>
                    
                    {/* Social Sharing Options */}
                    <div className={effectiveMobileTab === 0 ? 'mb-2' : 'mb-3'}>
                      <SocialSharingOptions 
                        onSelectionChange={setSelectedSocialPlatforms}
                        compact={effectiveMobileTab === 0}
                      />
                    </div>
                  </div>

                  {effectiveMobileTab === 0 && (pendingUploadPreview || attachments.length > 0) && (
                    <div className="mt-2 rounded-xl border border-gray-200 bg-white p-2">
                      {pendingUploadPreview && (
                        <div className="mb-2 rounded-lg bg-[#F0F7F4] p-2">
                          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                            {pendingUploadPreview.type === 'image' ? (
                              <img src={pendingUploadPreview.url} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
                            ) : (
                              <div style={{ width: 56, height: 56, borderRadius: 8, background: '#E8EDE4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {pendingUploadPreview.type === 'video' ? '▶' : '📄'}
                              </div>
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className="truncate text-xs font-semibold text-gray-700">{pendingUploadPreview.name}</div>
                              <div className="mt-1 text-[11px] text-[#4F6660]">Uploaden... {uploadProgress}%</div>
                              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white">
                                <div className="h-full rounded-full bg-[#8DA8A0]" style={{ width: `${uploadProgress}%` }} />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {attachments.map(att => (
                        <div key={att.url} className="mb-2 last:mb-0 rounded-lg border border-gray-100 p-2">
                          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                            {att.type === 'image' ? (
                              <img src={att.url} alt={att.name || 'Afbeelding'} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
                            ) : (
                              <div style={{ width: 56, height: 56, borderRadius: 8, background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {att.type === 'video' ? '▶' : '📄'}
                              </div>
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className="truncate text-xs font-semibold text-gray-700">{att.name || 'Bijlage'}</div>
                              <div className="text-[11px] text-[#4F6660]">Toegevoegd</div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setAttachments(prev => prev.filter(a => a.url !== att.url))}
                              className="rounded-full px-2 py-1 text-[11px] font-medium text-red-500"
                            >
                              Verwijder
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Platform preview — full-width (mobiel én desktop) */}
                  {(effectiveMobileTab === undefined || effectiveMobileTab === 0) && (newPostContent.trim().length >= 40 || pendingUploadPreview || attachments[0]) && (
                    renderMobileFeedPreview(pendingUploadPreview || attachments[0] || null)
                  )}

                  {/* A.I. Content Disclosure — verplicht voor elke post */}
                  <div ref={aiDisclosureRef} className={`${effectiveMobileTab === 0 ? 'mt-2 pt-2' : 'mt-3 pt-3'} border-t border-gray-100 rounded-lg transition-all duration-300 ${pulseAiSection ? 'bg-amber-50 px-3 pb-2' : ''}`}>
                    {showAiPastePrompt && (
                      <div className="mb-2 rounded-xl border border-[#DDE7DF] bg-[#F0F7F4] p-3">
                        <div className="text-xs font-semibold text-[#4F6660]">Grote tekst geplakt</div>
                        <p className="mt-1 text-[11px] leading-relaxed text-gray-600">
                          Heb je AI gebruikt bij deze tekst? Transparantie helpt vertrouwen houden in menselijk contact.
                        </p>
                        <div className="mt-2 grid grid-cols-3 gap-1.5">
                          {([
                            { value: 'no', label: 'Nee' },
                            { value: 'partial', label: 'Deels' },
                            { value: 'full', label: 'Volledig' },
                          ] as const).map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => answerAiPastePrompt(option.value)}
                              className="rounded-lg border border-[#DDE7DF] bg-white px-2 py-1.5 text-[11px] font-semibold text-[#4F6660]"
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className={effectiveMobileTab === 0 ? 'flex items-center gap-1 mb-1.5' : 'flex items-center gap-1 mb-2'}>
                      <span className={`${effectiveMobileTab === 0 ? 'text-[11px]' : 'text-xs'} font-medium transition-colors duration-300 ${pulseAiSection ? 'text-amber-700' : 'text-gray-600'}`}>Is dit A.I.-content?</span>
                      <span className="text-[10px] text-red-500 font-semibold">*verplicht</span>
                    </div>
                    <div className={`flex ${effectiveMobileTab === 0 ? 'gap-1.5' : 'gap-2'} rounded-lg transition-all duration-300 ${pulseAiSection ? 'ring-2 ring-amber-400 animate-pulse p-1' : ''}`}>
                      {([
                        { value: 'no', label: 'Nee' },
                        { value: 'partial', label: 'Gedeeltelijk A.I.' },
                        { value: 'full', label: 'Volledig A.I.' },
                      ] as const).map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => { setAiDisclosure(opt.value); setPulseAiSection(false); }}
                          className={`flex-1 ${effectiveMobileTab === 0 ? 'px-2 py-1.5 text-[11px] rounded-md' : 'px-3 py-1.5 text-xs rounded-lg'} border transition-all ${
                            aiDisclosure === opt.value
                              ? 'border-[#7A9488] bg-[#7A9488] text-white font-medium'
                              : pulseAiSection
                                ? 'border-amber-400 bg-white text-amber-700 hover:border-amber-500'
                                : 'border-gray-200 bg-white text-gray-600 hover:border-[#7A9488] hover:text-[#4F6660]'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {aiDisclosure === null && (newPostContent.trim() || attachments.length > 0) && (
                      <p className={`mt-1.5 text-[11px] flex items-center gap-1 transition-all duration-300 ${pulseAiSection ? 'text-amber-700 font-semibold' : 'text-amber-600'}`}>
                        <span className={pulseAiSection ? 'animate-bounce inline-block' : ''}>⚠</span> Kies één optie om te kunnen plaatsen
                      </p>
                    )}
                    {aiAssistanceTypes.length > 0 && aiDisclosure === 'no' && (
                      <p className="mt-1.5 text-[11px] text-amber-600">
                        De app herkent AI-hulp in deze sessie. Controleer of `Nee` klopt.
                      </p>
                    )}
                    {aiAssistanceTypes.length > 0 && (aiDisclosure === 'partial' || aiDisclosure === 'full') && (
                      <p className="mt-1.5 text-[11px] text-[#4F6660]">
                        AI-hulp wordt transparant opgeslagen bij deze post.
                      </p>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className={`mt-3 pt-3 border-t border-gray-100 ${effectiveMobileTab === 0 ? 'hidden' : ''}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex space-x-2 flex-wrap items-center">
                        <button 
                          ref={emojiButtonRef}
                          aria-label="Emoji toevoegen" 
                          title="Emoji toevoegen" 
                          onClick={() => setShowEmojiPicker(!showEmojiPicker)} 
                          className="flex items-center gap-1.5 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors text-sm"
                        >
                          <EmojiIcon size={18} weight="regular" />
                          <span>Emoji</span>
                        </button>
                        <button 
                          aria-label="Upload afbeelding of video" 
                          title="Upload afbeelding of video" 
                          onClick={openMediaPicker} 
                          className="flex items-center gap-1.5 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors text-sm"
                        >
                          <ImageIcon size={18} weight="regular" />
                          <span>Afbeelding</span>
                        </button>
                        <button 
                          aria-label="Upload bestand" 
                          title="Upload bestand" 
                          onClick={openFilePicker} 
                          className="flex items-center gap-1.5 px-3 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors text-sm"
                        >
                          <AttachIcon size={18} weight="regular" />
                          <span>Bestand</span>
                        </button>
            </div>
                  <div className="flex items-center gap-2">
                    <button 
                          onClick={() => {
                            if (aiDisclosure === null) {
                              setPulseAiSection(true);
                              aiDisclosureRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                              setTimeout(() => setPulseAiSection(false), 2200);
                              return;
                            }
                            setShowSchedulerModal(true);
                          }}
                          disabled={(!newPostContent.trim() && attachments.length === 0)}
                          aria-label="Post plannen"
                          className="px-5 py-2 text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                          style={{ backgroundColor: '#8DA8A0' }}
                          onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = '#7A9488'; }}
                          onMouseLeave={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = '#8DA8A0'; }}
                    >
                      <CalendarIcon size={16} weight="regular" />
                      <span>Plannen</span>
                    </button>
                    <button 
                          onClick={handleCreatePost}
                          disabled={isSubmittingPost || (!newPostContent.trim() && attachments.length === 0)}
                          aria-label="Post plaatsen"
                          className="px-5 py-2 text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                          style={{ backgroundColor: '#8DA8A0' }}
                          onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = '#7A9488'; }}
                          onMouseLeave={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = '#8DA8A0'; }}
                    >
                      {isSubmittingPost ? (
                        <>
                          <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                          </svg>
                          <span>Plaatsen…</span>
                        </>
                      ) : (
                        <span>Plaatsen</span>
                      )}
                    </button>
                  </div>
                    {/* Post submission toast */}
                    {postToast && (
                      <div className={`mt-3 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${postToast.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                        {postToast.type === 'success' ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        )}
                        <span>{postToast.message}</span>
                      </div>
                    )}
                    </div>
                    {/* Hidden inputs for file picking */}
                    <input ref={photoInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) { setSelectedImageFile(f); uploadSelectedFile(f, 'image'); }
                      if (photoInputRef.current) photoInputRef.current.value = '';
                    }} />
                    <input ref={videoInputRef} type="file" accept="video/*" capture="environment" className="hidden" onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadSelectedFile(f, 'video');
                      if (videoInputRef.current) videoInputRef.current.value = '';
                    }} />
                    <input ref={fileInputRef} type="file" className="hidden" onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadSelectedFile(f, 'file');
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }} />

                    {/* Upload progress */}
                    {effectiveMobileTab !== 0 && uploading && (
                      <div aria-live="polite" className="mt-2 text-xs text-gray-500">Uploaden… {uploadProgress}%</div>
                    )}

                    {/* Attachments preview */}
                    {effectiveMobileTab !== 0 && attachments.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-3">
                        {attachments.map(att => (
                          <div key={att.url} className="border rounded-lg p-2 flex items-center gap-2 bg-white">
                            {(att.type === 'image' || (att.name && /\.(jpg|jpeg|png|gif|webp)$/i.test(att.name))) && (
                              <img src={att.url} alt={att.name || 'image'} className="w-12 h-12 object-cover rounded" />
                            )}
                            {(att.type === 'video' || (att.name && /\.(mp4|avi|mov|wmv|flv|webm)$/i.test(att.name))) && (
                              <div className="w-12 h-12 bg-gray-900 text-white flex items-center justify-center rounded">▶️</div>
                            )}
                            {(att.type === 'file' || (!att.type && att.name && !/\.(jpg|jpeg|png|gif|webp|mp4|avi|mov|wmv|flv|webm)$/i.test(att.name))) && (
                              <div className="w-12 h-12 bg-gray-100 flex items-center justify-center rounded">📄</div>
                            )}
                            <div className="text-xs text-gray-600 max-w-[160px] truncate">{att.name || att.type}</div>
                            <button onClick={() => setAttachments(prev => prev.filter(a => a.url !== att.url))} className="text-xs text-red-600 hover:underline">Verwijder</button>
                  </div>
                        ))}
                </div>
              )}
                  </div>
                  </>
                )}
              </div>
            {/* /scrollable content area */}
            </div>

            {/* ── Mobile bottom toolbar — flex-shrink-0, pins above keyboard ──
                 Shows emoji/media/file icons on mobile full-screen compose mode. */}
            {effectiveMobileTab === 0 && isPostExpanded && (
              <div className="flex-shrink-0 border-t border-gray-200 bg-white px-2 pb-3 pt-2">
                <div className="flex items-center gap-0.5">
                  <button
                    ref={emojiButtonRef}
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="flex items-center justify-center w-11 h-11 rounded-full hover:bg-gray-100 text-gray-500"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                    title="Emoji"
                  >
                    <EmojiIcon size={22} weight="regular" />
                  </button>
                  {/* Direct camera capture — foto */}
                  <button
                    onClick={() => handleMobileMediaChoice('photo')}
                    className="flex items-center justify-center w-11 h-11 rounded-full hover:bg-[#E8F4EF] text-[#4F6660] active:bg-[#4F6660] active:text-white transition-colors"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                    title="Foto maken"
                  >
                    <Camera size={22} />
                  </button>
                  {/* Direct camera capture — video */}
                  <button
                    onClick={() => handleMobileMediaChoice('video')}
                    className="flex items-center justify-center w-11 h-11 rounded-full hover:bg-[#E8F4EF] text-[#4F6660] active:bg-[#4F6660] active:text-white transition-colors"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                    title="Video opnemen"
                  >
                    <Video size={22} />
                  </button>
                  {/* Gallery / existing files */}
                  <button
                    onClick={openMobileMediaSheet}
                    className="flex items-center justify-center w-11 h-11 rounded-full hover:bg-gray-100 text-gray-500"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                    title="Galerij / bijlage"
                  >
                    <ImageIcon size={22} weight="regular" />
                  </button>
                  <button
                    onClick={openFilePicker}
                    className="flex items-center justify-center w-11 h-11 rounded-full hover:bg-gray-100 text-gray-500"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                    title="Bestand"
                  >
                    <AttachIcon size={22} weight="regular" />
                  </button>
                  <button
                    onClick={() => {
                      if (aiDisclosure === null) {
                        setPulseAiSection(true);
                        aiDisclosureRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                        setTimeout(() => setPulseAiSection(false), 2200);
                        return;
                      }
                      setShowSchedulerModal(true);
                    }}
                    disabled={!newPostContent.trim() && attachments.length === 0}
                    className="flex items-center justify-center w-11 h-11 rounded-full hover:bg-gray-100 text-gray-500 disabled:opacity-40"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                    title="Plannen"
                  >
                    <CalendarIcon size={20} weight="regular" />
                  </button>
                  {uploading && (
                    <span className="ml-auto mr-2 text-xs text-gray-400">Uploaden… {uploadProgress}%</span>
                  )}
                </div>
              </div>
            )}

        </div>
      </div>

          {/* Emoji Picker */}
          <EmojiPicker
            isOpen={showEmojiPicker}
            onClose={() => setShowEmojiPicker(false)}
            anchorRef={emojiButtonRef}
            onEmojiSelect={(emoji: string) => {
              setNewPostContent(prev => prev + emoji);
              setShowEmojiPicker(false);
            }}
          />

          {/* Feeds - hidden on mobile when on non-newsfeed tab */}
          <div
            className={effectiveMobileTab === 0 ? `flex-1 min-h-0 flex flex-col ${showFocusCard ? 'overflow-visible' : 'overflow-hidden'}` : `grid ${feedGridCols} gap-4 [>*]:order-none`}
            style={!showFeedContent ? { display: 'none' } : {}}
          >
            {/* Nieuwsfeed (alle gebruikers) */}
            <div
              className={effectiveMobileTab === 0
                ? `${feedsVisibility.news ? 'flex flex-col flex-1 min-h-0' : 'hidden'}`
                : `bg-white rounded-2xl ${feedsVisibility.news ? '' : 'hidden'} order-1 relative`}
              style={effectiveMobileTab === 0 ? {} : { border: '1px solid #F0EEE9', boxShadow: '0 1px 6px rgba(0,0,0,0.018)' }}
              role="region" aria-labelledby="newsfeed-heading"
            >
              <div className={`items-center gap-2 px-4 rounded-t-2xl ${showFocusCard ? 'overflow-visible relative z-[1000]' : 'overflow-hidden'} transition-all duration-300 ease-in-out ${
                effectiveMobileTab === 0
                  ? mobileFeedChromeHidden
                    ? 'flex max-h-0 py-0 opacity-0 -translate-y-2 pointer-events-none'
                    : 'flex max-h-[44px] pt-2 pb-2 opacity-100 translate-y-0'
                  : 'flex pt-5 pb-4'
              }`}>
                {/* Title — hidden on mobile (PageTabs tab already shows "Newsfeed") */}
                <h3 id="newsfeed-heading" className={`whitespace-nowrap ${effectiveMobileTab === 0 ? 'hidden' : ''}`} style={{ fontSize: '1.05rem', fontWeight: 600, color: '#3C4F49', letterSpacing: '0.015em' }}>Nieuwsfeed</h3>
                {/* Focus Control - subtiel, direct na de teller */}
                <div className="relative z-[1001] ml-1 flex-shrink-0">
                  <button
                    onClick={() => setShowFocusCard(!showFocusCard)}
                    className="flex flex-shrink-0 items-center gap-1 px-2 py-0.5 rounded-md transition-all duration-200 whitespace-nowrap"
                    style={
                      showFocusCard || focusIsBlocked || focusState.activeMode !== 'none'
                        ? { backgroundColor: '#6B9E8F', color: '#FFFFFF' }
                        : { backgroundColor: '#8EAF9F', color: '#FFFFFF' }
                    }
                    aria-label="Focus Control"
                    title="Focus Control — beperk je scrolltijd"
                  >
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9.5 2a2.5 2.5 0 1 1 5 0 2.5 2.5 0 0 1-5 0z"/>
                      <path d="M12 7c-4 0-7 2.5-7 5.5S8 18 12 18s7-2.5 7-5.5S16 7 12 7z"/>
                      <path d="M12 10v3l2 1.5"/>
                    </svg>
                    <span className="text-[10px] font-medium" style={{ letterSpacing: '0.01em' }}>
                      {focusRemainingSeconds > 0 ? formatRemainingTime(focusRemainingSeconds) : 'Focus'}
                    </span>
                    <svg className={`w-2.5 h-2.5 hidden sm:block transition-transform duration-200 ${showFocusCard ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showFocusCard && effectiveMobileTab !== 0 && (
                    <div className="absolute left-0 top-full mt-1 z-[1001]">
                      <FocusControlCard onClose={() => setShowFocusCard(false)} />
                    </div>
                  )}
                </div>
                
                {/* Spacer */}
                <div className="flex-1" />
                
                {/* Connections Only Toggle */}
                <button
                  onClick={() => setConnectionsOnly(!connectionsOnly)}
                  className="flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap transition-all"
                  style={connectionsOnly
                    ? { backgroundColor: '#7A9488', color: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }
                    : { backgroundColor: '#FFFFFF', color: '#7A8A84', border: '1px solid #ECEAE5' }
                  }
                  title={connectionsOnly ? 'Toon alle posts' : 'Toon alleen posts van connecties'}
                >
                  {connectionsOnly ? '✓ Connecties' : 'Alle posts'}
                </button>
                
                {/* Vergroot */}
                <button onClick={() => setExpandedFeed('news')} aria-label="Vergroot" title="Vergroot" className="flex-shrink-0 px-1.5 py-0.5 text-[10px] bg-white border text-gray-500 rounded hover:bg-gray-50">⤢</button>
                
                {/* Info */}
                <div className="flex-shrink-0">
                  <InfoTooltip 
                    data-info-spot="newsfeed"
                    title="Nieuwsfeed" 
                    items={[
                      "Posts worden gesorteerd op tijd — nieuwste bovenaan",
                      "Respect en Mutual Respect versterken zichtbaarheid",
                      "Geen likes of scores — alleen echte interactie telt",
                      "Zichtbaarheid = tijd online, niet positie of ranking",
                      "AI Visibility Impact analyseert netwerkgedrag (niet feed-ranking)",
                      "Toggle 'Connecties' om alleen bekende connecties te zien",
                      "Gedeelde video's krijgen een visibilityBoost"
                    ]} 
                  />
                </div>
              </div>
              <div
                onScroll={handleMobileFeedScroll}
                className={`feed-container overflow-y-auto overflow-x-hidden px-4 space-y-5 ${effectiveMobileTab === 0 ? 'flex-1 min-h-0 pb-[82px]' : 'max-h-[calc(100vh-160px)] pb-4'}`}
              >
                {sortedDisplayPosts.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    {connectionsOnly ? (
                      <>
                        <p className="mb-2">Geen posts van je connecties</p>
                        <p className="text-xs mb-2">Maak connecties om hun posts te zien</p>
                      </>
                    ) : (
                      <>
                        <p className="mb-2">Nog geen posts</p>
                        <button onClick={() => { setIsPostExpanded(true); textAreaRef.current?.focus(); }} className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50">Plaats je eerste post</button>
                      </>
                    )}
                  </div>
                ) : (
                sortedDisplayPosts.map((post: any, index: number) => (
                  <div key={index} className="bg-white rounded-lg p-4 relative overflow-hidden" style={{ border: '1px solid #F0EEE9', boxShadow: '0 1px 4px rgba(0,0,0,0.015)' }}>
                    {/* Mute Person Button */}
                    <div className="absolute top-2 right-2">
                      <MutePersonButton 
                        userId={post.userId || post.authorId || 'unknown'}
                        userName={post.authorName || post.displayName || 'Onbekende gebruiker'}
                      />
                    </div>
                    
                    <div className="flex items-center space-x-2 mb-3">
                      <img src={getPostAvatar(post)} alt="Profielafbeelding" className="w-8 h-8 rounded-full" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm" style={{ color: '#1F2933' }}>{post.author}</p>
                          {/* Connection Indicator - toon alleen wanneer toggle UIT is */}
                          {!connectionsOnly && Array.isArray(communityUserIds) && communityUserIds.includes(post.userId) && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-green-50 text-green-600 border border-green-200" title="Connectie">
                              <span>🤝</span>
                            </span>
                          )}
                          {/* A.I. content badge — subtiel, klein, rechts */}
                          {(post.aiDisclosure === 'partial' || post.aiDisclosure === 'full') && (
                            <span
                              className="inline-flex items-center px-1 py-0.5 rounded text-[9px] font-medium tracking-wide select-none"
                              style={{ backgroundColor: 'rgba(139,92,246,0.07)', color: '#7C6FA0', border: '1px solid rgba(139,92,246,0.14)' }}
                              title={post.aiDisclosure === 'full' ? 'Volledig A.I.-gegenereerde content' : 'Gedeeltelijk A.I.-gegenereerde content'}
                            >
                              A.I.
                            </span>
                          )}
                          {/* Social Media Badges — licht, zacht, past bij sage thema */}
                          {post.sharedTo && post.sharedTo.includes('linkedin') && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs" style={{ backgroundColor: 'rgba(10,102,194,0.07)', color: '#5E8AB4', border: '1px solid rgba(10,102,194,0.12)' }} title="Gedeeld naar LinkedIn">
                              <LinkedInIcon size={11} weight="regular" />
                              LinkedIn
                            </span>
                          )}
                          {post.sharedTo && post.sharedTo.includes('facebook') && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs" style={{ backgroundColor: 'rgba(24,119,242,0.06)', color: '#6A9FD8', border: '1px solid rgba(24,119,242,0.10)' }} title="Gedeeld naar Facebook">
                              <FacebookIcon size={11} weight="regular" />
                              Facebook
                            </span>
                          )}
                          {post.sharedTo && post.sharedTo.includes('twitter') && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs" style={{ backgroundColor: 'rgba(0,0,0,0.04)', color: '#8A8A8A', border: '1px solid rgba(0,0,0,0.08)' }} title="Gedeeld naar X">
                              <TwitterIcon size={11} weight="regular" />
                              X
                            </span>
                          )}
                          {post.sharedTo && post.sharedTo.includes('instagram') && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs" style={{ backgroundColor: 'rgba(225,48,108,0.06)', color: '#C88A9E', border: '1px solid rgba(225,48,108,0.10)' }} title="Gedeeld naar Instagram">
                              <InstagramIcon size={11} weight="regular" />
                              Instagram
                            </span>
                          )}
                        </div>
                        <p className="text-xs" style={{ color: '#5B6B75' }}>{formatPostDate(post.timestamp?.toDate?.() || post.timestamp || Date.now())}</p>
                      </div>
                    </div>
                    {renderPostContent(post)}
                    {renderPostAttachments(post)}
                    <div className="mt-2 flex justify-center">
                      <PostRespectArea
                        postId={post.id}
                        postAuthorId={post.userId}
                      />
                    </div>
                    <div className="mt-2">
                      <PostInteractions
                        post={post}
                        currentUserId={user?.uid || ''}
                        currentUserName={userProfile?.displayName || user?.email || ''}
                        currentUser={user}
                        userProfile={userProfile}
                        shouldRemoveEmojis={removeEmojis}
                        onShare={sharePostToMyFeed}
                        canEditCta={canCurrentUserEditCTA}
                        onEditCta={openCtaEditor}
                      />
                    </div>
                    
                    <div className="mt-2 flex justify-end gap-2">
                      {isAdmin && (
                        <button 
                          onClick={() => adminDeletePost(post)} 
                          className="text-xs px-2 py-1 rounded border bg-red-100 text-red-600 border-red-200 hover:bg-red-200" 
                          title="Admin: Post verwijderen"
                        >
                          🗑️
                        </button>
                      )}
                      
                      {/* Connection Button - only show for other users' posts */}
                      {post.userId !== user?.uid && (
                        <>
                          {getConnectionStatus(post.userId) === 'connected' ? (
                            <div className="text-xs px-2 py-1 rounded border bg-green-100 text-green-600 border-green-200 flex items-center gap-1">
                              <span>🤝</span>
                              <span>Verbonden</span>
                            </div>
                          ) : getConnectionStatus(post.userId) === 'pending' ? (
                            <div className="text-xs px-2 py-1 rounded border bg-yellow-100 text-yellow-600 border-yellow-200 flex items-center gap-1">
                              <span>⏳</span>
                              <span>Verzonden</span>
                            </div>
                          ) : (
                            <button
                              onClick={async () => {
                                // Double-check connection status before sending request
                                const currentStatus = getConnectionStatus(post.userId);
                                if (currentStatus === 'connected') {
                                  alert('Je bent al verbonden met deze gebruiker.');
                                  return;
                                }
                                if (currentStatus === 'pending') {
                                  alert('Je hebt al een connectieverzoek gestuurd naar deze gebruiker.');
                                  return;
                                }
                                await sendConnectionRequest(post.userId, post.author, post.id);
                              }}
                              className="text-xs px-2 py-1 rounded border bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-50 flex items-center gap-1"
                              title="Verbind met deze gebruiker"
                            >
                              <span>🔗</span>
                              <span>Verbind</span>
                            </button>
                          )}
                        </>
                      )}
                      
                      <button onClick={() => togglePin(post)} className={`text-xs px-2 py-1 rounded border ${post.pinned ? 'bg-yellow-100 text-yellow-800 border-yellow-200' : 'bg-gray-100 text-gray-600 border-gray-200'} hover:bg-gray-50`} title={post.pinned ? 'Unpin uit Pinned feed' : 'Pin naar Pinned feed'}>
                        <span style={{ color: '#36454F' }}>📌</span> {post.pinned ? 'Unpin' : 'Pin'}
                      </button>
                    </div>
                  </div>
                ))
                )}
                </div>
              
                      </div>

            {/* Mijn Posts */}
            <div
              className={`${feedsVisibility.my ? '' : 'hidden'} order-3 ${
                effectiveMobileTab === 0
                  ? 'bg-white flex flex-col flex-1 min-h-0'
                  : 'bg-white rounded-2xl'
              }`}
              style={effectiveMobileTab === 0 ? {} : { border: '1px solid #F0EEE9', boxShadow: '0 1px 6px rgba(0,0,0,0.018)' }}
              role="region"
              aria-labelledby="myposts-heading"
            >
              <div className={effectiveMobileTab === 0 ? 'flex items-center gap-2 px-3 py-2' : 'flex items-center gap-2 px-4 pt-5 pb-4 rounded-t-2xl'}>
                <h3
                  id="myposts-heading"
                  style={{
                    fontSize: effectiveMobileTab === 0 ? '0.88rem' : '1.05rem',
                    fontWeight: 600,
                    color: '#3C4F49',
                    letterSpacing: '0.015em'
                  }}
                >
                  Mijn posts
                </h3>
                {effectiveMobileTab === 0 && myPosts.length === 0 && (
                  <span className="text-[11px] text-gray-400">Nog geen posts</span>
                )}
                <div className="flex-1" />
                {!(effectiveMobileTab === 0 && myPosts.length === 0) && (
                  <button onClick={() => setExpandedFeed('my')} aria-label="Vergroot" title="Vergroot" className="flex-shrink-0 px-1.5 py-0.5 text-[10px] bg-white border text-gray-500 rounded hover:bg-gray-50">⤢</button>
                )}
              </div>
              <div
                className={`feed-container overflow-y-auto overflow-x-hidden ${
                  effectiveMobileTab === 0
                    ? myPosts.length === 0
                      ? 'flex-1 px-3 pb-[82px]'
                      : 'flex-1 min-h-0 px-3 pb-[82px] space-y-3'
                    : 'max-h-[50vh] px-4 pb-4 space-y-5'
                }`}
              >
                {myPosts.length === 0 ? (
                  <div className={effectiveMobileTab === 0 ? 'h-full flex items-center justify-center text-sm text-gray-500' : 'p-6 text-center text-gray-500'}>Je hebt nog geen posts</div>
                ) : (
                <>
                <div className="flex items-center justify-between px-1">
                  <div className="text-xs text-gray-600">Beheer je posts</div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      max={3650}
                      value={purgeDays}
                      onChange={(e) => setPurgeDays(Math.max(1, Number(e.target.value || 0)))}
                      className="w-16 border rounded px-2 py-1 text-xs"
                      title="Aantal dagen"
                    />
                  <button
                      onClick={() => deleteMyPostsOlderThan(purgeDays)}
                      className="px-2 py-1 text-xs rounded border bg-white text-red-600 hover:bg-red-50"
                      title="Verwijder mijn posts ouder dan X dagen"
                    >
                      Verwijder &gt; {purgeDays}d
                  </button>
                  </div>
                </div>
                {myPosts.map((post: any, index: number) => (
                  <div key={post.id || index} className="bg-white rounded-lg p-4 relative overflow-hidden" style={{ border: '1px solid #F0EEE9', boxShadow: '0 1px 4px rgba(0,0,0,0.015)' }}>
                    {/* Mute Person Button */}
                    <div className="absolute top-2 right-2">
                      <MutePersonButton 
                        userId={post.userId || post.authorId || 'unknown'}
                        userName={post.authorName || post.displayName || 'Onbekende gebruiker'}
                      />
                    </div>
                    <div className="flex items-center space-x-2 mb-3">
                      <img src={getPostAvatar(post)} alt="Profile" className="w-8 h-8 rounded-full" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="font-medium text-sm" style={{ color: '#1F2933' }}>{post.author}</p>
                          {(post.aiDisclosure === 'partial' || post.aiDisclosure === 'full') && (
                            <span
                              className="inline-flex items-center px-1 py-0.5 rounded text-[9px] font-medium tracking-wide select-none"
                              style={{ backgroundColor: 'rgba(139,92,246,0.07)', color: '#7C6FA0', border: '1px solid rgba(139,92,246,0.14)' }}
                              title={post.aiDisclosure === 'full' ? 'Volledig A.I.-gegenereerde content' : 'Gedeeltelijk A.I.-gegenereerde content'}
                            >
                              A.I.
                            </span>
                          )}
                        </div>
                        <p className="text-xs" style={{ color: '#5B6B75' }}>{formatPostDate(post.timestamp?.toDate?.() || post.timestamp || Date.now())}</p>
                      </div>
                    </div>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      {editingPostId === post.id ? (
                        <div className="flex-1">
                          <textarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            rows={3}
                            className="w-full border rounded-lg p-2 text-sm"
                          />
                          <div className="mt-2 flex items-center gap-2 justify-end">
                  <button
                              onClick={() => { setEditingPostId(null); setEditContent(''); }}
                              className="px-2 py-1 text-xs rounded border bg-white hover:bg-gray-50"
                            >
                              Annuleren
                  </button>
                  <button
                              onClick={() => updateOwnPostContent(post.id, editContent)}
                              disabled={!editContent.trim()}
                              className="px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                            >
                              Opslaan
                  </button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-700 flex-1">{renderPostContent(post)}</div>
                      )}
                      {post.userId === (user?.uid || '') && (
                        <div className="flex items-center gap-2">
                  <button
                            onClick={() => { setEditingPostId(post.id); setEditContent(post.content || ''); }}
                            className="px-2 py-1 text-xs rounded border bg-white hover:bg-gray-50"
                            title="Wijzig"
                          >
                            ✏️
                  </button>
                  <button
                            onClick={() => deleteOwnPost(post)}
                            className="px-2 py-1 text-xs rounded border bg-white text-red-600 hover:bg-red-50"
                          >
                            Verwijder
                  </button>
                </div>
                      )}
              </div>
                    <div className="mt-2 flex justify-center">
                      <PostRespectArea
                        postId={post.id}
                        postAuthorId={post.userId}
                      />
                    </div>
                    <PostInteractions
                      post={post}
                      currentUserId={user?.uid || ''}
                      currentUserName={userProfile?.displayName || user?.email || ''}
                      currentUser={user}
                      userProfile={userProfile}
                      shouldRemoveEmojis={removeEmojis}
                      onShare={sharePostToMyFeed}
                      canEditCta={canCurrentUserEditCTA}
                      onEditCta={openCtaEditor}
                      showDelete={post.userId === (user?.uid || '')}
                      onDelete={deleteOwnPost}
                      showEdit={post.userId === (user?.uid || '')}
                      onEdit={updateOwnPostContent}
                      isAdmin={isAdmin}
                      onAdminDelete={adminDeletePost}
                    />
                    {renderPostAttachments(post)}
                    <div className="mt-2 flex justify-end gap-2">
                      {isAdmin && (
                        <button 
                          onClick={() => adminDeletePost(post)} 
                          className="text-xs px-2 py-1 rounded border bg-red-100 text-red-600 border-red-200 hover:bg-red-200" 
                          title="Admin: Post verwijderen"
                        >
                          🗑️
                        </button>
                      )}
                      <button onClick={() => togglePin(post)} className={`text-xs px-2 py-1 rounded border ${post.pinned ? 'bg-yellow-100 text-yellow-800 border-yellow-200' : 'bg-gray-100 text-gray-600 border-gray-200'} hover:bg-gray-50`} title={post.pinned ? 'Unpin uit Pinned feed' : 'Pin naar Pinned feed'}>
                        <span style={{ color: '#36454F' }}>📌</span> {post.pinned ? 'Unpin' : 'Pin'}
                      </button>
                    </div>
                  </div>
                ))}
                </>
                )}
                </div>
              
                      </div>

            {/* Movement feed (alleen jouw movement) */}
            <div className={`bg-white rounded-2xl ${feedsVisibility.community ? '' : 'hidden'} order-2 relative`} style={{ border: '1px solid #F0EEE9', boxShadow: '0 1px 6px rgba(0,0,0,0.018)' }} role="region" aria-labelledby="community-heading">
              {!hasCommunityAccess && (
                <div className="absolute inset-0 z-10 bg-white/70 backdrop-blur rounded-2xl flex items-center justify-center">
                  <div className="text-center p-4">
                    <div className="text-2xl mb-2">🔒</div>
                    <div className="text-sm text-gray-700 font-medium mb-2">Alleen voor Community-abonnement</div>
                    <a href="#membership" className="inline-block px-3 py-2 text-sm rounded-lg text-white bg-gradient-to-r from-[#7A9488] via-[#9AAF88] to-[#C5D3B8] hover:opacity-90">Upgrade</a>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 px-4 pt-5 pb-4 rounded-t-2xl">
                <div className="min-w-0">
                  <h3 id="community-heading" style={{ fontSize: '1.05rem', fontWeight: 600, color: '#3C4F49', letterSpacing: '0.015em' }}>Movement feed</h3>
                  <p className="text-xs" style={{ color: '#7A8A84', marginTop: '0.15rem' }}>Volg hier je Movement feed</p>
                </div>
                <div className="flex-1" />
                <button onClick={() => hasCommunityAccess && setExpandedFeed('community')} aria-label="Vergroot" title="Vergroot" className="flex-shrink-0 px-1.5 py-0.5 text-[10px] bg-white border text-gray-500 rounded hover:bg-gray-50" disabled={!hasCommunityAccess}>⤢</button>
              </div>
              <div className={`feed-container overflow-y-auto overflow-x-hidden px-4 space-y-5 ${effectiveMobileTab === 0 ? 'flex-1 min-h-0 pb-[82px]' : 'max-h-[calc(100vh-160px)] pb-4'} ${!hasCommunityAccess ? 'pointer-events-none select-none' : ''}`}>
                {sortedCommunityFeedPosts.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">Geen movement updates</div>
                ) : (
                  sortedCommunityFeedPosts.map((post: any, index: number) => (
                    <MotionCard key={index} className="p-4 relative mb-4 overflow-hidden">
                      <div className="flex items-center space-x-2 mb-3">
                        <img src={getPostAvatar(post)} alt="Profielafbeelding" className="w-8 h-8 rounded-full" />
                        <div>
                          <p className="font-medium text-sm" style={{ color: '#1F2933' }}>{post.author}</p>
                          <p className="text-xs" style={{ color: '#5B6B75' }}>{formatPostDate(post.timestamp?.toDate?.() || post.timestamp || Date.now())}</p>
                        </div>
                      </div>
                      {renderPostContent(post)}
                      {renderPostAttachments(post)}
                      <div className="mt-1 flex justify-center">
                        <PostRespectArea
                          postId={post.id}
                          postAuthorId={post.userId}
                        />
                      </div>
                      <div className="mt-2">
                        <PostInteractions
                          post={post}
                          currentUserId={user?.uid || ''}
                          currentUserName={userProfile?.displayName || user?.email || ''}
                          currentUser={user}
                          userProfile={userProfile}
                          shouldRemoveEmojis={removeEmojis}
                          onShare={sharePostToMyFeed}
                          canEditCta={canCurrentUserEditCTA}
                          onEditCta={openCtaEditor}
                        />
                      </div>
                    </MotionCard>
                  ))
                )}
              </div>
            </div>

            {/* Gepinned */}
            <div className={`bg-white rounded-2xl ${feedsVisibility.pinned ? '' : 'hidden'} order-4`} style={{ border: '1px solid #F0EEE9', boxShadow: '0 1px 6px rgba(0,0,0,0.018)' }} role="region" aria-labelledby="pinned-heading">
              <div className="flex items-center gap-2 px-4 pt-5 pb-4 rounded-t-2xl">
                <h3 id="pinned-heading" style={{ fontSize: '1.05rem', fontWeight: 600, color: '#3C4F49', letterSpacing: '0.015em' }}>Gepinde posts</h3>
                <div className="flex-1" />
                <button onClick={() => setExpandedFeed('pinned')} aria-label="Vergroot" title="Vergroot" className="flex-shrink-0 px-1.5 py-0.5 text-[10px] bg-white border text-gray-500 rounded hover:bg-gray-50">⤢</button>
              </div>
              <div className={`feed-container overflow-y-auto overflow-x-hidden px-4 space-y-5 ${effectiveMobileTab === 0 ? 'flex-1 min-h-0 pb-[82px]' : 'max-h-[calc(100vh-160px)] pb-4'}`}>
                {pinnedPosts.length === 0 && (
                  <div className="p-6 text-center text-gray-500">Geen gepinde berichten</div>
                )}
                {pinnedPosts.map((post: any) => (
                  <div key={post.id} className="bg-white rounded-lg p-4 relative" style={{ border: '1px solid #F0EEE9', boxShadow: '0 1px 4px rgba(0,0,0,0.015)' }}>
                    {/* Mute Person Button */}
                    <div className="absolute top-2 right-2">
                      <MutePersonButton 
                        userId={post.userId || post.authorId || 'unknown'}
                        userName={post.authorName || post.displayName || 'Onbekende gebruiker'}
                      />
                    </div>
                    <div className="flex items-center space-x-2 mb-3">
                      <img src={getPostAvatar(post)} alt="Profile" className="w-8 h-8 rounded-full" />
                      <div>
                        <p className="font-medium text-sm" style={{ color: '#1F2933' }}>{post.author}</p>
                        <p className="text-xs" style={{ color: '#5B6B75' }}>{formatPostDate(post.timestamp?.toDate?.() || post.timestamp || Date.now())}</p>
                      </div>
                      <button onClick={() => togglePin(post)} className="ml-auto text-xs px-2 py-1 rounded border bg-yellow-100 text-yellow-800 border-yellow-200 hover:opacity-90">Unpin</button>
                    </div>
                    {renderPostContent(post)}
                    {renderPostAttachments(post)}
                      </div>
                ))}
                    </div>
                  </div>
            {/* Mobile feed switcher — fixed above bottom navigation */}
            {effectiveMobileTab === 0 && (
              <div
                className="fixed left-0 right-0 bottom-[58px] z-[60] bg-white border-t border-[#E8E4DC] px-3 py-2"
              >
                <button
                  type="button"
                  onClick={cycleMobileFeed}
                  className={`w-full rounded-full bg-[#E6F0EB] px-4 py-2 text-center text-xs font-medium text-[#2d6b5e] transition-all duration-300 hover:bg-[#DCEBE4] flex flex-col items-center justify-center gap-1 ${
                    mobileFeedChromeHidden ? 'opacity-[0.05]' : 'opacity-100'
                  }`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  aria-label={`Actieve feed: ${activeMobileFeedLabel}. Tik om te wisselen.`}
                >
                  <span>{activeMobileFeedLabel}</span>
                  <span className="flex items-center gap-1" aria-hidden="true">
                    {mobileFeedOrder.map((key, index) => (
                      <span
                        key={key}
                        className={`rounded-full transition-all ${
                          index === activeMobileFeedIndex
                            ? 'w-3 h-1 bg-[#7A9488]/45'
                            : 'w-1 h-1 bg-[#7A9488]/18'
                        }`}
                      />
                    ))}
                  </span>
                </button>
              </div>
            )}
                </div>
                      </div>
                      </div>
                    </div>
    {insightsOpen && (
      <div 
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) setInsightsOpen(false);
        }}
      >
        <div 
          className="bg-white rounded-xl w-full max-w-2xl shadow-lg border border-gray-200"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Clean Text Insights</h3>
            <button className="text-gray-500 hover:text-gray-700" onClick={() => setInsightsOpen(false)}>✕</button>
                  </div>
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-gray-50 rounded-lg p-3 border">
                <div className="text-gray-600">Totaal posts</div>
                <div className="text-lg font-semibold">{insightsData?.totalPosts || 0}</div>
            </div>
              <div className="bg-gray-50 rounded-lg p-3 border">
                <div className="text-gray-600">Emoji's verwijderd (potentieel)</div>
                <div className="text-lg font-semibold">{insightsData?.emojiRemoved || 0}</div>
                  </div>
              <div className="bg-gray-50 rounded-lg p-3 border">
                <div className="text-gray-600">Controletekens verwijderd</div>
                <div className="text-lg font-semibold">{insightsData?.controlCharsRemoved || 0}</div>
                </div>
                    </div>
                    
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Geblokkeerde woorden</h4>
              <div className="text-xs text-gray-500 mb-2">Standaard: {defaultSoftWords.join(', ') || 'geen'}</div>
              <div className="flex flex-wrap gap-2 mb-3">
                <button
                  onClick={() => {
                    setCustomBlockedWords((prev) => Array.from(new Set([...prev, ...presetSwearWords])));
                    setInsightsData(generateInsights());
                  }}
                  className="px-3 py-1 text-xs bg-red-50 text-red-700 rounded-full border border-red-200 hover:bg-red-100"
                  title="Voeg veelvoorkomende scheldwoorden toe"
                >
                  + Preset Scheldwoorden
                </button>
                <button
                  onClick={() => {
                    setCustomBlockedWords((prev) => Array.from(new Set([...prev, ...presetNegativePhrases])));
                    setInsightsData(generateInsights());
                  }}
                  className="px-3 py-1 text-xs bg-yellow-50 text-yellow-800 rounded-full border border-yellow-200 hover:bg-yellow-100"
                  title="Voeg veelvoorkomende negatieve zinnen toe"
                >
                  + Preset Negatieve Zinnen
                </button>
              </div>
              <div className="flex items-center gap-2 mb-3">
                                <input
                  value={newCustomWord}
                  onChange={(e) => setNewCustomWord(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newCustomWord.trim()) {
                      const w = newCustomWord.trim();
                      if (!customBlockedWords.includes(w)) setCustomBlockedWords([...customBlockedWords, w]);
                      setNewCustomWord('');
                    }
                  }}
                  placeholder="Voeg woord toe (Enter)"
                  className="flex-1 border rounded-lg px-3 py-2 text-sm"
                />
                <button
                  onClick={() => {
                    if (!newCustomWord.trim()) return;
                    const w = newCustomWord.trim();
                    if (!customBlockedWords.includes(w)) setCustomBlockedWords([...customBlockedWords, w]);
                    setNewCustomWord('');
                  }}
                  className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Toevoegen
                </button>
                        </div>
              <div className="flex flex-wrap gap-2">
                {customBlockedWords.length === 0 && (
                  <span className="text-sm text-gray-500">Geen aangepaste woorden</span>
                )}
                {customBlockedWords.map((w) => (
                  <span key={w} className="inline-flex items-center gap-2 px-2 py-1 bg-gray-100 rounded-full text-sm">
                    <span>{w}</span>
                    <button onClick={() => setCustomBlockedWords(customBlockedWords.filter(x => x !== w))} className="text-gray-500 hover:text-gray-700">✕</button>
                  </span>
                ))}
                          </div>
                          </div>

            <div>
              <h4 className="font-medium text-gray-900 mb-2">Matches in huidige posts</h4>
              {Object.keys(insightsData?.wordMatches ?? {}).length > 0 ? (
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {Object.entries(insightsData?.wordMatches ?? {}).map(([w, c]) => (
                    <div key={w} className="flex items-center justify-between bg-gray-50 rounded-lg p-2 border">
                      <span>{w}</span>
                      <span className="text-gray-600">{c as number}</span>
                        </div>
                  ))}
                      </div>
                    ) : (
                <div className="text-sm text-gray-500">Geen matches gevonden</div>
              )}
            </div>
          </div>
          <div className="p-4 border-t flex items-center justify-end gap-2">
            <button className="px-3 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200" onClick={() => setInsightsOpen(false)}>Sluiten</button>
            <button
              className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              onClick={() => { try { localStorage.setItem('cleanCustomWords', JSON.stringify(customBlockedWords)); } catch {}; setInsightsOpen(false); }}
            >
              Opslaan
            </button>
          </div>
        </div>

        {/* Right Sidebar removed to restore feed width */}
      </div>
    )}
    {showMobileMediaSheet && (
      <div
        className="fixed inset-0 z-[10000] bg-black/35 flex items-end justify-center"
        onClick={() => setShowMobileMediaSheet(false)}
      >
        <div
          className="w-full max-w-md rounded-t-3xl bg-white p-4 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-gray-200" />
          <p className="mb-3 text-sm font-semibold text-gray-800">Media toevoegen</p>
          <div className="space-y-2">
            {([
              { key: 'photo',   icon: <ImageIcon size={18} weight="regular" />, label: 'Foto maken',      sub: 'Camera openen' },
              { key: 'video',   icon: <Video size={18} />,                       label: 'Video opnemen',   sub: 'Korte video opnemen' },
              { key: 'gallery', icon: <AttachIcon size={18} weight="regular" />, label: 'Galerij kiezen', sub: 'Bestaande foto of video' },
            ] as const).map(({ key, icon, label, sub }) => (
              <button
                key={key}
                type="button"
                onClick={() => handleMobileMediaChoice(key)}
                className="flex w-full items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5 text-left"
              >
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#F0F7F4] text-[#4F6660]">
                  {icon}
                </span>
                <span>
                  <span className="block text-sm font-semibold text-gray-800">{label}</span>
                  <span className="block text-[11px] text-gray-500">{sub}</span>
                </span>
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setShowMobileMediaSheet(false)}
            className="mt-3 w-full rounded-xl bg-gray-100 py-2.5 text-sm font-medium text-gray-500"
          >
            Annuleren
          </button>
        </div>
      </div>
    )}
    {cameraOpen && (
      <div 
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) setCameraOpen(false);
        }}
      >
        <div 
          className="bg-white rounded-xl w-full max-w-2xl shadow-2xl border border-gray-200"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">
              {cameraMode === 'photo' ? 'Camera - Foto' : 'Camera - Video'}
            </h3>
            <button className="text-gray-500 hover:text-gray-700" onClick={closeCameraModal}>✕</button>
          </div>
          <div className="p-4">
            <div className="aspect-video bg-black rounded-lg overflow-hidden flex items-center justify-center">
              <video ref={videoElRef} className="w-full h-full" playsInline muted></video>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {cameraMode === 'video' ? (isRecording ? 'Opname bezig…' : 'Klaar om op te nemen') : 'Klaar om een foto te maken'}
              </div>
              <div className="flex items-center gap-2">
                {cameraMode === 'photo' && (
                  <button onClick={capturePhotoFromStream} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Maak foto</button>
                )}
                {cameraMode === 'video' && !isRecording && (
                  <button onClick={startRecording} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Start opname</button>
                )}
                {cameraMode === 'video' && isRecording && (
                  <button onClick={stopRecording} className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900">Stop</button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )}
    {expandedFeed && (
      <div 
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) setExpandedFeed(null);
        }}
      >
        <div 
          className={`bg-white rounded-xl w-full ${expandedFeed === 'news' ? 'max-w-7xl' : 'max-w-5xl'} shadow-2xl border border-gray-200`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">
              {expandedFeed === 'news' && 'Nieuwsfeed'}
              {expandedFeed === 'community' && 'Movement feed'}
              {expandedFeed === 'my' && 'Mijn Posts'}
              {expandedFeed === 'pinned' && 'Gepinned'}
            </h3>
            <button className="text-gray-500 hover:text-gray-700" onClick={() => setExpandedFeed(null)}>✕</button>
          </div>
          {expandedFeed === 'news' ? (
            <div className="flex h-[75vh]">
              {/* Left Sidebar - Open voor netwerk lijst */}
              <div className="w-80 border-r border-gray-200 flex flex-col">
                <div className="p-3 border-b bg-gray-50">
                  <h4 className="font-semibold text-sm text-gray-900 mb-1">Relevante connecties</h4>
                  <p className="text-xs text-gray-600">Mensen open voor netwerk</p>
                </div>
                <div className="p-3 border-b">
                  <div className="space-y-2">
                    <input
                      value={newsfeedSearchName}
                      onChange={(e) => setNewsfeedSearchName(e.target.value)}
                      placeholder="Zoek op naam"
                      className="w-full text-xs px-2 py-1.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <div className="relative" ref={industryDropdownRef}>
                      <input
                        value={newsfeedSearchIndustry}
                        onChange={(e) => {
                          setNewsfeedSearchIndustry(e.target.value);
                          setShowIndustryDropdown(true);
                        }}
                        onFocus={() => setShowIndustryDropdown(true)}
                        placeholder="Zoek op branche of bedrijf"
                        className="w-full text-xs px-2 py-1.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      {showIndustryDropdown && newsfeedSearchIndustry.trim().length >= 2 && filteredIndustrySuggestions.length > 0 && (
                        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-40 overflow-y-auto">
                          {filteredIndustrySuggestions.map((item) => (
                            <button
                              key={item}
                              type="button"
                              onClick={() => {
                                setNewsfeedSearchIndustry(item);
                                setShowIndustryDropdown(false);
                              }}
                              className="w-full text-left px-2 py-1.5 text-xs hover:bg-gray-50 transition-colors"
                            >
                              {item}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <input
                      value={newsfeedSearchPostcode}
                      onChange={(e) => setNewsfeedSearchPostcode(e.target.value)}
                      placeholder="Zoek op postcode"
                      className="w-full text-xs px-2 py-1.5 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {(newsfeedSearchName || newsfeedSearchIndustry || newsfeedSearchPostcode) && (
                      <button
                        onClick={() => {
                          setNewsfeedSearchName('');
                          setNewsfeedSearchIndustry('');
                          setNewsfeedSearchPostcode('');
                        }}
                        className="text-[11px] text-gray-600 hover:text-gray-900"
                      >
                        Reset filters
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <OpenForConnectionsList 
                    onConnect={(userId, userName) => {
                      console.log(`Connectie verzoek verzonden naar ${userName} (${userId})`);
                    }}
                    className="h-full"
                    searchName={newsfeedSearchName}
                    searchIndustry={newsfeedSearchIndustry}
                    searchPostcode={newsfeedSearchPostcode}
                  />
                </div>
              </div>
              {/* Right Side - Newsfeed */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="space-y-4">
                {filteredCommunityPosts.map((post: any, i: number) => (
                  <div key={i} className="p-4 border rounded-lg">
                    <div className="flex items-center space-x-2 mb-3">
                      <img src={getPostAvatar(post)} alt="Profile" className="w-8 h-8 rounded-full" />
                      <div>
                        <p className="font-medium text-sm" style={{ color: '#1F2933' }}>{post.author}</p>
                        <p className="text-xs" style={{ color: '#5B6B75' }}>{formatPostDate(post.timestamp?.toDate?.() || post.timestamp || Date.now())}</p>
                      </div>

                    </div>
                    {/* "Stel voor" knop voor deze post auteur */}
                    <div className="mb-3">
                      <button
                        onClick={() => {
                          const postAuthorId = post.userId || post.authorId;
                          if (postAuthorId && postAuthorId !== user?.uid) {
                            setSelectedUserForIntroduction({
                              userId: postAuthorId,
                              userName: post.author,
                              userPhoto: getPostAvatar(post)
                            });
                            setShowIntroductionModal(true);
                          }
                        }}
                        className="w-full px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5"
                      >
                        <span>🤝</span>
                        <span>Stel {post.author} voor aan iemand</span>
                      </button>
                    </div>
                    {renderPostContent(post)}
                    {renderPostAttachments(post)}
                    <div className="mt-2 flex justify-center">
                      <PostRespectArea
                        postId={post.id}
                        postAuthorId={post.userId}
                      />
                    </div>
                    <div className="mt-2">
                      <PostInteractions
                        post={post}
                        currentUserId={user?.uid || ''}
                        currentUserName={userProfile?.displayName || user?.email || ''}
                        currentUser={user}
                        userProfile={userProfile}
                        shouldRemoveEmojis={removeEmojis}
                        onShare={sharePostToMyFeed}
                        canEditCta={canCurrentUserEditCTA}
                        onEditCta={openCtaEditor}
                      />
              </div>
                    <div className="mt-2 flex justify-end">
                      <button onClick={() => togglePin(post)} className={`text-xs px-2 py-1 rounded border ${post.pinned ? 'bg-yellow-100 text-yellow-800 border-yellow-200' : 'bg-gray-100 text-gray-600 border-gray-200'} hover:opacity-90`}>
                        {post.pinned ? 'Unpin' : 'Pin'}
                      </button>
                  </div>
                  </div>
                ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 max-h-[75vh] overflow-y-auto">
              {expandedFeed === 'my' && (
              <div className="space-y-4">
                {filteredCommunityPosts.map((post: any, i: number) => (
                  <div key={post.id || i} className="p-4 border rounded-lg">
                    <div className="flex items-center space-x-2 mb-3">
                      <img src={getPostAvatar(post)} alt="Profile" className="w-8 h-8 rounded-full" />
                      <div>
                        <p className="font-medium text-sm" style={{ color: '#1F2933' }}>{post.author}</p>
                        <p className="text-xs" style={{ color: '#5B6B75' }}>{formatPostDate(post.timestamp?.toDate?.() || post.timestamp || Date.now())}</p>
                      </div>
                    </div>
                    {renderPostContent(post)}
                    {renderPostAttachments(post)}
                    <div className="mt-2 flex justify-center">
                      <PostRespectArea
                        postId={post.id}
                        postAuthorId={post.userId}
                      />
                    </div>
                    <div className="mt-2">
                      <PostInteractions
                        post={post}
                        currentUserId={user?.uid || ''}
                        currentUserName={userProfile?.displayName || user?.email || ''}
                        currentUser={user}
                        userProfile={userProfile}
                        shouldRemoveEmojis={removeEmojis}
                        onShare={sharePostToMyFeed}
                        canEditCta={canCurrentUserEditCTA}
                        onEditCta={openCtaEditor}
                        showDelete={post.userId === (user?.uid || '')}
                        onDelete={deleteOwnPost}
                        isAdmin={isAdmin}
                        onAdminDelete={adminDeletePost}
                      />
                    </div>
                    <div className="mt-2 flex justify-end">
                      <button onClick={() => togglePin(post)} className={`text-xs px-2 py-1 rounded border ${post.pinned ? 'bg-yellow-100 text-yellow-800 border-yellow-200' : 'bg-gray-100 text-gray-600 border-gray-200'} hover:opacity-90`}>
                        {post.pinned ? 'Unpin' : 'Pin'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              )}
              {expandedFeed === 'pinned' && (
              <div className="space-y-4">
                {pinnedPosts.length === 0 && (
                  <div className="p-6 text-center" style={{ color: '#5B6B75' }}>Geen gepinde berichten</div>
                )}
                {pinnedPosts.map((post: any, i: number) => (
                  <div key={post.id || i} className="bg-white rounded-lg shadow-sm border p-4 relative mb-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <img src={getPostAvatar(post)} alt="Profile" className="w-8 h-8 rounded-full" />
                      <div>
                        <p className="font-medium text-sm" style={{ color: '#1F2933' }}>{post.author}</p>
                        <p className="text-xs" style={{ color: '#5B6B75' }}>{formatPostDate(post.timestamp?.toDate?.() || post.timestamp || Date.now())}</p>
                </div>
                      <button onClick={() => togglePin(post)} className="ml-auto text-xs px-2 py-1 rounded border bg-yellow-100 text-yellow-800 border-yellow-200 hover:opacity-90">Unpin</button>
              </div>
                    {renderPostContent(post)}
                    {renderPostAttachments(post)}
                    <div className="mt-2 flex justify-center">
                      <PostRespectArea
                        postId={post.id}
                        postAuthorId={post.userId}
                      />
                    </div>
                    <div className="mt-2">
                      <PostInteractions
                        post={post}
                        currentUserId={user?.uid || ''}
                        currentUserName={userProfile?.displayName || user?.email || ''}
                        currentUser={user}
                        userProfile={userProfile}
                        shouldRemoveEmojis={removeEmojis}
                        onShare={sharePostToMyFeed}
                        canEditCta={canCurrentUserEditCTA}
                        onEditCta={openCtaEditor}
                        showDelete={post.userId === (user?.uid || '')}
                        onDelete={deleteOwnPost}
                        isAdmin={isAdmin}
                        onAdminDelete={adminDeletePost}
                      />
    </div>
                  </div>
                ))}
              </div>
              )}
            </div>
          )}
        </div>
      </div>
    )}
    
    {/* Scroll Indicator */}
    <ScrollIndicator />

    {/* Introduction Modal */}
    {showIntroductionModal && selectedUserForIntroduction && (
      <IntroductionModal
        isOpen={showIntroductionModal}
        onClose={() => {
          setShowIntroductionModal(false);
          setSelectedUserForIntroduction(null);
        }}
        recommendedUserId={selectedUserForIntroduction.userId}
        recommendedUserName={selectedUserForIntroduction.userName}
        recommendedUserPhoto={selectedUserForIntroduction.userPhoto}
      />
    )}

    {ctaEditorPost && (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl border border-[#E0DDD8] w-full max-w-lg">
          <div className="p-5 border-b border-[#E0DDD8] flex items-center justify-between">
            <h3 className="text-base font-semibold text-[#1C1C1C]">CTA aanpassen</h3>
            <button
              type="button"
              onClick={() => setCtaEditorPost(null)}
              className="w-8 h-8 rounded-full text-[#5A5A5A] hover:bg-[#F7F5F0]"
            >
              ✕
            </button>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">Type</label>
              <select
                value={ctaDraft.type}
                onChange={(e) => setCtaDraft((prev) => ({ ...prev, type: e.target.value as FeedPostCtaType }))}
                className="w-full px-3 py-2 rounded-[10px] border border-[#E0DDD8] bg-white text-sm text-[#1C1C1C]"
              >
                <option value="connect">Connecten</option>
                <option value="connectieflow">ConnectieFlow</option>
                <option value="signup">Meld je aan</option>
                <option value="website">Bekijk website</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">Label</label>
              <input
                value={ctaDraft.label}
                onChange={(e) => setCtaDraft((prev) => ({ ...prev, label: e.target.value }))}
                className="w-full px-3 py-2 rounded-[10px] border border-[#E0DDD8] bg-white text-sm text-[#1C1C1C]"
                placeholder="Bijvoorbeeld: Meld je aan"
              />
            </div>
            {(ctaDraft.type === 'website' || ctaDraft.type === 'custom' || ctaDraft.type === 'signup') && (
              <div>
                <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">Doel (href/hash)</label>
                <input
                  value={ctaDraft.href || ''}
                  onChange={(e) => setCtaDraft((prev) => ({ ...prev, href: e.target.value }))}
                  className="w-full px-3 py-2 rounded-[10px] border border-[#E0DDD8] bg-white text-sm text-[#1C1C1C]"
                  placeholder="https://... of #connecties"
                />
              </div>
            )}
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setCtaEditorPost(null)}
                className="px-3 py-2 rounded-[10px] border border-[#E0DDD8] bg-white text-sm text-[#1C1C1C]"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={savePostCta}
                className="px-4 py-2 rounded-[10px] text-sm font-semibold"
                style={{ backgroundColor: '#2E5F56', color: '#FFFFFF' }}
              >
                Opslaan
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {screenLockEnabled && activeScreenLockPost && (
      <div className="fixed inset-0 z-[1300] bg-black/65 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-[14px] border border-[#E0DDD8] bg-white shadow-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-[#E0DDD8] bg-[#F7F5F0]">
            <p className="text-xs uppercase tracking-wide text-[#5A5A5A]">Relevante connectie suggestie</p>
            <h3 className="text-base font-semibold text-[#1C1C1C] mt-1">
              Groei-inzicht voor ondernemers
            </h3>
          </div>
          <div className="px-5 py-4">
            <p className="text-sm text-[#1C1C1C] leading-relaxed">
              {String(activeScreenLockPost?.content || '').slice(0, 140) || 'Open een relevante vervolgstap in ConnectieFlow.'}
            </p>
            {!screenLockReady && (
              <div className="mt-4 rounded-[10px] px-3 py-2 text-sm font-medium" style={{ backgroundColor: 'rgba(46,95,86,0.12)', color: '#2E5F56' }}>
                Beschikbaar over {screenLockCountdown}s
              </div>
            )}
          </div>
          <div className="px-5 py-4 border-t border-[#E0DDD8] flex items-center justify-end gap-2">
            <button
              type="button"
              disabled={!screenLockReady}
              onClick={() => closeScreenLock('scroll')}
              className="px-3 py-2 rounded-[10px] border border-[#E0DDD8] bg-white text-sm text-[#1C1C1C] disabled:opacity-40"
            >
              Verder scrollen
            </button>
            <button
              type="button"
              disabled={!screenLockReady}
              onClick={handleScreenLockContinue}
              className="px-4 py-2 rounded-[10px] text-sm font-semibold disabled:opacity-40"
              style={{ backgroundColor: '#2E5F56', color: '#FFFFFF' }}
            >
              Doorklikken
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Social Media Hub Modal */}
    {/* SocialMediaHub removed - use navigation to /social-media instead */}

    {/* Post Scheduler Modal */}
    <PostSchedulerModal
      isOpen={showSchedulerModal}
      onClose={() => {
        setShowSchedulerModal(false);
        // Clear the form after scheduling
        setNewPostContent('');
        setAttachments([]);
        setPostCollaborations({ userIds: [], names: [] });
        setIsPostExpanded(false);
      }}
      userId={user?.uid || ''}
      initialContent={newPostContent}
      initialAttachments={attachments}
    />

    {/* Network Club Finder Modal */}
    <NetworkClubFinder
      isOpen={showNetworkClubFinder}
      onClose={() => setShowNetworkClubFinder(false)}
    />

    {/* Focus Control Blocked Overlay - Renders over everything when active */}
    {user?.uid && <BlockedOverlay userId={user.uid} />}

    {showFocusCard && effectiveMobileTab === 0 && typeof document !== 'undefined' && createPortal(
      <div className="fixed left-1/2 top-20 bottom-24 z-[1200] flex w-[min(21.5rem,calc(100vw-2rem))] -translate-x-1/2 items-start justify-center">
        <FocusControlCard compact onClose={() => setShowFocusCard(false)} />
      </div>,
      document.body
    )}

    {/* File Preview Overlay Modal */}
    {filePreview && (
      <div
        className="fixed inset-0 bg-black/70 z-50 flex flex-col items-center justify-center p-4"
        onClick={() => setFilePreview(null)}
      >
        <div
          className="bg-white rounded-xl shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden"
          style={{ maxHeight: '90vh' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
            <div className="flex items-center gap-2 min-w-0">
              <FileText size={18} strokeWidth={1.5} className="text-gray-500 flex-shrink-0" />
              <span className="font-medium text-gray-900 truncate">{filePreview.name}</span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 ml-3">
              <a
                href={filePreview.url}
                download={filePreview.name}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                ⬇ Download
              </a>
              <button
                onClick={() => setFilePreview(null)}
                className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-colors"
              >
                ✕
              </button>
            </div>
          </div>
          {/* Iframe viewer */}
          <div className="flex-1 overflow-hidden" style={{ minHeight: '60vh' }}>
            <iframe
              src={filePreview.url}
              title={filePreview.name}
              className="w-full h-full border-0"
              style={{ minHeight: '60vh' }}
            />
          </div>
        </div>
      </div>
    )}
    {/* Profile photo positioner modal */}
    {showPhotoPositioner && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-6 max-w-lg w-full">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Positioneer je foto</h3>
          <p className="text-sm text-gray-600 mb-4">
            Sleep de foto om de beste positie te vinden voor de ronde crop
          </p>
          <div className="mb-6">
            <div
              className="relative w-full aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-move select-none"
              onMouseDown={handlePhotoDragMouseDown}
              onMouseMove={handlePhotoDragMouseMove}
              onMouseUp={handlePhotoDragMouseUp}
              onMouseLeave={handlePhotoDragMouseUp}
            >
              <img
                src={tempPhotoURL}
                alt="Positioneren"
                className="w-full h-full object-cover"
                style={{ objectPosition: `${tempPhotoPosition.x}% ${tempPhotoPosition.y}%` }}
                draggable={false}
              />
              <div className="absolute inset-0 pointer-events-none">
                <svg width="100%" height="100%" className="absolute inset-0">
                  <defs>
                    <mask id="profileCircleMask">
                      <rect width="100%" height="100%" fill="white" opacity="0.7"/>
                      <circle cx="50%" cy="50%" r="40%" fill="black"/>
                    </mask>
                  </defs>
                  <rect width="100%" height="100%" fill="black" opacity="0.5" mask="url(#profileCircleMask)"/>
                  <circle cx="50%" cy="50%" r="40%" fill="none" stroke="white" strokeWidth="2" strokeDasharray="5,5"/>
                </svg>
              </div>
              <div
                className="absolute w-3 h-3 border-2 border-white rounded-full shadow-lg pointer-events-none"
                style={{ left: `${tempPhotoPosition.x}%`, top: `${tempPhotoPosition.y}%`, transform: 'translate(-50%, -50%)' }}
              />
            </div>
            <div className="mt-4 flex items-center justify-center">
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-2">Voorbeeld</p>
                <div className="w-20 h-20 rounded-full bg-gray-200 overflow-hidden mx-auto">
                  <img
                    src={tempPhotoURL}
                    alt="Voorbeeld"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: `${tempPhotoPosition.x}% ${tempPhotoPosition.y}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => {
                setShowPhotoPositioner(false);
                URL.revokeObjectURL(tempPhotoURL);
                setTempPhotoFile(null);
                setTempPhotoURL('');
              }}
              className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
            >
              Annuleren
            </button>
            <button
              type="button"
              onClick={() => setTempPhotoPosition({ x: 50, y: 50 })}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
            >
              ↺ Reset
            </button>
            <button
              type="button"
              onClick={handlePhotoPositionSave}
              className="flex-1 px-4 py-2 text-white rounded-lg font-medium"
              style={{ backgroundColor: '#8DA8A0' }}
            >
              Opslaan
            </button>
          </div>
        </div>
      </div>
    )}
      {/* ── Mobile floating filter button ──────────────────────────────
           Navigates to Profiel tab → "Weergave & Filters" section.
           Hidden on desktop (md+). Positioned above the bottom nav. */}
      <button
        className="hidden"
        onClick={() => { window.location.hash = '#profile-hub'; }}
        aria-label="Weergave & Filters"
        style={{
          position: 'fixed',
          bottom: '64px',
          right: '12px',
          zIndex: 70,
          display: 'none',
          alignItems: 'center',
          gap: '5px',
          padding: '6px 10px',
          borderRadius: '999px',
          background: 'rgba(255,255,255,0.92)',
          color: '#7A9488',
          fontSize: '11px',
          fontWeight: 600,
          boxShadow: '0 1px 4px rgba(60,79,73,0.10)',
          border: '1px solid rgba(122,148,136,0.22)',
          cursor: 'pointer',
          opacity: 0.92,
          WebkitTapHighlightColor: 'transparent',
        } as React.CSSProperties}
      >
        <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        Filters
      </button>
      {/* Scroll-to-top inside composer (only when near the bottom of composer scroll) */}
      {/* Composer scroll-to-top is now rendered inside renderMobileFeedPreview on the card corner */}

      {/* Global scroll-to-top — always mounted when not in composer, CSS fade+slide */}
      {!(effectiveMobileTab === 0 && isPostExpanded) && createPortal(
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Terug naar boven"
          style={{
            position: 'fixed',
            bottom: effectiveMobileTab !== undefined ? '80px' : '24px',
            right: '16px',
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(79,102,96,0.22)',
            color: '#3a5a54',
            animation: showScrollTop ? 'sagePulse 2.4s ease-in-out infinite' : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9990,
            opacity: showScrollTop ? 1 : 0,
            transform: showScrollTop ? 'translateY(0) scale(1)' : 'translateY(6px) scale(0.88)',
            pointerEvents: showScrollTop ? 'auto' : 'none',
            transition: 'opacity 0.25s ease, transform 0.25s ease',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24" style={{ display: 'block', flexShrink: 0 }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </button>,
        document.body
      )}
    </>
  );
};

export default CommunityDashboard;



